import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  HomeIcon,
  UserGroupIcon,
  CalendarIcon,
  CalendarDaysIcon,
  DocumentIcon,
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  VideoCameraIcon,
  MapPinIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  SparklesIcon,
  MegaphoneIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentListIcon,
  XCircleIcon,
  CreditCardIcon,
  ChartBarIcon,
  UserCircleIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  BriefcaseIcon,
  BanknotesIcon,
  RectangleStackIcon,
  ArrowLeftIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { registrationService } from '../services/registrationService';
import SessionSummaryPanel from './SessionSummaryPanel';
import DocumentLibrary from './DocumentLibrary';
import FormsLibrarySection from './FormsLibrarySection';
import ClientDetailPanel from './ClientDetailPanel';
import type { ClientDetailClient } from './ClientDetailPanel';
import {
  getAuthUrl, getStatus, disconnect,
  GoogleStatus,
} from '../services/googleCalendarService';
import {
  getOnboardingPipeline, OnboardingClient,
  createPacket, listDocuments, DocumentItem,
  listTemplateTags, DocumentTemplateTag,
} from '../services/documentService';
import { config } from '../config/environment';
import { AppointmentType, APPOINTMENT_TYPES, getAppointmentTypeLabel } from '../constants/appointmentTypes';
import { Spinner, EmptyState } from './ui';
import TestSummaryPage from './TestSummaryPage';
import PaymentSettings from './PaymentSettings';
import PaymentMethodsCard from './PaymentMethodsCard';
import CoachPromptSettingsPanel from './CoachPromptSettingsPanel';
import CashflowTab from './CashflowTab';
import InvoicesTab from './InvoicesTab';
import CoachingLogTab from './CoachingLogTab';
import BillingTab from './BillingTab';
import CoachPackagesPanel from './CoachPackagesPanel';
import ScheduleSessionModal from './ScheduleSessionModal';
import SessionDetailModal from './SessionDetailModal';
import LogPastSessionModal from './LogPastSessionModal';
import NotificationPrefsPanel from './NotificationPrefsPanel';
import type { EventDef as NotifEventDef } from './NotificationPrefsPanel';
import {
  getAppointmentPaymentStatus, AppointmentPaymentStatus,
  setAppointmentPricing, getAppointmentPricing, updatePricingStatus,
  chargeClient, getClientPaymentMethod, AppointmentPricing, formatCents,
  updateMeetingStatus, resetManualPayment,
} from '../services/paymentService';
import { useFeatureFlags } from '../context/FeatureFlagContext';
import SubscriptionGateModal from './SubscriptionGateModal';
import TrialBanner from './TrialBanner';
import AddressFields, { type ApiCountry } from './AddressFields';
// import { setFlag } from '../services/featureFlagService'; // unused while feature toggles are hidden
import {
  getSocialStatus, getSocialAuthUrl, disconnectSocial,
  Platform, SocialStatus,
} from '../services/socialMediaService';
import SocialMediaPostModal from './SocialMediaPostModal';
import CoachCalendarView, { CalendarEvent } from './CoachCalendarView';
import Tooltip from './Tooltip';
import { createInvoice, sendInvoice, resendInvoice, getInvoiceForSession } from '../services/invoiceService';
import type { SlotInfo } from 'react-big-calendar';
import '../styles/calendar.css';
import { useHeaderUser } from '../contexts/HeaderContext';
import MultiSelectDropdown from './MultiSelectDropdown';
import PhoneInputField from './PhoneInputField';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { getServerConfig } from '../config/serverConfig';

// ─── Brand color utilities ────────────────────────────────────────────────────

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : null;
}
function getContrastColor(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#ffffff';
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255 > 0.5 ? '#1a1a1a' : '#ffffff';
}
function darkenHex(hex: string, pct: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const r = Math.max(0, Math.round(rgb.r * (1 - pct / 100)));
  const g = Math.max(0, Math.round(rgb.g * (1 - pct / 100)));
  const b = Math.max(0, Math.round(rgb.b * (1 - pct / 100)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
function lightenHex(hex: string, pct: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const r = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * pct / 100));
  const g = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * pct / 100));
  const b = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * pct / 100));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Update this date whenever a DST transition occurs so the notice re-surfaces for all coaches.
const DST_NOTICE_DATE = '2026-03-08'; // US spring-forward 2026
const DST_NOTICE_KEY  = 'dst_notice_dismissed';

// ─── Types ────────────────────────────────────────────────────────────────────

type Section = 'dashboard' | 'clients' | 'schedules' | 'services' | 'documents' | 'forms' | 'marketing' | 'settings' | 'test-summary' | 'reports' | 'invoices' | 'cashflow';

interface Client {
  relationship_id: string;
  client_id: string;
  client_name: string;
  client_email: string;
  start_date: string;
  is_active: boolean;
  name: string;
  email: string;
  status: 'active' | 'inactive' | 'new';
  ai_consent?: boolean;
  registration_source?: string;
}

interface Session {
  id: string;
  appointmentId?: string;
  clientId?: string;
  relationshipId?: string;
  scheduledAt: string;   // raw ISO from API — used for calendar rendering
  date: string;
  time: string;
  clientName: string;
  meetingName: string;
  duration: number;
  type: 'virtual' | 'in-person';
  status: 'confirmed' | 'pending' | 'cancelled';
  appointmentStatus?: string;   // raw DB status: scheduled | completed | no_show | cancelled
  googleMeetLink?: string;
  appointmentType?: AppointmentType;
  barterNote?: string;
  source?: 'live' | 'manual_entry';
  packageEnrollmentId?: string | null;
  packageName?: string | null;
  coachClientId?: number;
  recurringSeriesId?: string | null;
}


// ─── Helpers ──────────────────────────────────────────────────────────────────

const initials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);


const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

// ─── InvitePanel ──────────────────────────────────────────────────────────────

interface InvitePanelProps {
  open: boolean;
  coachId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const InvitePanel: React.FC<InvitePanelProps> = ({ open, coachId, onClose, onSuccess }) => {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', date_of_birth: '', street_address: '', occupation: '', employer: '', bio: '' });
  const [errors, setErrors] = useState<{ first_name?: string; last_name?: string; email?: string }>({});
  const [showOptional, setShowOptional] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [done, setDone] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const reset = () => {
    setForm({ first_name: '', last_name: '', email: '', phone: '', date_of_birth: '', street_address: '', occupation: '', employer: '', bio: '' });
    setErrors({});
    setShowOptional(false);
    setApiError('');
    setDone(false);
    setSentEmail('');
  };

  // Reset when panel opens
  React.useEffect(() => { if (open) reset(); }, [open]);

  if (!open) return null;

  const inp = (field: keyof typeof errors) =>
    `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200 placeholder-dark-muted ${errors[field] ? 'border-red-500' : 'border-dark-border'}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: typeof errors = {};
    if (!form.first_name.trim()) errs.first_name = 'Required';
    if (!form.last_name.trim())  errs.last_name  = 'Required';
    if (!form.email.trim())      errs.email      = 'Required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Invalid email';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    setApiError('');
    try {
      const token = localStorage.getItem('authToken');
      const payload: Record<string, string> = { first_name: form.first_name, last_name: form.last_name, email: form.email, coach_id: coachId };
      if (form.phone.trim())          payload.phone          = form.phone.trim();
      if (form.date_of_birth.trim()) payload.date_of_birth  = form.date_of_birth.trim();
      if (form.street_address.trim()) payload.street_address = form.street_address.trim();
      if (form.occupation.trim())    payload.occupation     = form.occupation.trim();
      if (form.employer.trim())      payload.employer       = form.employer.trim();
      if (form.bio.trim())           payload.bio            = form.bio.trim();
      const res = await fetch(`${config.apiBaseUrl}/coaches/invite/client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invite failed');
      setSentEmail(form.email);
      setDone(true);
      onSuccess();
    } catch (err: any) {
      setApiError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3 mb-2 bg-dark-hover border border-dark-border rounded-xl overflow-hidden">
      <div className="px-5 py-4">
        {done ? (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-green-400">
              ✓ Invite sent to <span className="font-medium text-white">{sentEmail}</span>
            </p>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={reset} className="text-xs px-3 py-1.5 border border-dark-border text-gray-300 rounded-lg hover:bg-dark-canvas">
                Invite Another
              </button>
              <button onClick={onClose} className="text-xs px-3 py-1.5 border border-dark-border text-gray-300 rounded-lg hover:bg-dark-canvas">
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-medium text-gray-400 mb-1">First Name</label>
                <input
                  type="text" value={form.first_name} placeholder="Jane"
                  onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                  className={inp('first_name')}
                />
                {errors.first_name && <p className="text-red-400 text-xs mt-0.5">{errors.first_name}</p>}
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-medium text-gray-400 mb-1">Last Name</label>
                <input
                  type="text" value={form.last_name} placeholder="Smith"
                  onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                  className={inp('last_name')}
                />
                {errors.last_name && <p className="text-red-400 text-xs mt-0.5">{errors.last_name}</p>}
              </div>
              <div className="flex-[2] min-w-[200px]">
                <label className="block text-xs font-medium text-gray-400 mb-1">Email</label>
                <input
                  type="email" value={form.email} placeholder="jane@example.com"
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className={inp('email')}
                />
                {errors.email && <p className="text-red-400 text-xs mt-0.5">{errors.email}</p>}
              </div>
              <div className="flex gap-2 flex-shrink-0 pb-0.5">
                <button
                  type="submit" disabled={loading}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium whitespace-nowrap"
                >
                  {loading ? 'Sending…' : 'Send Invite'}
                </button>
                <button type="button" onClick={onClose} className="px-3 py-2 border border-dark-border text-gray-400 hover:text-white rounded-lg text-sm">
                  Cancel
                </button>
              </div>
            </div>
            {/* Optional profile fields */}
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowOptional(v => !v)}
                className="text-xs text-gray-400 hover:text-gray-200 flex items-center gap-1 transition-colors"
              >
                <svg className={`w-3 h-3 transition-transform ${showOptional ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                {showOptional ? 'Hide optional profile fields' : 'Add optional profile info'}
              </button>
              {showOptional && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Phone</label>
                    <PhoneInputField
                      value={form.phone}
                      onChange={val => setForm(f => ({ ...f, phone: val }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Date of Birth</label>
                    <input type="date" value={form.date_of_birth}
                      onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))}
                      className="w-full px-3 py-2 border border-dark-border rounded-lg text-sm bg-dark-canvas text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-400 mb-1">Street Address</label>
                    <input type="text" value={form.street_address} placeholder="123 Main St"
                      onChange={e => setForm(f => ({ ...f, street_address: e.target.value }))}
                      className="w-full px-3 py-2 border border-dark-border rounded-lg text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Occupation</label>
                    <input type="text" value={form.occupation} placeholder="e.g. Software Engineer"
                      onChange={e => setForm(f => ({ ...f, occupation: e.target.value }))}
                      className="w-full px-3 py-2 border border-dark-border rounded-lg text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Employer</label>
                    <input type="text" value={form.employer} placeholder="Company name"
                      onChange={e => setForm(f => ({ ...f, employer: e.target.value }))}
                      className="w-full px-3 py-2 border border-dark-border rounded-lg text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-400 mb-1">Notes / Bio</label>
                    <textarea rows={2} value={form.bio} placeholder="Any context about this client..."
                      onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                      className="w-full px-3 py-2 border border-dark-border rounded-lg text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                    />
                  </div>
                </div>
              )}
            </div>
            {apiError && <p className="text-red-400 text-xs mt-2">{apiError}</p>}
          </form>
        )}
      </div>
    </div>
  );
};

// ─── InfoTip ──────────────────────────────────────────────────────────────────
const InfoTip = ({ text }: { text: string }) => (
  <span className="relative group inline-flex items-center ml-1 align-middle">
    <span className="w-3.5 h-3.5 rounded-full border border-gray-500 text-gray-500 text-[9px] flex items-center justify-center cursor-help leading-none select-none flex-shrink-0">
      i
    </span>
    <span className="pointer-events-none absolute bottom-5 left-1/2 -translate-x-1/2 z-[200] hidden group-hover:block w-56 bg-dark-card border border-dark-border text-xs text-gray-300 rounded-lg p-2 shadow-xl">
      {text}
    </span>
  </span>
);

// ─── ProfileSection ───────────────────────────────────────────────────────────
// Defined at module level so React always sees the same component type reference.
// If defined inside ProfileSubSection, each re-render creates a new function →
// React unmounts/remounts the section, closing native pickers like <input type="color">.

const ProfileSection = React.memo(({ title, isOpen, onToggle, children }: {
  title: string; isOpen: boolean; onToggle: () => void; children: React.ReactNode;
}) => (
  <div className="bg-dark-card border border-dark-border rounded-xl">
    <button
      className={`w-full px-5 py-3.5 flex items-center justify-between hover:bg-dark-hover/40 transition-colors ${isOpen ? 'rounded-t-xl' : 'rounded-xl'}`}
      onClick={onToggle}
    >
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">{title}</p>
      <ChevronRightIcon className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
    </button>
    {isOpen && <div className="px-5 pb-5 space-y-4 border-t border-dark-border pt-4">{children}</div>}
  </div>
));
ProfileSection.displayName = 'ProfileSection';

// ─── ProfileSubSection ────────────────────────────────────────────────────────
// Defined at module level so React always sees the same component type reference.
// If defined inside CoachDashboard, each parent re-render creates a new function →
// React unmounts/remounts, triggering useEffect on every render → infinite fetch loop.

const ProfileSubSection = ({
  subNav, coachId,
  companyLogoUrl, setCompanyLogoUrl,
  brandColor, setBrandColor,
}: {
  subNav: React.ReactNode;
  coachId: string;
  companyLogoUrl: string | null;
  setCompanyLogoUrl: (url: string | null) => void;
  brandColor: string | null;
  setBrandColor: (color: string | null) => void;
}) => {
  const { flags } = useFeatureFlags();
  const token = localStorage.getItem('authToken') || '';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '', bio: '', profile_description: '',
    company_name: '', license_number: '', website: '',
    street_address: '', city: '', state_province: '', postal_code: '', country: '', currency: '',
  });
  const [slug, setSlug] = useState('');

  const [slugError, setSlugError] = useState<string | null>(null);

  // Credential fields
  const [certifications, setCertifications] = useState<{ id: number; code: string; description: string | null }[]>([]);
  const [institutes, setInstitutes] = useState<{ id: number; name: string; description: string | null }[]>([]);
  const [certificationIds, setCertificationIds] = useState<number[]>([]);
  const [credentialNumber, setCredentialNumber] = useState('');
  const [credentialIssueDate, setCredentialIssueDate] = useState('');
  const [instituteId, setInstituteId] = useState<number | null>(null);
  const [specializationOptions, setSpecializationOptions] = useState<{ id: number; name: string }[]>([]);
  const [selectedSpecIds, setSelectedSpecIds] = useState<number[]>([]);
  const [apiCountries, setApiCountries] = useState<ApiCountry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [verifyStatus, setVerifyStatus] = useState<{ is_email_verified: boolean; is_phone_verified: boolean; has_phone: boolean } | null>(null);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneOtpSending, setPhoneOtpSending] = useState(false);
  const [phoneOtpVerifying, setPhoneOtpVerifying] = useState(false);
  const [phoneOtpError, setPhoneOtpError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      fetch(`${config.apiBaseUrl}/countries`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${config.apiBaseUrl}/coaches/me`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${config.apiBaseUrl}/verify/status`, { headers }).then(r => r.json()).catch(() => null),
      fetch(`${config.apiBaseUrl}/specializations`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${config.apiBaseUrl}/coaches/certifications`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${config.apiBaseUrl}/coaches/institutes`).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([countries, d, vs, specs, certs, insts]) => {
        setApiCountries(countries);
        if (!d) return;
        setForm({
          first_name:    d.first_name    ?? '',
          last_name:     d.last_name     ?? '',
          phone:         d.phone         ?? '',
          bio:           d.bio           ?? '',
          profile_description: d.profile_description ?? '',
          company_name:  d.company_name  ?? '',
          license_number: d.license_number ?? '',
          website:       d.website       ?? '',
          street_address: d.street_address ?? '',
          city:          d.city          ?? '',
          state_province: d.state_province ?? '',
          postal_code:   d.postal_code   ?? '',
          country:       d.country       ?? '',
          currency:      d.currency      ?? '',
        });
        setSelectedSpecIds((d.specialization_ids as number[]) ?? []);
        setSpecializationOptions(specs);
        setCertifications(certs);
        setInstitutes(insts);
        if (d.certification_ids) setCertificationIds(d.certification_ids as number[]);
        if (d.credential_number) setCredentialNumber(d.credential_number);
        if (d.credential_issue_date) setCredentialIssueDate(d.credential_issue_date);
        if (d.institute_id) setInstituteId(d.institute_id);
        if (vs) setVerifyStatus(vs);
        if (d.company_logo_url) setCompanyLogoUrl(`${config.apiBaseUrl}/coaches/${coachId}/logo?t=${Date.now()}`);
        if (d.brand_color) setBrandColor(d.brand_color);
        if (d.slug) {
          setSlug(d.slug);
        } else {
          // Auto-suggest from company name or first-last name
          const suggest = (d.company_name || `${d.first_name}-${d.last_name}`)
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')
            .slice(0, 20);
          setSlug(suggest);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const field = (key: keyof typeof form, label: string, multiline = false, tip?: string) => (
    <div key={key}>
      <label className="block text-xs font-medium text-gray-400 mb-1">
        {label}{tip && <InfoTip text={tip} />}
      </label>
      {multiline ? (
        <textarea
          rows={4}
          value={form[key]}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-y"
        />
      ) : (
        <input
          type="text"
          value={form[key]}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      )}
    </div>
  );

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const iso = e.target.value;
    const match = apiCountries.find(c => c.iso_code === iso);
    setForm(f => ({ ...f, country: iso, state_province: '', ...(match ? { currency: match.currency_code } : {}) }));
  };

  const [openSections, setOpenSections] = React.useState<Record<string, boolean>>({
    personal: true, verification: false, professional: false, branding: false, specializations: false, address: false,
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoDeleting, setLogoDeleting] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [localBrandColor, setLocalBrandColor] = useState<string>(brandColor || '#7c3aed');

  const handleSendPhoneOtp = async () => {
    setPhoneOtpSending(true);
    setPhoneOtpError(null);
    try {
      const resp = await fetch(`${config.apiBaseUrl}/verify/send`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ channel: 'sms', phone: form.phone }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed to send code');
      setPhoneOtpSent(true);
    } catch (e: any) {
      setPhoneOtpError(e.message);
    } finally {
      setPhoneOtpSending(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    setPhoneOtpVerifying(true);
    setPhoneOtpError(null);
    try {
      const resp = await fetch(`${config.apiBaseUrl}/verify/check`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ channel: 'sms', code: phoneOtp }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Verification failed');
      setVerifyStatus(s => s ? { ...s, is_phone_verified: true } : s);
      setPhoneOtpSent(false);
      setPhoneOtp('');
    } catch (e: any) {
      setPhoneOtpError(e.message);
    } finally {
      setPhoneOtpVerifying(false);
    }
  };

  const handleSave = async () => {
    if (form.phone && !isValidPhoneNumber(form.phone)) {
      setPhoneError('Enter a valid phone number');
      return;
    }
    setPhoneError(null);
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    setSlugError(null);
    try {
      const body: Record<string, unknown> = {
        ...form,
        currency: form.currency || null,
        specialization_ids: selectedSpecIds,
        brand_color: localBrandColor || null,
        certification_ids: certificationIds,
        credential_number: credentialNumber || null,
        credential_issue_date: credentialIssueDate || null,
        institute_id: instituteId,
      };
      if (slug.trim()) body.slug = slug.trim();
      const res = await fetch(`${config.apiBaseUrl}/coaches/me`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409 && data.detail?.toLowerCase().includes('taken')) {
          setSlugError('This URL is already taken. Choose another.');
        } else {
          throw new Error(data.detail || 'Save failed');
        }
        return;
      }
      setBrandColor(localBrandColor || null);
      if (data.slug) { setSlug(data.slug); }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError(null);
    setLogoUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${config.apiBaseUrl}/coaches/me/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      // Use a local data URL for the preview — avoids any server URL / env var issues
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (ev.target?.result) setCompanyLogoUrl(ev.target.result as string);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setLogoUploading(false);
      e.target.value = '';
    }
  };

  const handleLogoDelete = async () => {
    setLogoError(null);
    setLogoDeleting(true);
    try {
      const res = await fetch(`${config.apiBaseUrl}/coaches/me/logo`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      setCompanyLogoUrl(null);
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setLogoDeleting(false);
    }
  };

  const toggleSection = (s: string) => setOpenSections(p => ({ ...p, [s]: !p[s] }));

  if (loading) return <div className="p-8"><div className="text-sm text-dark-muted">Loading…</div></div>;

  return (
    <div className="p-8 max-w-2xl space-y-4">
      {subNav}
      <h1 className="text-2xl font-bold text-white mb-6">My Profile</h1>

      <ProfileSection title="Personal" isOpen={openSections['personal']} onToggle={() => toggleSection('personal')}>
        <div className="grid grid-cols-2 gap-4">
          {field('first_name', 'First Name', false, 'Your first name as it appears to clients.')}
          {field('last_name', 'Last Name', false, 'Your last name as it appears to clients.')}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Phone</label>
          <ReactPhoneInput
            country="us"
            value={form.phone.replace(/^\+/, '')}
            onChange={val => { setForm(f => ({ ...f, phone: val ? `+${val}` : '' })); setPhoneError(null); setPhoneOtpSent(false); setPhoneOtp(''); setPhoneOtpError(null); }}
            onBlur={() => {
              if (form.phone && !isValidPhoneNumber(form.phone)) {
                setPhoneError('Enter a valid phone number');
              }
            }}
            containerClass="rpi2-dark"
            inputStyle={{ width: '100%', background: '#0d0b1e', border: `1px solid ${phoneError ? '#ef4444' : '#2d2b45'}`, borderRadius: '0.5rem', color: '#e2e0f0', fontSize: '0.875rem', height: '38px' }}
            buttonStyle={{ background: '#1a1830', border: `1px solid ${phoneError ? '#ef4444' : '#2d2b45'}`, borderRight: 'none', borderRadius: '0.5rem 0 0 0.5rem' }}
            dropdownStyle={{ background: '#0d0b1e', color: '#e2e0f0' }}
            enableSearch
          />
          {phoneError && <p className="text-red-400 text-xs mt-1">{phoneError}</p>}
          {/* Inline phone verification */}
          {verifyStatus && form.phone && !phoneError && (
            <div className="mt-2">
              {verifyStatus.is_phone_verified ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-400 bg-green-900/30 px-2 py-0.5 rounded-full">Phone verified ✓</span>
              ) : !phoneOtpSent ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSendPhoneOtp}
                    disabled={phoneOtpSending}
                    className="text-xs text-primary-400 hover:text-primary-300 disabled:opacity-50 transition-colors"
                  >
                    {phoneOtpSending ? 'Sending…' : 'Send verification code'}
                  </button>
                  {phoneOtpError && <p className="text-xs text-red-400">{phoneOtpError}</p>}
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={phoneOtp}
                    onChange={e => setPhoneOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="6-digit code"
                    className="w-28 px-2 py-1 text-xs bg-dark-canvas border border-dark-border rounded-lg text-gray-200 placeholder-dark-muted focus:outline-none focus:border-primary-500 tracking-widest text-center"
                  />
                  <button
                    type="button"
                    onClick={handleVerifyPhoneOtp}
                    disabled={phoneOtpVerifying || phoneOtp.length !== 6}
                    className="text-xs bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white rounded-lg px-3 py-1 transition-colors"
                  >
                    {phoneOtpVerifying ? 'Verifying…' : 'Verify'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPhoneOtpSent(false); setPhoneOtp(''); setPhoneOtpError(null); }}
                    className="text-xs text-dark-muted hover:text-gray-300 transition-colors"
                  >
                    Resend
                  </button>
                  {phoneOtpError && <p className="w-full text-xs text-red-400 mt-0.5">{phoneOtpError}</p>}
                </div>
              )}
            </div>
          )}
        </div>
        {field('bio', 'Bio', true, 'A short personal bio shown on your public profile and client-facing pages.')}
        {field('profile_description', 'Profile Description', true, 'A longer description of your coaching style, approach, and what clients can expect working with you.')}
      </ProfileSection>

      {verifyStatus && (
        <ProfileSection title="Contact Verification" isOpen={openSections['verification']} onToggle={() => toggleSection('verification')}>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-300">Email</span>
            {verifyStatus.is_email_verified
              ? <span className="text-xs font-medium text-green-400 bg-green-900/30 px-2 py-0.5 rounded-full">Verified ✓</span>
              : <a href="/verify" className="text-xs text-violet-400 hover:text-violet-300 underline">Verify now</a>
            }
          </div>
          {flags.enable_phone_verification && verifyStatus.has_phone && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Phone</span>
              {verifyStatus.is_phone_verified
                ? <span className="text-xs font-medium text-green-400 bg-green-900/30 px-2 py-0.5 rounded-full">Verified ✓</span>
                : <a href="/verify" className="text-xs text-violet-400 hover:text-violet-300 underline">Verify phone</a>
              }
            </div>
          )}
        </ProfileSection>
      )}

      <ProfileSection title="Professional" isOpen={openSections['professional']} onToggle={() => toggleSection('professional')}>
        {field('company_name', 'Company / Practice Name', false, 'The name of your coaching practice or business. Shown on your profile and client-facing pages.')}
        {field('license_number', 'License / Certification Number', false, 'Your professional license or certification number (e.g. ICF credential). Optional — for your records and client trust.')}
        {field('website', 'Website', false, 'Your practice website. Displayed on your public profile.')}

        {/* Coaching Certifications — multi-select dropdown */}
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Coaching Certifications<InfoTip text="Select all credentials you hold. Contact support to add unlisted certifications." />
          </label>
          <MultiSelectDropdown
            options={certifications.map(c => ({ id: c.id, label: c.code }))}
            selectedIds={certificationIds}
            onChange={setCertificationIds}
            placeholder="Select certifications…"
          />
        </div>

        {certificationIds.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Credential Number<InfoTip text="Your primary credential ID issued by the certifying body." />
              </label>
              <input
                type="text"
                value={credentialNumber}
                onChange={e => setCredentialNumber(e.target.value)}
                placeholder="e.g. ICF-123456"
                className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Issue Date<InfoTip text="The date your certification was issued or last renewed." />
              </label>
              <input
                type="date"
                value={credentialIssueDate}
                onChange={e => setCredentialIssueDate(e.target.value)}
                className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
        )}

        {/* Coaching Institute */}
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Accredited Coaching Institute<InfoTip text="The accredited institution where you trained or hold membership (e.g. ADDCA, ICF). Administered by CoachSight ECR — contact support to add unlisted institutes." />
          </label>
          <select
            value={instituteId ?? ''}
            onChange={e => setInstituteId(e.target.value ? Number(e.target.value) : null)}
            className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">— None —</option>
            {institutes.map(i => (
              <option key={i.id} value={i.id}>{i.name}{i.description ? ` — ${i.description}` : ''}</option>
            ))}
          </select>
        </div>

      </ProfileSection>

      <ProfileSection title="Branding" isOpen={openSections['branding']} onToggle={() => toggleSection('branding')}>
        {/* Logo */}
        <div>
          <p className="text-xs font-medium text-gray-400 mb-1">
            Company Logo<InfoTip text="Upload your practice logo. It replaces the 'CoachSight' wordmark in the sidebar your clients see." />
          </p>
          <p className="text-xs text-gray-500 mb-3">Shown in the sidebar in place of "CoachSight". JPEG, PNG, or WebP · max 5 MB.</p>
          {companyLogoUrl && (
            <div className="mb-3 inline-flex items-center justify-center rounded-lg border border-dark-border bg-dark-canvas px-4 py-3">
              <img src={companyLogoUrl} alt="Company logo" className="h-10 max-w-[160px] object-contain" />
            </div>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <label className={`cursor-pointer px-4 py-2 text-sm rounded-lg border transition-colors ${logoUploading ? 'opacity-50 cursor-not-allowed border-dark-border text-gray-500' : 'border-primary-600 text-primary-400 hover:bg-primary-900/20'}`}>
              {logoUploading ? 'Uploading…' : companyLogoUrl ? 'Replace Logo' : 'Upload Logo'}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleLogoUpload} disabled={logoUploading} />
            </label>
            {companyLogoUrl && (
              <button
                type="button"
                onClick={handleLogoDelete}
                disabled={logoDeleting}
                className="px-4 py-2 text-sm text-red-400 hover:text-red-300 border border-red-900/40 hover:border-red-800 rounded-lg transition-colors disabled:opacity-50"
              >
                {logoDeleting ? 'Removing…' : 'Remove Logo'}
              </button>
            )}
          </div>
          {logoError && <p className="text-xs text-red-400 mt-1">{logoError}</p>}
        </div>

        {/* Brand color */}
        <div>
          <p className="text-xs font-medium text-gray-400 mb-1">
          Brand Color<InfoTip text="Your practice's primary color. Applied to the client sidebar background, active nav items, and primary buttons. Save changes to preview it." />
        </p>
          <p className="text-xs text-gray-500 mb-3">Applied to the sidebar background, active nav item, and primary buttons. Save to apply.</p>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="color"
              value={localBrandColor}
              onChange={e => setLocalBrandColor(e.target.value)}
              className="w-10 h-10 rounded-lg border border-dark-border cursor-pointer bg-transparent"
            />
            <input
              type="text"
              value={localBrandColor}
              onChange={e => setLocalBrandColor(e.target.value)}
              placeholder="#7c3aed"
              className="w-28 bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500 font-mono"
            />
            <button
              type="button"
              onClick={() => setLocalBrandColor('#7c3aed')}
              className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 border border-dark-border hover:border-gray-500 rounded-lg transition-colors"
            >
              Reset to Default
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">Saved with "Save Changes" — sidebar preview applies after save</p>
        </div>

        {/* Subdomain slug */}
        <div>
          <p className="text-xs font-medium text-gray-400 mb-1">
            Practice URL<InfoTip text="Your unique CoachSight ECR address — clients and prospects can find your practice at your-slug.coachsightecr.com." />
          </p>
          <p className="text-xs text-gray-500 mb-2">Lowercase letters, digits, and hyphens only · max 20 characters.</p>
          <div className="flex items-center gap-0 rounded-lg border border-dark-border overflow-hidden">
            <input
              type="text"
              value={slug}
              onChange={e => {
                const v = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                if (v.length <= 20) setSlug(v);
              }}
              placeholder="your-practice"
              className="flex-1 bg-dark-canvas text-sm text-white px-3 py-2 outline-none"
            />
            <span className="px-3 py-2 bg-dark-canvas text-xs text-gray-500 select-none whitespace-nowrap border-l border-dark-border">
              .coachsightecr.com
            </span>
          </div>
          {slugError && <p className="text-xs text-red-400 mt-1">{slugError}</p>}
          {slug && !slugError && (
            <p className="text-xs text-violet-400 mt-1">{slug}.coachsightecr.com</p>
          )}
        </div>
      </ProfileSection>

      {specializationOptions.length > 0 && (
        <ProfileSection title="Specializations" isOpen={openSections['specializations']} onToggle={() => toggleSection('specializations')}>
          <MultiSelectDropdown
            options={specializationOptions.map(s => ({ id: s.id, label: s.name }))}
            selectedIds={selectedSpecIds}
            onChange={setSelectedSpecIds}
            placeholder="Select specializations…"
          />
        </ProfileSection>
      )}

      <ProfileSection title="Address" isOpen={openSections['address']} onToggle={() => toggleSection('address')}>
        <AddressFields
          country={form.country}
          state_province={form.state_province}
          city={form.city}
          postal_code={form.postal_code}
          street_address={form.street_address}
          errors={{}}
          onChange={e => setForm(f => ({ ...f, [e.target.name]: e.target.value }))}
          onCountryChange={handleCountryChange}
          countryOptions={apiCountries.length > 0 ? apiCountries : undefined}
          inputClassName="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
          labelClassName="block text-xs font-medium text-gray-400 mb-1"
        />
        {/* Currency — defaulted from country, but editable */}
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Currency</label>
          <select
            value={form.currency}
            onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
            className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">Select currency…</option>
            {(apiCountries.length > 0
              ? Array.from(new Map(apiCountries.map(c => [c.currency_code, c])).values())
              : [
                  { currency_code: 'USD', currency_name: 'US Dollar', currency_symbol: '$' },
                  { currency_code: 'CAD', currency_name: 'Canadian Dollar', currency_symbol: 'C$' },
                  { currency_code: 'GBP', currency_name: 'British Pound', currency_symbol: '£' },
                  { currency_code: 'AUD', currency_name: 'Australian Dollar', currency_symbol: 'A$' },
                ]
            ).map(c => (
              <option key={c.currency_code} value={c.currency_code}>
                {c.currency_code} — {c.currency_name} ({c.currency_symbol})
              </option>
            ))}
          </select>
        </div>
        {field('postal_code', 'Postal Code')}
      </ProfileSection>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        {saveSuccess && <span className="text-sm text-green-400">Profile saved.</span>}
        {saveError && <span className="text-sm text-red-400">{saveError}</span>}
      </div>
    </div>
  );
};

// ─── PipelineTagFields ────────────────────────────────────────────────────────
// Defined outside CoachDashboard so typing only re-renders this small component,
// not the entire dashboard, preventing the input losing focus on each keystroke.

interface PipelineTagFieldsHandle {
  getValues: () => Record<string, string>;
}

const PipelineTagFields = React.forwardRef<
  PipelineTagFieldsHandle,
  { tags: DocumentTemplateTag[]; initialValues: Record<string, string> }
>(({ tags, initialValues }, ref) => {
  const [values, setValues] = React.useState<Record<string, string>>(initialValues);

  React.useImperativeHandle(ref, () => ({
    getValues: () => values,
  }), [values]);

  if (tags.length === 0) return null;
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Template tag values <span className="text-gray-400 font-normal">(override defaults for this send)</span>
      </label>
      <div className="space-y-2">
        {tags.map(tag => (
          <div key={tag.key} className="flex items-center gap-2">
            <code className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono flex-shrink-0 w-36 truncate">{`{{${tag.key}}}`}</code>
            <input
              type="text"
              value={values[tag.key] ?? tag.default_value}
              onChange={e => setValues(prev => ({ ...prev, [tag.key]: e.target.value }))}
              placeholder={tag.default_value || tag.label}
              className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        ))}
      </div>
    </div>
  );
});

function buildJoinUrl(meetingUrl: string, appointmentId?: string, name?: string): string {
  const params = new URLSearchParams({ meeting_url: meetingUrl });
  if (appointmentId) params.set('appointment_id', appointmentId);
  if (name) params.set('name', name);
  return `/session/join?${params.toString()}`;
}

function openJoinWindow(url: string): void {
  const w = Math.round(window.screen.width * 0.8);
  const h = Math.round(window.screen.height * 0.8);
  const left = Math.round((window.screen.width - w) / 2);
  const top = Math.round((window.screen.height - h) / 2);
  window.open(url, '_blank', `noopener,noreferrer,width=${w},height=${h},left=${left},top=${top}`);
}

// ─── Component ────────────────────────────────────────────────────────────────

const CoachDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<Section>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendly') === 'connected') return 'settings';
    if (params.get('google') === 'connected') return 'settings';
    if (params.get('zoom') === 'connected') return 'settings';
    if (params.get('stripe') === 'connected' || params.get('stripe') === 'setup_expired') return 'settings';
    const hash = window.location.hash.replace('#', '').split('/')[0] as Section;
    const valid: Section[] = ['dashboard', 'clients', 'schedules', 'documents', 'forms', 'marketing', 'settings', 'test-summary', 'reports', 'invoices', 'cashflow'];
    return valid.includes(hash) ? hash : 'dashboard';
  });
  const [servicesSubTab, setServicesSubTab] = useState<'sessions' | 'packages'>('sessions');
  const [pkgDocsOpen, setPkgDocsOpen] = useState(false);
  const [pkgDocsMarkdown, setPkgDocsMarkdown] = useState<string | null>(null);
  const [sessDocsOpen, setSessDocsOpen] = useState(false);
  const [sessDocsMarkdown, setSessDocsMarkdown] = useState<string | null>(null);

  // Coach identity from localStorage
  const coachData = (() => {
    try { return JSON.parse(localStorage.getItem('userData') || '{}'); }
    catch { return {}; }
  })();
  const coachId = coachData.id ? String(coachData.id) : '1';
  const coachName = [coachData.firstName, coachData.lastName].filter(Boolean).join(' ') || 'Coach';
  const coachEmail = coachData.email || '';
  const coachInitials = [coachData.firstName, coachData.lastName]
    .filter(Boolean).map((n: string) => n[0].toUpperCase()).join('') || 'C';

  // ── Header user registration ───────────────────────────────────────────────
  const { setHeaderUser } = useHeaderUser();
  useEffect(() => {
    setHeaderUser(coachName, coachInitials);
    return () => setHeaderUser(null, null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachName, coachInitials]);

  // ── State ──────────────────────────────────────────────────────────────────

  const [upcomingSessions, setUpcomingSessions] = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [prepGeneratingIds, setPrepGeneratingIds] = useState<Set<string>>(new Set());
  const [googleCalEvents, setGoogleCalEvents] = useState<CalendarEvent[]>([]);
  const [calendlyCalEvents, setCalendlyCalEvents] = useState<CalendarEvent[]>([]);

  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Coach's own pending action items (e.g. "Send agreement to <client>"), keyed by coach_client_id
  const [coachPendingTasksByRelId, setCoachPendingTasksByRelId] = useState<Record<string, {id: string; title: string}>>({});

  // Schedule session modal
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [selectedClientForSession, setSelectedClientForSession] = useState<Client | null>(null);
  const [createdMeetLink, setCreatedMeetLink] = useState<string | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<Session | null>(null);
  const [rescheduleScope, setRescheduleScope] = useState<'this_only' | 'this_and_future' | undefined>(undefined);
  // Chooser dialog for the combined cancel/reschedule icon on session rows
  const [manageSession, setManageSession] = useState<Session | null>(null);


  // Summary panel
  const [summarySession, setSummarySession] = useState<Session | null>(null);

  // Upload transcript
  const [uploadSession, setUploadSession] = useState<Session | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadConfirming, setUploadConfirming] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Client detail panel
  const [detailClient, setDetailClient] = useState<ClientDetailClient | null>(null);

  const [coachCurrency, setCoachCurrency] = useState<string>('');

  // Profile (Settings — loaded but stored for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [coachProfile, setCoachProfile] = useState({
    bio: '', phone: '',
    line1: '', line2: '', city: '', state: '', postal: '', country: '',
    specializations: '', certifications: '',
  });

  // Branding
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);
  const [brandColor, setBrandColor] = useState<string | null>(null);

  // Google Calendar
  const [googleStatus, setGoogleStatus] = useState<GoogleStatus | null>(null);
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [googleSuccessMsg, setGoogleSuccessMsg] = useState('');
  const [googleConnectError, setGoogleConnectError] = useState('');

  // Zoom
  interface ZoomStatus { connected: boolean; zoom_email: string | null; app_configured: boolean; }
  const [zoomStatus, setZoomStatus] = useState<ZoomStatus | null>(null);
  const [zoomConnecting, setZoomConnecting] = useState(false);
  const [zoomConnectError, setZoomConnectError] = useState('');

  // Calendly
  interface CalendlyStatus {
    app_configured: boolean;
    connected: boolean;
    email: string | null;
    scheduling_type: string;
    calendly_url: string | null;
    event_type_uris: string[];
  }
  interface CalendlyEventType {
    uri: string;
    name: string;
    duration: number;
    scheduling_url: string;
    video_configured: boolean;
    video_type: string | null;
  }
  const [calendlyStatus, setCalendlyStatus] = useState<CalendlyStatus | null>(null);
  const [calendlyConnecting, setCalendlyConnecting] = useState(false);
  const [calendlyConnectError, setCalendlyConnectError] = useState('');
  const [calendlyEventTypes, setCalendlyEventTypes] = useState<CalendlyEventType[]>([]);
  const [calendlyEventTypesLoading, setCalendlyEventTypesLoading] = useState(false);
  const [calendlySelectedUris, setCalendlySelectedUris] = useState<string[]>([]);
  const [calendlySaving, setCalendlySaving] = useState(false);
  const [calendlySaveMsg, setCalendlySaveMsg] = useState('');

  // Google event types management (Settings → Scheduling)
  interface GoogleEventTypeRow { id: string; name: string; duration_minutes: number; description: string | null; billing_type: string; is_active: boolean; allow_external_booking: boolean; default_price_cents: number | null; default_currency: string | null; }
  const [googleEtList, setGoogleEtList] = useState<GoogleEventTypeRow[]>([]);
  const [googleEtLoading, setGoogleEtLoading] = useState(false);
  const [googleEtForm, setGoogleEtForm] = useState<{ name: string; duration_minutes: string; description: string; billing_type: string; default_price: string; default_currency: string } | null>(null);
  const [enforcePaymentSessions, setEnforcePaymentSessions] = useState(false);
  const [enforcePaymentSessionsSaving, setEnforcePaymentSessionsSaving] = useState(false);
  const [enforcePaymentPackages, setEnforcePaymentPackages] = useState(false);
  const googleEtNameRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (googleEtForm !== null) googleEtNameRef.current?.focus(); }, [googleEtForm !== null]); // eslint-disable-line react-hooks/exhaustive-deps
  const [googleEtSaving, setGoogleEtSaving] = useState(false);
  const [googleEtError, setGoogleEtError] = useState('');
  const [googleEtEditId, setGoogleEtEditId] = useState<string | null>(null);
  const [googleEtEditForm, setGoogleEtEditForm] = useState<{ name: string; description: string; billing_type: string; default_price: string; default_currency: string }>({ name: '', description: '', billing_type: 'billable', default_price: '', default_currency: '' });
  const [googleEtEditSaving, setGoogleEtEditSaving] = useState(false);
  const [googleEtEditError, setGoogleEtEditError] = useState('');
  // Pending external booking changes — keyed by event type id, not yet saved
  const [googleEtPendingExternal, setGoogleEtPendingExternal] = useState<Record<string, boolean>>({});
  const [googleEtExternalSaving, setGoogleEtExternalSaving] = useState<Record<string, boolean>>({});
  const [slotIncrement, setSlotIncrement] = useState<15 | 30 | 60>(30);
  const [meetingBuffer, setMeetingBuffer] = useState(0);
  const [minBookingNoticeDays, setMinBookingNoticeDays] = useState(1);
  const [reminderSlots, setReminderSlots] = useState<number[]>([720, 60, 0]);
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderMsg, setReminderMsg] = useState('');
  type TimeBlock = { id?: number; start_time: string; end_time: string };
  type DaySchedule = { day_of_week: number; day_name: string; blocks: TimeBlock[] };
  const defaultSchedule: DaySchedule[] = [
    { day_of_week: 0, day_name: 'Monday',    blocks: [{ start_time: '09:00', end_time: '17:00' }] },
    { day_of_week: 1, day_name: 'Tuesday',   blocks: [{ start_time: '09:00', end_time: '17:00' }] },
    { day_of_week: 2, day_name: 'Wednesday', blocks: [{ start_time: '09:00', end_time: '17:00' }] },
    { day_of_week: 3, day_name: 'Thursday',  blocks: [{ start_time: '09:00', end_time: '17:00' }] },
    { day_of_week: 4, day_name: 'Friday',    blocks: [{ start_time: '09:00', end_time: '17:00' }] },
    { day_of_week: 5, day_name: 'Saturday',  blocks: [] },
    { day_of_week: 6, day_name: 'Sunday',    blocks: [] },
  ];
  const [weeklySchedule, setWeeklySchedule] = useState<DaySchedule[]>(defaultSchedule);
  const [coachTimezone, setCoachTimezone] = useState('UTC');
  const [schedulingConfigSaving, setSchedulingConfigSaving] = useState(false);
  const [schedulingConfigMsg, setSchedulingConfigMsg] = useState('');
  const [googleCardExpanded, setGoogleCardExpanded] = useState(false);
  const [availabilityCardExpanded, setAvailabilityCardExpanded] = useState(true);

  // Payment status per appointment (coach view)
  const [apptPaymentStatuses, setApptPaymentStatuses] = useState<Record<string, AppointmentPaymentStatus>>({});
  const [stripeSuccessMsg, setStripeSuccessMsg] = useState('');
  const [billingChargingActive, setBillingChargingActive] = useState(false);

  // Payment panel (per-session management)
  const [paymentPanelSession, setPaymentPanelSession] = useState<Session | null>(null);
  const [panelPricing, setPanelPricing] = useState<AppointmentPricing | null>(null);
  const [panelPricingLoading, setPanelPricingLoading] = useState(false);
  const [panelEditingRate, setPanelEditingRate] = useState(false);
  const [panelRateInput, setPanelRateInput] = useState('');
  const [panelRateCurrency, setPanelRateCurrency] = useState('usd');
  const [panelHasCard, setPanelHasCard] = useState<boolean | null>(null);
  const [panelCardLast4, setPanelCardLast4] = useState<string | null>(null);
  const [panelHasInvoice, setPanelHasInvoice] = useState<boolean | null>(null);
  const [panelChargeConfirm, setPanelChargeConfirm] = useState(false);
  const [panelError, setPanelError] = useState('');
  const [panelSuccess, setPanelSuccess] = useState('');
  const [panelActionLoading, setPanelActionLoading] = useState(false);

  // Account slide-up menu (lower-left coach name button)
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showMyAccountModal, setShowMyAccountModal] = useState<'subscription' | 'payment-history' | null>(null);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  // Close account menu when clicking outside
  useEffect(() => {
    if (!accountMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [accountMenuOpen]);

  // Settings sub-section navigation + hover state for cascading flyout
  type SettingsSubSection = 'scheduling' | 'payments' | 'ai-notes' | 'profile' | 'subscription' | 'documents' | 'privacy' | 'notifications';
  const [settingsSubSection, setSettingsSubSection] = useState<SettingsSubSection | null>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendly') === 'connected') return 'scheduling';
    if (params.get('google') === 'connected') return 'scheduling';
    if (params.get('zoom') === 'connected') return 'scheduling';
    if (params.get('stripe') === 'connected' || params.get('stripe') === 'setup_expired') return 'payments';
    const parts = window.location.hash.replace('#', '').split('/');
    if (parts[0] === 'settings' && parts[1]) {
      const validSubs: SettingsSubSection[] = ['scheduling', 'payments', 'ai-notes', 'profile', 'documents', 'privacy', 'notifications', 'subscription'];
      const sub = parts[1] as SettingsSubSection;
      return validSubs.includes(sub) ? sub : null;
    }
    return null;
  });
  // Reports sub-section navigation
  type ReportsSubSection = 'cashflow' | 'coaching-log' | 'invoices';
  const [reportsSubSection, setReportsSubSection] = useState<ReportsSubSection | null>(null);
  const [paymentsOpen, setPaymentsOpen] = useState(true);

  // Sync active section to URL hash so page refresh restores the same view
  useEffect(() => {
    window.location.hash = settingsSubSection ? `settings/${settingsSubSection}` : activeSection;
  }, [activeSection, settingsSubSection]);

  useEffect(() => {
    const onHashChange = () => {
      const parts = window.location.hash.replace('#', '').split('/');
      const valid: Section[] = ['dashboard', 'clients', 'schedules', 'documents', 'forms', 'marketing', 'settings', 'test-summary', 'reports', 'invoices', 'cashflow'];
      const section = parts[0] as Section;
      if (!valid.includes(section)) return;
      setActiveSection(section);
      if (section === 'settings' && parts[1]) {
        const validSubs: SettingsSubSection[] = ['scheduling', 'payments', 'ai-notes', 'profile', 'documents', 'privacy', 'notifications', 'subscription'];
        const sub = parts[1] as SettingsSubSection;
        setSettingsSubSection(validSubs.includes(sub) ? sub : null);
      } else {
        setSettingsSubSection(null);
      }
      if (section !== 'reports') setReportsSubSection(null);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const { flags } = useFeatureFlags();
  // const [flagSaving, setFlagSaving] = useState<string | null>(null); // unused while feature toggles are hidden
  // Invite client slide-out
  const [inviteOpen, setInviteOpen] = useState(false);

  // Activate/deactivate client
  const [togglingClientId, setTogglingClientId] = useState<string | null>(null);
  const [confirmToggleClient, setConfirmToggleClient] = useState<Client | null>(null);

  // Marketing sub-tab
  const [marketingTab, setMarketingTab] = useState<'social' | 'email' | 'content' | 'website'>('social');

  // Social media
  const [socialStatuses, setSocialStatuses] = useState<Record<Platform, SocialStatus | null>>({
    linkedin: null,
    twitter: null,
    facebook: null,
    instagram: null,
  });
  const [socialPostModalOpen, setSocialPostModalOpen] = useState(false);
  const [socialConnecting, setSocialConnecting] = useState<Platform | null>(null);
  const [socialBanner, setSocialBanner] = useState<Platform | null>(null);

  // Video provider selection modal (shown after Google Calendar OAuth)
  const [showVideoProviderModal, setShowVideoProviderModal] = useState(false);

  const [onboardingStatus, setOnboardingStatus] = useState<{
    verify_contact_info: boolean;
    connect_calendar: boolean;
    setup_payments: boolean;
    complete_profile: boolean;
    upload_agreement_template: boolean;
    is_phone_verified: boolean;
  } | null>(null);

  // Onboarding pipeline (coach dashboard card)
  const [onboardingPipeline, setOnboardingPipeline] = useState<OnboardingClient[]>([]);
  const [pipelineSendModal, setPipelineSendModal] = useState<OnboardingClient | null>(null);
  const [pipelineDocs, setPipelineDocs] = useState<DocumentItem[]>([]);
  const [pipelinePacketTitle, setPipelinePacketTitle] = useState('Welcome Packet');
  const [pipelineSelectedDocIds, setPipelineSelectedDocIds] = useState<Set<string>>(new Set());
  const [pipelineSending, setPipelineSending] = useState(false);
  const [pipelineSendError, setPipelineSendError] = useState('');
  const [pipelineSendSuccess, setPipelineSendSuccess] = useState('');
  const [pipelineCustomTags, setPipelineCustomTags] = useState<DocumentTemplateTag[]>([]);
  const pipelineTagFieldsRef = React.useRef<PipelineTagFieldsHandle>(null);

  // Calendar view state
  const [calendarView, setCalendarView] = useState<'calendar' | 'list'>('list');
  const [calendarEventPopover, setCalendarEventPopover] = useState<CalendarEvent | null>(null);
  const [sessionDetailSession, setSessionDetailSession] = useState<Session | null>(null);
  const [calendarSlotDate, setCalendarSlotDate] = useState<Date | null>(null);
  const [showClientPickerForSlot, setShowClientPickerForSlot] = useState(false);
  const [showLogPastSession, setShowLogPastSession] = useState(false);
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null);
  const [markingOfflineId, setMarkingOfflineId] = useState<string | null>(null);
  const [clientPickerSearch, setClientPickerSearch] = useState('');
  const [calendarContextMenu, setCalendarContextMenu] = useState<{ event: CalendarEvent; x: number; y: number } | null>(null);
  const [cancellingAppointmentId, setCancellingAppointmentId] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const [cancelScopeSession, setCancelScopeSession] = useState<Session | null>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchSessions = async () => {
    setSessionsLoading(true);
    try {
      const response = await registrationService.getCoachSessions(coachId, false);
      if (response.success && response.data) {
        const apiData = response.data as any;
        const meetings = apiData.appointments || apiData.meetings || [];
        const transformed: Session[] = meetings.map((m: any) => {
          const d = new Date(m.scheduled_at);
          return {
            id: m.id,
            appointmentId: m.id,
            clientId: m.client?.id,
            relationshipId: m.relationship?.id,
            scheduledAt: m.scheduled_at,
            date: d.toLocaleDateString('en-CA'), // YYYY-MM-DD in browser local timezone
            time: d.toTimeString().substring(0, 5),
            clientName: `${m.client.first_name} ${m.client.last_name}`,
            meetingName: m.meeting_name || '',
            duration: m.duration_minutes,
            type: m.meeting_url ? 'virtual' : 'in-person',
            status: m.status === 'scheduled' ? 'confirmed' : m.status,
            appointmentStatus: m.status,
            googleMeetLink: m.meeting_url || undefined,
            appointmentType: m.appointment_type || 'billable',
            barterNote: m.barter_note || undefined,
            source: (m.source as 'live' | 'manual_entry') || 'live',
            packageEnrollmentId: m.package_enrollment_id || null,
            packageName: m.package_name || null,
            coachClientId: m.relationship?.id,
            recurringSeriesId: m.recurring_series_id || null,
          };
        });
        setUpcomingSessions(transformed);
        // Payment status is embedded in each session row — no second request needed
        const statuses: Record<string, AppointmentPaymentStatus> = {};
        meetings.forEach((m: any) => {
          if (m.id && m.payment_status) {
            statuses[m.id] = { appointment_id: m.id, ...m.payment_status };
          }
        });
        setApptPaymentStatuses(statuses);
      } else {
        setUpcomingSessions([]);
      }
    } catch {
      setUpcomingSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  const fetchClients = async () => {
    setClientsLoading(true);
    setClientsError('');
    try {
      const token = localStorage.getItem('authToken') || '';
      const [clientsRes, myTasksRes] = await Promise.all([
        fetch(`${config.apiBaseUrl}/coaches/${coachId}/clients?active_only=false`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${config.apiBaseUrl}/action-items/coach/my-tasks`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (clientsRes.status === 401) { localStorage.removeItem('authToken'); localStorage.removeItem('userData'); localStorage.removeItem('userType'); navigate('/', { replace: true }); return; }
      if (!clientsRes.ok) throw new Error(`HTTP ${clientsRes.status}`);
      const data = await clientsRes.json();
      setClients(data.clients.map((c: any) => ({
        relationship_id: c.relationship_id,
        client_id: c.client_id,
        client_name: c.client_name,
        client_email: c.client_email,
        start_date: c.start_date,
        is_active: c.is_active,
        name: c.client_name,
        email: c.client_email,
        status: c.is_active ? 'active' : 'inactive',
        ai_consent: c.ai_consent,
        registration_source: c.registration_source,
      })));
      if (myTasksRes.ok) {
        const tasksData = await myTasksRes.json();
        const byRelId: Record<string, {id: string; title: string}> = {};
        for (const item of (tasksData.items ?? [])) {
          if (item.coach_client_id && item.title?.startsWith('Send agreement')) {
            byRelId[String(item.coach_client_id)] = { id: item.id, title: item.title };
          }
        }
        setCoachPendingTasksByRelId(byRelId);
      }
    } catch (err) {
      setClientsError(err instanceof Error ? err.message : 'Failed to load clients');
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchGoogleCalendarEvents = async () => {
    try {
      const token = localStorage.getItem('authToken') || '';
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const end = new Date(now.getFullYear(), now.getMonth() + 3, 0).toISOString();
      const res = await fetch(
        `${config.apiBaseUrl}/google/events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      if (!data.connected || !data.events) return;
      const events: CalendarEvent[] = data.events.map((e: any) => {
        // Google Calendar returns date-only strings (e.g. "2026-05-07") for all-day events
        // and dateTime strings (e.g. "2026-05-07T09:00:00+00:00") for timed events.
        const isAllDay = typeof e.start === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(e.start);
        const start = isAllDay
          ? (() => { const d = new Date(e.start + 'T00:00:00'); return d; })()
          : new Date(e.start);
        const end = isAllDay
          ? (() => { const d = new Date(e.end + 'T00:00:00'); return d; })()
          : new Date(e.end);
        return {
          id: `gcal-${e.id}`,
          title: e.title,
          start,
          end,
          allDay: isAllDay,
          resource: { type: 'gcal' as any, googleMeetLink: e.meet_link },
        };
      });
      setGoogleCalEvents(events);
    } catch { /* non-fatal */ }
  };

  const fetchCalendlyCalendarEvents = async () => {
    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}/calendly/scheduled-events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const events: CalendarEvent[] = (data.busy || []).map((e: any) => ({
        id: `calendly-${e.uri || e.start}`,
        title: e.title || 'Calendly Event',
        start: new Date(e.start),
        end: new Date(e.end),
        resource: { type: 'gcal' as any },
      }));
      setCalendlyCalEvents(events);
    } catch { /* non-fatal */ }
  };

  // (No section locking — all nav items are accessible regardless of setup status)

  const fetchOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('authToken') || '';
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${config.apiBaseUrl}/coaches/me/onboarding-status`, { headers });
      if (res.ok) setOnboardingStatus(await res.json());
    } catch {
      // non-fatal
    }
  };

  const fetchOnboardingPipeline = async () => {
    try {
      const pipeline = await getOnboardingPipeline();
      setOnboardingPipeline(pipeline);
    } catch {
      // non-fatal
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchOnboardingStatus();
    fetchOnboardingPipeline();
    // Load scheduling provider statuses on mount so openSchedule knows the scheduling type
    const tok = localStorage.getItem('authToken') || '';
    // Load logo + brand color on mount so sidebar shows immediately
    fetch(`${config.apiBaseUrl}/coaches/me`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.company_logo_url) setCompanyLogoUrl(`${config.apiBaseUrl}/coaches/${d.id}/logo?t=${Date.now()}`);
        if (d?.brand_color) setBrandColor(d.brand_color);
        if (d?.currency) setCoachCurrency(d.currency.toUpperCase());
        if (typeof d?.enforce_payment_sessions === 'boolean') setEnforcePaymentSessions(d.enforce_payment_sessions);
        if (typeof d?.enforce_payment_packages === 'boolean') setEnforcePaymentPackages(d.enforce_payment_packages);
      })
      .catch(() => {});
    fetch(`${config.apiBaseUrl}/calendly/status`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setCalendlyStatus(data); })
      .catch(() => {});
    fetch(`${config.apiBaseUrl}/zoom/status`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setZoomStatus(data); })
      .catch(() => {});
    getStatus()
      .then(setGoogleStatus)
      .catch(() => setGoogleStatus({ app_configured: false, connected: false, email: null }));
    // Load scheduling config on mount so coach timezone is available for slot generation.
    // If the DB still has the server default 'UTC', auto-detect from browser and silently persist
    // so that slots fetched via /book/{id}/slots use the correct timezone immediately.
    fetch(`${config.apiBaseUrl}/event-types/scheduling-config/me`, {
      headers: { Authorization: `Bearer ${tok}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        const tz = data.timezone && data.timezone !== 'UTC' ? data.timezone : detectedTz;
        setCoachTimezone(tz);
        if ((!data.timezone || data.timezone === 'UTC') && tz !== 'UTC') {
          fetch(`${config.apiBaseUrl}/event-types/scheduling-config/me`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ timezone: tz }),
          }).catch(() => {});
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeSection === 'clients' || activeSection === 'documents' || activeSection === 'schedules' || activeSection === 'reports' || activeSection === 'services' || activeSection === 'invoices' || activeSection === 'cashflow') fetchClients(); }, [activeSection]);
  useEffect(() => {
    if (activeSection !== 'dashboard') return;
    const token = localStorage.getItem('authToken') || '';
    const _localDateStr = new Date().toLocaleDateString('en-CA');
    const _localStart   = new Date(`${_localDateStr}T00:00:00`);
    const _localEnd     = new Date(`${_localDateStr}T00:00:00`);
    _localEnd.setDate(_localEnd.getDate() + 1);
    const _summaryUrl = `${config.apiBaseUrl}/coaches/me/dashboard-summary`
      + `?local_today_start=${encodeURIComponent(_localStart.toISOString())}`
      + `&local_today_end=${encodeURIComponent(_localEnd.toISOString())}`;
    fetch(_summaryUrl, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) setDashSummary(d); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeSection === 'schedules') {
      fetchSessions();
      fetchGoogleCalendarEvents();
      fetchCalendlyCalendarEvents();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);
  useEffect(() => {
    if (activeSection === 'settings') {
      getServerConfig().then(cfg => setBillingChargingActive(cfg.billing_charging_active)).catch(() => {});
      getStatus()
        .then(setGoogleStatus)
        .catch(() => setGoogleStatus({ app_configured: false, connected: false, email: null }));
      // Load Calendly + Zoom status
      const settingsToken = localStorage.getItem('authToken') || '';
      fetch(`${config.apiBaseUrl}/calendly/status`, { headers: { Authorization: `Bearer ${settingsToken}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setCalendlyStatus(data); })
        .catch(() => {});
      fetch(`${config.apiBaseUrl}/zoom/status`, { headers: { Authorization: `Bearer ${settingsToken}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setZoomStatus(data); })
        .catch(() => {});
      // Load coach profile for Settings→Profile tab
      const token = localStorage.getItem('authToken') || '';
      fetch(`${config.apiBaseUrl}/coaches/${coachId}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(profile => {
          if (profile) {
            setCoachProfile({
              bio: profile.bio || '',
              phone: profile.phone || '',
              line1: profile.street_address || '',
              line2: '',
              city: profile.city || '',
              state: profile.state_province || '',
              postal: profile.postal_code || '',
              country: profile.country || '',
              specializations: (profile.specializations || []).map((s: any) => s.name).join(', '),
              certifications: profile.certifications || '',
            });
          }
        })
        .catch(() => {});
    }
  }, [activeSection, coachId]);


  // Auto-expand Google card and load config when connected; load when card is manually expanded
  useEffect(() => {
    if (googleStatus?.connected && !googleCardExpanded) {
      setGoogleCardExpanded(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleStatus?.connected]);

  useEffect(() => {
    if (
      activeSection === 'services' ||
      (activeSection === 'settings' &&
        settingsSubSection === 'scheduling' &&
        googleCardExpanded)
    ) {
      const token = localStorage.getItem('authToken') || '';
      setGoogleEtLoading(true);
      fetch(`${config.apiBaseUrl}/event-types`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : [])
        .then((ets: any[]) => {
          setGoogleEtList(ets.map((et: any) => ({
            id: et.id,
            name: et.name,
            duration_minutes: et.duration_minutes,
            description: et.description,
            billing_type: et.billing_type ?? 'billable',
            is_active: et.is_active,
            allow_external_booking: et.allow_external_booking ?? false,
            default_price_cents: et.default_price_cents ?? null,
            default_currency: et.default_currency ?? null,
          })));
        })
        .catch(() => {})
        .finally(() => setGoogleEtLoading(false));
      // Load per-day working hours
      fetch(`${config.apiBaseUrl}/coaches/me/working-hours`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then((schedule: DaySchedule[] | null) => {
          if (schedule && schedule.length === 7) setWeeklySchedule(schedule);
        })
        .catch(() => {});
      fetch(`${config.apiBaseUrl}/event-types/scheduling-config/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) {
            if ([15, 30, 60].includes(data.slot_increment_minutes)) setSlotIncrement(data.slot_increment_minutes);
            setMeetingBuffer(data.meeting_buffer_minutes ?? 0);
            setMinBookingNoticeDays(data.min_booking_notice_days ?? 1);
            // If timezone was never configured (still the server default 'UTC'), fall back to
            // the browser's detected timezone so slots appear at the coach's actual local times.
            const detectedTz2 = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
            const tz = data.timezone && data.timezone !== 'UTC' ? data.timezone : detectedTz2;
            setCoachTimezone(tz);
            if ((!data.timezone || data.timezone === 'UTC') && tz !== 'UTC') {
              fetch(`${config.apiBaseUrl}/event-types/scheduling-config/me`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ timezone: tz }),
              }).catch(() => {});
            }
          }
        })
        .catch(() => {});
      fetch(`${config.apiBaseUrl}/event-types/reminder-settings/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.reminder_minutes_before) setReminderSlots(data.reminder_minutes_before);
        })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, settingsSubSection, googleCardExpanded]);

  // Auto-load Calendly event types when connected and viewing scheduling settings
  useEffect(() => {
    if (
      activeSection === 'settings' &&
      settingsSubSection === 'scheduling' &&
      calendlyStatus?.connected &&
      calendlyEventTypes.length === 0 &&
      !calendlyEventTypesLoading
    ) {
      const token = localStorage.getItem('authToken') || '';
      const savedUris = calendlyStatus.event_type_uris ?? [];
      setCalendlyEventTypesLoading(true);
      fetch(`${config.apiBaseUrl}/calendly/event-types`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) {
            setCalendlyEventTypes(data.event_types || []);
            if (savedUris.length > 0) setCalendlySelectedUris(savedUris);
          }
        })
        .catch(() => {})
        .finally(() => setCalendlyEventTypesLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, settingsSubSection, calendlyStatus?.connected, calendlyStatus?.event_type_uris]);

  // Detect ?google=connected redirect from OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') {
      setActiveSection('settings');
      setSettingsSubSection('scheduling');
      setGoogleSuccessMsg('Google Calendar connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      getStatus().then(setGoogleStatus).catch(() => {});
      setTimeout(() => setGoogleSuccessMsg(''), 5000);
      // Always show "Meet or Zoom?" prompt after connecting Google Calendar
      setShowVideoProviderModal(true);
    }
    if (params.get('stripe') === 'connected') {
      setActiveSection('settings');
      setSettingsSubSection('payments');
      setStripeSuccessMsg('Stripe connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setStripeSuccessMsg(''), 5000);
    }
    if (params.get('stripe') === 'setup_expired') {
      setActiveSection('settings');
      setSettingsSubSection('payments');
      setStripeSuccessMsg('Your Stripe setup session expired. Click "Continue Onboarding" to pick up where you left off.');
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setStripeSuccessMsg(''), 10000);
    }
    if (params.get('calendly') === 'connected') {
      setActiveSection('settings');
      setSettingsSubSection('scheduling');
      window.history.replaceState({}, '', window.location.pathname);
      const tok = localStorage.getItem('authToken') || '';
      fetch(`${config.apiBaseUrl}/calendly/activate`, { method: 'POST', headers: { Authorization: `Bearer ${tok}` } })
        .catch(() => {});
      fetch(`${config.apiBaseUrl}/calendly/status`, { headers: { Authorization: `Bearer ${tok}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setCalendlyStatus(data); })
        .catch(() => {});
      // Pre-load event types so they appear immediately on the scheduling page
      setCalendlyEventTypesLoading(true);
      fetch(`${config.apiBaseUrl}/calendly/event-types`, { headers: { Authorization: `Bearer ${tok}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setCalendlyEventTypes(data.event_types || []); })
        .catch(() => {})
        .finally(() => setCalendlyEventTypesLoading(false));
    }
    if (params.get('zoom') === 'connected') {
      setActiveSection('settings');
      setSettingsSubSection('scheduling');
      window.history.replaceState({}, '', window.location.pathname);
      const tok = localStorage.getItem('authToken') || '';
      fetch(`${config.apiBaseUrl}/zoom/status`, { headers: { Authorization: `Bearer ${tok}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setZoomStatus(data); })
        .catch(() => {});
    }
    if (params.get('zoom') === 'error') {
      const reason = params.get('reason') || '';
      const msg = reason === 'invalid_state'
        ? 'This Zoom connection attempt is no longer valid — it may have been replaced by a newer attempt or it expired. Please try connecting again.'
        : 'Zoom connection failed. Please try again.';
      setActiveSection('settings');
      setSettingsSubSection('scheduling');
      setZoomConnectError(msg);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadSocialStatuses = useCallback(async () => {
    const platforms: Platform[] = ['linkedin', 'twitter', 'facebook', 'instagram'];
    const updates: Partial<Record<Platform, SocialStatus | null>> = {};
    await Promise.allSettled(
      platforms.map(async p => {
        try {
          updates[p] = await getSocialStatus(p);
        } catch {
          updates[p] = null;
        }
      })
    );
    setSocialStatuses(prev => ({ ...prev, ...updates }));
  }, []);

  useEffect(() => {
    if (coachId && flags['social_media']) loadSocialStatuses();
  }, [coachId, flags, loadSocialStatuses]);

  // Detect ?linkedin=connected, ?twitter=connected, etc.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const socialPlatforms: Platform[] = ['linkedin', 'twitter', 'facebook', 'instagram'];
    for (const p of socialPlatforms) {
      if (params.get(p) === 'connected') {
        setSocialBanner(p);
        loadSocialStatuses();
        // Switch to marketing view
        setActiveSection('marketing');
        setMarketingTab('social');
        // Clean URL
        params.delete(p);
        const newUrl = window.location.pathname + (params.toString() ? `?${params}` : '');
        window.history.replaceState({}, '', newUrl);
        break;
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Schedules search/filter state ─────────────────────────────────────────

  const [scheduleSearch, setScheduleSearch] = useState(() => new URLSearchParams(window.location.search).get('client') || '');
  const [scheduleDateFrom, setScheduleDateFrom] = useState(new Date().toLocaleDateString('en-CA'));
  const [scheduleDateTo, setScheduleDateTo] = useState('');
  const [thisWeekOpen, setThisWeekOpen] = useState(true);
  const [nextWeekOpen, setNextWeekOpen] = useState(true);
  const [dashSummary, setDashSummary] = useState<{
    today_sessions: Array<{ appointment_id: string; coach_client_id: string; client_name: string; scheduled_at: string; meeting_url: string | null; meeting_name: string | null; status: string }>;
    this_week_count: number;
    next_week_count: number;
    this_month_count: number;
    outstanding_by_client: Array<{ client_id: string; client_name: string; total_cents: number; currency: string }>;
    outstanding_total_cents: number;
    outstanding_currency: string;
    active_enrollments_count: number;
    active_enrollments: Array<{ enrollment_id: string; client_name: string; package_name: string; sessions_completed: number; total_sessions: number | null }>;
    revenue_by_month: Array<{ month: string; month_key: string; total_cents: number; currency: string; by_client: Array<{ client_name: string; cents: number }> }>;
    revenue_currency: string;
    all_time_revenue_cents: number;
    all_time_revenue_currency: string;
  } | null>(null);
  const [enrollmentsModalOpen, setEnrollmentsModalOpen] = useState(false);

  // ── Outstanding payments detail view ─────────────────────────────────────────
  type OutstandingAppt = (
    | {
        kind: 'session';
        appointment_id: string;
        scheduled_at: string;
        meeting_name: string | null;
        amount_cents: number;
        currency: string;
        invoice_id: string | null;
        invoice_status: string | null;
      }
    | {
        kind: 'package';
        payment_id: string;
        enrollment_id: string;
        package_name: string;
        label: string;
        due_date: string | null;
        payment_status: string;
        amount_cents: number;
        currency: string;
      }
  );
  const [outstandingClientModal, setOutstandingClientModal] = useState<{ client_id: string; client_name: string } | null>(null);
  const [outstandingAppts, setOutstandingAppts] = useState<OutstandingAppt[]>([]);
  const [outstandingLoading, setOutstandingLoading] = useState(false);
  const [outstandingActionLoading, setOutstandingActionLoading] = useState<string | null>(null);
  const [outstandingError, setOutstandingError] = useState('');

  const fetchOutstandingAppts = async (clientId: string) => {
    setOutstandingLoading(true);
    setOutstandingError('');
    try {
      const token = localStorage.getItem('authToken');
      const r = await fetch(`${config.apiBaseUrl}/coaches/me/outstanding-payments?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Failed to load');
      setOutstandingAppts(await r.json());
    } catch (e: any) {
      setOutstandingError(e.message || 'Failed to load');
    } finally {
      setOutstandingLoading(false);
    }
  };

  // handleOutstandingProcessPay / handleOutstandingInvoice / handleOutstandingResendInvoice
  // are defined after openPaymentPanel (to avoid use-before-define).

  const [upcomingOpen, setUpcomingOpen] = useState(false);
  const [pastOpen, setPastOpen] = useState(false);
  const [groupExpandedClients, setGroupExpandedClients] = useState<Map<string, Set<string>>>(() => new Map());
  const toggleGroupClient = (groupLabel: string, clientName: string) =>
    setGroupExpandedClients(prev => {
      const next = new Map(prev);
      const cur = new Set(next.get(groupLabel) ?? []);
      cur.has(clientName) ? cur.delete(clientName) : cur.add(clientName);
      next.set(groupLabel, cur);
      return next;
    });

  // ── Session grouping ───────────────────────────────────────────────────────

  const todayStr    = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD local
  const tomorrowStr = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toLocaleDateString('en-CA'); })();
  // End of current calendar week (Sunday); if today is Sunday daysToSun=0 so thisWeekEnd=today
  const thisWeekEnd = (() => { const d = new Date(); const daysToSun = (7 - d.getDay()) % 7; d.setDate(d.getDate() + daysToSun); return d.toLocaleDateString('en-CA'); })();
  // End of next calendar week (following Sunday)
  const nextWeekEnd = (() => { const d = new Date(); const daysToSun = (7 - d.getDay()) % 7; d.setDate(d.getDate() + daysToSun + 7); return d.toLocaleDateString('en-CA'); })();

  const pastSessions     = upcomingSessions.filter(s => s.date < todayStr);
  const todaySessions    = upcomingSessions.filter(s => s.date === todayStr);
  const tomorrowSessions = upcomingSessions.filter(s => s.date === tomorrowStr);
  const thisWeekSessions = upcomingSessions.filter(s => s.date > tomorrowStr && s.date <= thisWeekEnd);
  const nextWeekSessions = upcomingSessions.filter(s => s.date > thisWeekEnd && s.date <= nextWeekEnd);
  const laterSessions    = upcomingSessions.filter(s => s.date > nextWeekEnd);
  const applyScheduleFilters = (sessions: Session[]) => {
    return sessions.filter(s => {
      const matchClient = !scheduleSearch || s.clientName.toLowerCase().includes(scheduleSearch.toLowerCase());
      const matchFrom   = !scheduleDateFrom || s.date >= scheduleDateFrom;
      const matchTo     = !scheduleDateTo   || s.date <= scheduleDateTo;
      return matchClient && matchFrom && matchTo;
    });
  };

  // ── Calendar events ────────────────────────────────────────────────────────

  const calendarEvents = useMemo((): CalendarEvent[] => {
    const apptEvents: CalendarEvent[] = upcomingSessions.map(s => {
      const start = new Date(s.scheduledAt);
      const end = new Date(start.getTime() + (s.duration || 60) * 60_000);
      return {
        id: s.id,
        title: s.meetingName && s.meetingName !== s.clientName
          ? `${s.meetingName}: ${s.clientName}`
          : s.clientName,
        start,
        end,
        resource: {
          type: 'appointment' as const,
          status: s.status,
          clientName: s.clientName,
          appointmentId: s.appointmentId,
          googleMeetLink: s.googleMeetLink,
          appointmentType: s.appointmentType,
          recurringSeriesId: s.recurringSeriesId,
        },
      };
    });
    // Merge Google Calendar + Calendly events, deduping any that overlap with CoachSight appointments
    const apptTimes = new Set(apptEvents.map(e => e.start.toISOString()));
    const gcalFiltered = googleCalEvents.filter(e => !apptTimes.has(e.start.toISOString()));
    const calendlyFiltered = calendlyCalEvents.filter(e => !apptTimes.has(e.start.toISOString()));
    return [...apptEvents, ...gcalFiltered, ...calendlyFiltered];
  }, [upcomingSessions, googleCalEvents, calendlyCalEvents]);


  // ── Handlers ──────────────────────────────────────────────────────────────

  const openSchedule = (client: Client) => {
    setRescheduleTarget(null);
    setRescheduleScope(undefined);
    setSelectedClientForSession(client);
    // Always refresh Google Calendar events so the modal shows up-to-date busy blocks
    // regardless of which section is currently active.
    fetchGoogleCalendarEvents();
    if (calendlyStatus?.scheduling_type === 'calendly') {
      // Ensure Calendly event types and selected URIs are loaded
      if (calendlySelectedUris.length === 0 && calendlyStatus?.event_type_uris?.length) {
        setCalendlySelectedUris(calendlyStatus.event_type_uris);
      }
      if (calendlyEventTypes.length === 0) {
        handleCalendlyLoadEventTypes();
      }
    }
    setIsScheduleOpen(true);
  };



  const generatePrep = async (appointmentId: string) => {
    setPrepGeneratingIds(prev => new Set(prev).add(appointmentId));
    try {
      await fetch(`${config.apiBaseUrl}/api/session-prep/${appointmentId}/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
      });
    } finally {
      setPrepGeneratingIds(prev => { const s = new Set(prev); s.delete(appointmentId); return s; });
    }
  };

  // Opens the standard booking flow (ScheduleSessionModal) in reschedule mode:
  // same session-type picker and green availability slots, but confirming moves
  // the existing appointment instead of creating a new one.
  const openReschedule = (session: Session, recurringScope?: 'this_only' | 'this_and_future') => {
    if (!session.appointmentId) return;
    const client = clients.find(c => c.client_id === session.clientId);
    if (client) {
      setSelectedClientForSession(client);
    } else {
      // Session's client not in the loaded list (e.g. filtered) — build the
      // minimal shape ScheduleSessionModal needs from the session itself.
      setSelectedClientForSession({
        client_id: session.clientId ?? '',
        relationship_id: session.relationshipId ?? '',
        name: session.clientName,
        email: '',
        ai_consent: null,
      } as unknown as Client);
    }
    setRescheduleTarget(session);
    setRescheduleScope(recurringScope);
    fetchGoogleCalendarEvents();
    setIsScheduleOpen(true);
  };



  const openPaymentPanel = async (session: Session) => {
    if (!session.appointmentId) return;
    setPaymentPanelSession(session);
    setPanelPricing(null);
    setPanelHasCard(null);
    setPanelCardLast4(null);
    setPanelHasInvoice(null);
    setPanelChargeConfirm(false);
    setPanelEditingRate(false);
    setPanelError('');
    setPanelSuccess('');
    setPanelPricingLoading(true);
    try {
      const [pricingData, cardData, invoiceData] = await Promise.all([
        getAppointmentPricing(session.appointmentId),
        session.clientId ? getClientPaymentMethod(session.clientId).catch(() => ({ has_card: false })) : Promise.resolve({ has_card: false }),
        getInvoiceForSession(session.appointmentId).catch(() => null),
      ]);
      setPanelHasInvoice(invoiceData !== null);
      setPanelPricing(pricingData.pricing);
      if (pricingData.pricing) {
        setPanelRateInput(String(pricingData.pricing.rate));
        setPanelRateCurrency(pricingData.pricing.currency);
      } else {
        setPanelRateInput('');
        setPanelRateCurrency('usd');
        setPanelEditingRate(true); // open input immediately when no rate is set
      }
      setPanelHasCard((cardData as any).has_card ?? false);
      setPanelCardLast4((cardData as any).card_last4 ?? null);
    } catch { /* ignore */ }
    finally { setPanelPricingLoading(false); }
  };

  const handleOutstandingProcessPay = async (appt: OutstandingAppt) => {
    if (!outstandingClientModal || appt.kind !== 'session') return;
    const minSess: Session = {
      id: appt.appointment_id,
      appointmentId: appt.appointment_id,
      clientId: outstandingClientModal.client_id,
      clientName: outstandingClientModal.client_name,
      scheduledAt: appt.scheduled_at,
      date: appt.scheduled_at.split('T')[0],
      time: '',
      meetingName: appt.meeting_name || '',
      duration: 60,
      type: 'virtual',
      status: 'confirmed',
    };
    setOutstandingClientModal(null);
    await openPaymentPanel(minSess);
  };

  const handleOutstandingInvoice = async (appt: OutstandingAppt) => {
    if (!outstandingClientModal || appt.kind !== 'session') return;
    setOutstandingActionLoading(appt.appointment_id);
    setOutstandingError('');
    try {
      const inv = await createInvoice({ appointment_id: appt.appointment_id });
      await sendInvoice(inv.id);
      await fetchOutstandingAppts(outstandingClientModal.client_id);
    } catch (e: any) {
      setOutstandingError(e.message || 'Failed to send invoice');
    } finally {
      setOutstandingActionLoading(null);
    }
  };

  const handleOutstandingResendInvoice = async (appt: OutstandingAppt) => {
    if (!outstandingClientModal || appt.kind !== 'session' || !appt.invoice_id) return;
    setOutstandingActionLoading(appt.appointment_id);
    setOutstandingError('');
    try {
      await resendInvoice(appt.invoice_id);
      await fetchOutstandingAppts(outstandingClientModal.client_id);
    } catch (e: any) {
      setOutstandingError(e.message || 'Failed to resend invoice');
    } finally {
      setOutstandingActionLoading(null);
    }
  };

  const handlePanelSaveRate = async () => {
    if (!paymentPanelSession?.appointmentId) return;
    const rate = parseFloat(panelRateInput);
    if (isNaN(rate) || rate <= 0) { setPanelError('Enter a valid rate greater than 0'); return; }
    setPanelActionLoading(true);
    setPanelError('');
    try {
      await setAppointmentPricing(paymentPanelSession.appointmentId, rate, panelRateCurrency);
      const data = await getAppointmentPricing(paymentPanelSession.appointmentId);
      setPanelPricing(data.pricing);
      setPanelEditingRate(false);
      setPanelSuccess('Rate saved');
    } catch (e: any) { setPanelError(e.message || 'Failed to save rate'); }
    finally { setPanelActionLoading(false); }
  };

  const handlePanelCharge = async () => {
    if (!paymentPanelSession?.appointmentId) return;
    if (!panelHasInvoice) {
      setPanelChargeConfirm(true);
      return;
    }
    await _executePanelCharge();
  };

  const handlePanelChargeConfirmed = async () => {
    if (!paymentPanelSession?.appointmentId) return;
    setPanelChargeConfirm(false);
    setPanelActionLoading(true);
    setPanelError('');
    try {
      const inv = await createInvoice({ appointment_id: paymentPanelSession.appointmentId });
      await sendInvoice(inv.id);
      setPanelHasInvoice(true);
    } catch (e: any) {
      setPanelError(e.message || 'Failed to create invoice');
      setPanelActionLoading(false);
      return;
    }
    setPanelActionLoading(false);
    await _executePanelCharge();
  };

  const _executePanelCharge = async () => {
    if (!paymentPanelSession?.appointmentId) return;
    setPanelActionLoading(true);
    setPanelError('');
    setPanelSuccess('');
    try {
      const result = await chargeClient(paymentPanelSession.appointmentId);
      setPanelSuccess(`Charged ${formatCents(result.amount_cents, result.currency)} — ${result.status}`);
      const ps = await getAppointmentPaymentStatus(paymentPanelSession.appointmentId);
      setApptPaymentStatuses(prev => ({ ...prev, [paymentPanelSession.appointmentId!]: ps }));
      const pricing = await getAppointmentPricing(paymentPanelSession.appointmentId);
      setPanelPricing(pricing.pricing);
    } catch (e: any) { setPanelError(e.message || 'Charge failed'); }
    finally { setPanelActionLoading(false); }
  };

  const handlePanelWaive = async () => {
    if (!paymentPanelSession?.appointmentId) return;
    setPanelActionLoading(true);
    setPanelError('');
    try {
      await updatePricingStatus(paymentPanelSession.appointmentId, 'waived');
      const data = await getAppointmentPricing(paymentPanelSession.appointmentId);
      setPanelPricing(data.pricing);
      setPanelSuccess('Payment waived');
    } catch (e: any) { setPanelError(e.message || 'Failed to waive'); }
    finally { setPanelActionLoading(false); }
  };

  const handlePanelSendInvoice = async () => {
    if (!paymentPanelSession?.appointmentId) return;
    setPanelActionLoading(true);
    setPanelError('');
    try {
      const inv = await createInvoice({ appointment_id: paymentPanelSession.appointmentId });
      await sendInvoice(inv.id);
      setPanelSuccess('Invoice sent to client');
    } catch (e: any) { setPanelError(e.message || 'Failed to send invoice'); }
    finally { setPanelActionLoading(false); }
  };

  const closeUploadDialog = () => {
    setUploadSession(null);
    setUploadFile(null);
    setUploadConfirming(false);
    setUploadError('');
  };

  const handleTranscriptUpload = async (file: File) => {
    if (!uploadSession?.appointmentId) return;
    setUploadConfirming(false);
    setUploadLoading(true);
    setUploadError('');
    try {
      const text = await file.text();
      const res = await fetch(
        `${config.apiBaseUrl}/session/${uploadSession.appointmentId}/summarize-transcript`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
          },
          body: JSON.stringify({ transcript_text: text }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      closeUploadDialog();
      setSummarySession(uploadSession);
    } catch (e: any) {
      setUploadError(e.message || 'Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleLogout = () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      fetch(`${config.apiBaseUrl}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    navigate('/');
  };


  const toggleClientStatus = async (client: Client) => {
    if (!client.relationship_id) return;
    setTogglingClientId(client.client_id);
    try {
      const authToken = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}/coaches/${coachId}/clients/${client.relationship_id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ is_active: !client.is_active }),
      });
      if (!res.ok) throw new Error('Failed to update status');
      setClients(prev => prev.map(c =>
        c.client_id === client.client_id ? { ...c, is_active: !c.is_active, status: !c.is_active ? 'active' : 'inactive' } : c
      ));
    } catch (err) {
      console.error('Toggle failed:', err);
    } finally {
      setTogglingClientId(null);
    }
  };

  // ── Shared sub-components ─────────────────────────────────────────────────

  const SessionCard = ({ session, showDate = false }: { session: Session; showDate?: boolean }) => {
    const Icon = session.type === 'virtual' ? VideoCameraIcon : MapPinIcon;
    const tz = coachTimezone || undefined;
    const sessionDate = new Date(session.scheduledAt);
    const dateLabel = showDate
      ? sessionDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz })
      : null;
    const timeLabel = sessionDate.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: tz });

    const isPast = session.date < new Date().toLocaleDateString('en-CA');

    const [showTypeMenu, setShowTypeMenu] = React.useState(false);
    const [typeDraft, setTypeDraft] = React.useState(session.appointmentType || 'billable');
    const [typeBarterDraft, setTypeBarterDraft] = React.useState(session.barterNote || '');
    const [typeSaving, setTypeSaving] = React.useState(false);
    const [typeError, setTypeError] = React.useState('');
    const [currentType, setCurrentType] = React.useState(session.appointmentType || 'billable');

    // Meeting status
    const [meetingStatus, setMeetingStatus] = React.useState(session.appointmentStatus || session.status);
    const [showStatusMenu, setShowStatusMenu] = React.useState(false);
    const [statusSaving, setStatusSaving] = React.useState(false);

    const MEETING_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
      scheduled:    { label: 'Scheduled', className: 'text-gray-400 border-dark-border hover:border-gray-500' },
      confirmed:    { label: 'Scheduled', className: 'text-gray-400 border-dark-border hover:border-gray-500' },
      completed:    { label: 'Completed', className: 'text-green-400 border-green-800/50 hover:border-green-600' },
      no_show:      { label: 'No-Show',   className: 'text-amber-400 border-amber-800/50 hover:border-amber-600' },
      cancelled:    { label: 'Cancelled', className: 'text-red-400 border-red-800/50 hover:border-red-600' },
      in_progress:  { label: 'Live',      className: 'text-blue-400 border-blue-800/50' },
    };

    const saveMeetingStatus = async (newStatus: 'scheduled' | 'completed' | 'no_show' | 'cancelled') => {
      if (!session.appointmentId) return;
      setStatusSaving(true);
      try {
        await updateMeetingStatus(session.appointmentId, newStatus);
        setMeetingStatus(newStatus);
        setShowStatusMenu(false);
      } catch (err: any) {
        // surface error inline if needed — for now just close
      } finally {
        setStatusSaving(false);
      }
    };

    const undoManualPayment = async () => {
      if (!session.appointmentId) return;
      try {
        await resetManualPayment(session.appointmentId);
        setApptPaymentStatuses(prev => {
          const next = { ...prev };
          const existing = next[session.appointmentId!];
          if (existing) {
            next[session.appointmentId!] = {
              ...existing,
              status: existing.pricing_rate_cents ? 'unpaid' : 'no_rate',
              payment: null,
            };
          }
          return next;
        });
      } catch { /* silent */ }
    };

    const TYPE_LABELS: Record<string, { label: string; className: string }> = {
      billable:      { label: 'Billable',      className: 'text-gray-400 bg-dark-surface border-dark-border hover:border-gray-500' },
      pro_bono:      { label: 'Pro Bono',      className: 'text-blue-400 bg-blue-900/20 border-blue-800/50 hover:border-blue-600' },
      barter:        { label: 'Barter',        className: 'text-amber-400 bg-amber-900/20 border-amber-800/50 hover:border-amber-600' },
      complimentary: { label: 'Complimentary', className: 'text-green-400 bg-green-900/20 border-green-800/50 hover:border-green-600' },
    };

    const saveType = async () => {
      if (typeDraft === 'barter' && !typeBarterDraft.trim()) {
        setTypeError('A barter note is required.');
        return;
      }
      if (!session.appointmentId) return;
      setTypeSaving(true);
      setTypeError('');
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${config.apiBaseUrl}/coaches/sessions/${session.appointmentId}/type`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ appointment_type: typeDraft, barter_note: typeDraft === 'barter' ? typeBarterDraft.trim() : undefined }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed to update');
        setCurrentType(typeDraft);
        setShowTypeMenu(false);
      } catch (err: any) {
        setTypeError(err.message || 'Failed to update');
      } finally {
        setTypeSaving(false);
      }
    };

    return (
      <>
      <div className="flex items-center justify-between py-3 px-4 rounded-lg border border-dark-border hover:border-dark-border hover:bg-dark-hover transition-colors">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 text-right w-20">
            <span className="text-sm font-semibold text-white block">{timeLabel}</span>
            {dateLabel && <span className="text-xs text-dark-muted">{dateLabel}</span>}
          </div>
          <div className="w-px h-7 bg-dark-border flex-shrink-0" />
          <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/dashboard/coach/session/${session.appointmentId}`)}
                className="text-sm font-medium text-white truncate hover:text-primary-300 transition-colors text-left"
              >
                {session.meetingName || session.clientName}
              </button>
              {session.source === 'manual_entry' && (
                <span className="flex-shrink-0 text-xs bg-dark-surface border border-dark-border text-gray-400 px-1.5 py-0.5 rounded">Manual</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-xs text-dark-muted">{session.clientName} · {session.duration} min</p>
              {/* Appointment type picker */}
              {session.appointmentId && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); setTypeDraft(currentType); setTypeBarterDraft(session.barterNote || ''); setTypeError(''); setShowTypeMenu(p => !p); }}
                    className={`text-xs border px-1.5 py-0.5 rounded transition-colors ${TYPE_LABELS[currentType]?.className}`}
                  >
                    {TYPE_LABELS[currentType]?.label ?? currentType}
                  </button>
                  {showTypeMenu && (
                    <div className="absolute left-0 top-full mt-1 z-50 bg-dark-card border border-dark-border rounded-lg shadow-xl p-3 w-52 space-y-2">
                      <select
                        value={typeDraft}
                        onChange={e => setTypeDraft(e.target.value as AppointmentType)}
                        className="w-full text-xs border border-dark-border rounded px-2 py-1.5 bg-dark-canvas text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      >
                        {APPOINTMENT_TYPES.map(t => (
                          <option key={t} value={t}>{getAppointmentTypeLabel(t)}</option>
                        ))}
                      </select>
                      {typeDraft === 'barter' && (
                        <textarea
                          value={typeBarterDraft}
                          onChange={e => setTypeBarterDraft(e.target.value)}
                          rows={2}
                          placeholder="Describe the exchange…"
                          className="w-full text-xs border border-dark-border rounded px-2 py-1.5 bg-dark-canvas text-gray-200 placeholder-dark-muted focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none"
                        />
                      )}
                      {typeError && <p className="text-xs text-red-400">{typeError}</p>}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={saveType}
                          disabled={typeSaving}
                          className="flex-1 text-xs bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded px-2 py-1"
                        >
                          {typeSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowTypeMenu(false)}
                          className="flex-1 text-xs border border-dark-border text-gray-400 hover:text-gray-200 rounded px-2 py-1"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Meeting status picker */}
              {session.appointmentId && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); setShowStatusMenu(p => !p); }}
                    disabled={statusSaving}
                    className={`text-xs border px-1.5 py-0.5 rounded transition-colors disabled:opacity-50 ${MEETING_STATUS_CONFIG[meetingStatus]?.className ?? 'text-gray-400 border-dark-border'}`}
                  >
                    {MEETING_STATUS_CONFIG[meetingStatus]?.label ?? meetingStatus}
                  </button>
                  {showStatusMenu && (
                    <div className="absolute left-0 top-full mt-1 z-50 bg-dark-card border border-dark-border rounded-lg shadow-xl py-1 w-40">
                      {(['scheduled', 'completed', 'no_show', 'cancelled'] as const).map(s => (
                        <button
                          key={s}
                          type="button"
                          onClick={e => { e.stopPropagation(); saveMeetingStatus(s); }}
                          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-dark-hover transition-colors ${meetingStatus === s ? 'text-white font-semibold' : 'text-gray-300'}`}
                        >
                          {MEETING_STATUS_CONFIG[s]?.label ?? s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Package badge */}
              {session.packageName && (
                <span className="text-xs border px-1.5 py-0.5 rounded text-violet-400 bg-violet-900/20 border-violet-800/50 truncate max-w-[120px]" title={session.packageName}>
                  {session.packageName}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
          {!isPast && (
            session.googleMeetLink ? (
              <Tooltip text="Join meeting">
                <button
                  onClick={() => openJoinWindow(buildJoinUrl(session.googleMeetLink!, session.appointmentId, session.clientName || session.meetingName))}
                  className="p-1.5 rounded text-primary-400 hover:text-primary-300 hover:bg-dark-hover transition-colors"
                >
                  <VideoCameraIcon className="w-4 h-4" />
                </button>
              </Tooltip>
            ) : (
              <Tooltip text="No meeting link">
                <span className="p-1.5 rounded text-dark-muted cursor-default">
                  <VideoCameraIcon className="w-4 h-4" />
                </span>
              </Tooltip>
            )
          )}
          {session.appointmentId && (
            <Tooltip text="Upload transcript">
              <button
                onClick={() => { setUploadSession(session); setUploadError(''); }}
                className="p-1.5 rounded text-sky-400 hover:text-sky-300 hover:bg-dark-hover transition-colors"
              >
                <ArrowUpTrayIcon className="w-4 h-4" />
              </button>
            </Tooltip>
          )}
          {session.appointmentId && !['completed', 'cancelled'].includes(session.status) && (
            <Tooltip text={prepGeneratingIds.has(session.appointmentId) ? 'Generating prep…' : 'Generate session prep'}>
              <button
                onClick={() => generatePrep(session.appointmentId!)}
                disabled={prepGeneratingIds.has(session.appointmentId)}
                className="p-1.5 rounded text-violet-400 hover:text-violet-300 hover:bg-dark-hover transition-colors disabled:opacity-40"
              >
                <SparklesIcon className="w-4 h-4" />
              </button>
            </Tooltip>
          )}
          {session.appointmentId && (
            <Tooltip text="View session notes">
              <button
                onClick={() => setSummarySession(session)}
                className="p-1.5 rounded text-blue-400 hover:text-blue-300 hover:bg-dark-hover transition-colors"
              >
                <ClipboardDocumentListIcon className="w-4 h-4" />
              </button>
            </Tooltip>
          )}
          {session.appointmentId && session.status === 'confirmed' && (
            <Tooltip text="Cancel / reschedule session">
              <button
                onClick={() => setManageSession(session)}
                disabled={cancellingAppointmentId === session.appointmentId}
                className="p-1.5 rounded text-amber-400 hover:text-amber-300 hover:bg-dark-hover transition-colors disabled:opacity-50"
              >
                <ClockIcon className="w-4 h-4" />
              </button>
            </Tooltip>
          )}
          {session.appointmentId && (() => {
            const ps = apptPaymentStatuses[session.appointmentId!];
            const status = ps?.status;
            if (status === 'succeeded') {
              return <span className="text-xs font-medium text-green-400 bg-green-900/30 border border-green-800 px-2 py-0.5 rounded-full">Paid</span>;
            }
            if (status === 'manual') {
              const method = ps?.payment?.payment_method_note || ps?.payment?.payment_method || 'manual';
              return (
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium text-emerald-400 bg-emerald-900/30 border border-emerald-800 px-2 py-0.5 rounded-full capitalize"
                        title={`Paid via ${method}`}>Paid ({method})</span>
                  <Tooltip text="Undo manual payment">
                    <button onClick={e => { e.stopPropagation(); undoManualPayment(); }}
                            className="p-0.5 text-gray-500 hover:text-red-400 transition-colors">
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </Tooltip>
                </div>
              );
            }
            if (status === 'pending') {
              return <span className="text-xs font-medium text-yellow-400 bg-yellow-900/30 border border-yellow-800 px-2 py-0.5 rounded-full">Pending</span>;
            }
            if (status === 'refunded') {
              return <span className="text-xs font-medium text-gray-400 bg-gray-800 border border-gray-600 px-2 py-0.5 rounded-full">Refunded</span>;
            }
            return null;
          })()}
          {session.appointmentId && (
            <Tooltip text="Edit session details">
              <button
                onClick={e => { e.stopPropagation(); setSessionDetailSession(session); }}
                className="p-1.5 rounded text-gray-400 hover:text-gray-200 hover:bg-dark-hover transition-colors"
              >
                <PencilSquareIcon className="w-4 h-4" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

    </>
    );
  };

  const SessionGroup = ({
    label, sessions, showDate = false, collapsible = false, open, onToggle, groupByClient = false,
    expandedClients, onToggleClient,
  }: {
    label: string; sessions: Session[]; showDate?: boolean;
    collapsible?: boolean; open?: boolean; onToggle?: () => void;
    groupByClient?: boolean;
    expandedClients?: Set<string>; onToggleClient?: (name: string) => void;
  }) => {
    if (sessions.length === 0) return null;
    const isOpen = !collapsible || open;

    // Build ordered client groups preserving first-occurrence order
    const clientGroups: { name: string; sessions: Session[] }[] = [];
    const seen = new Map<string, Session[]>();
    for (const s of sessions) {
      if (!seen.has(s.clientName)) { seen.set(s.clientName, []); clientGroups.push({ name: s.clientName, sessions: seen.get(s.clientName)! }); }
      seen.get(s.clientName)!.push(s);
    }

    const toggleClient = (name: string) => onToggleClient?.(name);

    return (
      <div>
        <button
          type="button"
          onClick={collapsible ? onToggle : undefined}
          className={`flex items-center gap-1.5 mb-2 w-full text-left ${collapsible ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <p className="text-xs font-semibold text-dark-muted uppercase tracking-wider">
            {label} <span className="font-normal normal-case">({sessions.length})</span>
          </p>
          {collapsible && (
            <ChevronRightIcon className={`w-3.5 h-3.5 text-dark-muted transition-transform ${isOpen ? 'rotate-90' : ''}`} />
          )}
        </button>
        {isOpen && (
          groupByClient ? (
            <div className="space-y-1">
              {clientGroups.map(({ name, sessions: cs }) => {
                const clientOpen = (expandedClients ?? new Set()).has(name);
                return (
                  <div key={name} className="border border-dark-border rounded-lg">
                    <button
                      type="button"
                      onClick={() => toggleClient(name)}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-dark-canvas hover:bg-dark-hover transition-colors text-left rounded-t-lg"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-primary-700">{initials(name)}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-100">{name}</span>
                        <span className="text-xs text-dark-muted">{cs.length} session{cs.length > 1 ? 's' : ''}</span>
                      </div>
                      <ChevronRightIcon className={`w-3.5 h-3.5 text-dark-muted transition-transform ${clientOpen ? 'rotate-90' : ''}`} />
                    </button>
                    {clientOpen && (
                      <div className="divide-y divide-dark-border">
                        {cs.map(s => <SessionCard key={s.id} session={s} showDate={showDate} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-1.5">
              {sessions.map(s => <SessionCard key={s.id} session={s} showDate={showDate} />)}
            </div>
          )
        )}
      </div>
    );
  };

  // ── Section renderers ─────────────────────────────────────────────────────

  const DashboardSection = () => {
    const setupComplete = onboardingStatus
      ? onboardingStatus.verify_contact_info &&
        onboardingStatus.connect_calendar &&
        onboardingStatus.setup_payments &&
        onboardingStatus.complete_profile &&
        onboardingStatus.upload_agreement_template
      : false;

    // Expand setup card by default only when required steps are still pending.
    // null → blockers unknown (status loading) so default open; once loaded, collapse if no blockers.
    const hasBlockers = onboardingStatus
      ? !onboardingStatus.verify_contact_info || !onboardingStatus.connect_calendar
      : true;
    const [setupOpen, setSetupOpen] = React.useState(hasBlockers);
    React.useEffect(() => { setSetupOpen(hasBlockers); }, [hasBlockers]);

    // DST notice banner: show until dismissed after the most recent DST transition date.
    const [dstNoticeDismissed, setDstNoticeDismissed] = React.useState(() => {
      const stored = localStorage.getItem(DST_NOTICE_KEY);
      return !!stored && stored >= DST_NOTICE_DATE;
    });
    const dismissDstNotice = () => {
      localStorage.setItem(DST_NOTICE_KEY, new Date().toISOString());
      setDstNoticeDismissed(true);
    };

    // Phone verification modal
    const [phoneVerifyOpen, setPhoneVerifyOpen] = React.useState(false);
    const [pvPhone, setPvPhone] = React.useState<string | null>(null);
    const [pvOtpSent, setPvOtpSent] = React.useState(false);
    const [pvOtp, setPvOtp] = React.useState('');
    const [pvSending, setPvSending] = React.useState(false);
    const [pvVerifying, setPvVerifying] = React.useState(false);
    const [pvError, setPvError] = React.useState<string | null>(null);
    const tok = localStorage.getItem('authToken') || '';
    const pvHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` };

    const openPhoneVerifyModal = async () => {
      setPvOtpSent(false); setPvOtp(''); setPvError(null);
      setPhoneVerifyOpen(true);
      if (!pvPhone) {
        try {
          const d = await fetch(`${config.apiBaseUrl}/coaches/me`, { headers: pvHeaders }).then(r => r.ok ? r.json() : null);
          if (d?.phone) setPvPhone(d.phone);
        } catch { /* non-fatal */ }
      }
    };

    const pvSendCode = async () => {
      setPvSending(true); setPvError(null);
      try {
        const resp = await fetch(`${config.apiBaseUrl}/verify/send`, {
          method: 'POST', headers: pvHeaders,
          body: JSON.stringify({ channel: 'sms', phone: pvPhone }),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || 'Failed to send code');
        setPvOtpSent(true);
      } catch (e: any) { setPvError(e.message); }
      finally { setPvSending(false); }
    };

    const pvVerify = async () => {
      setPvVerifying(true); setPvError(null);
      try {
        const resp = await fetch(`${config.apiBaseUrl}/verify/check`, {
          method: 'POST', headers: pvHeaders,
          body: JSON.stringify({ channel: 'sms', code: pvOtp }),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.detail || 'Verification failed');
        setPhoneVerifyOpen(false);
        fetchOnboardingStatus();
      } catch (e: any) { setPvError(e.message); }
      finally { setPvVerifying(false); }
    };

    // Derived content presence flags (false while loading = default open)
    const hasSessionsContent = !dashSummary
      ? true
      : (dashSummary.today_sessions.length > 0 ||
         dashSummary.this_week_count > 0 ||
         dashSummary.next_week_count > 0 ||
         dashSummary.this_month_count > 0);
    const hasOutstandingContent = !dashSummary ? true : dashSummary.outstanding_by_client.length > 0;
    const hasEnrollmentsContent = !dashSummary ? true : dashSummary.active_enrollments.length > 0;
    const hasRevenueContent     = !dashSummary ? true : dashSummary.revenue_by_month.some(m => m.total_cents > 0);

    const [sessionsOpen,    setSessionsOpen]    = React.useState(true);
    const [outstandingOpen, setOutstandingOpen] = React.useState(true);
    const [enrollmentsOpen, setEnrollmentsOpen] = React.useState(true);
    const [revenueOpen,     setRevenueOpen]     = React.useState(true);

    React.useEffect(() => {
      if (!dashSummary) return;
      setSessionsOpen(hasSessionsContent);
      setOutstandingOpen(hasOutstandingContent);
      setEnrollmentsOpen(hasEnrollmentsContent);
      setRevenueOpen(hasRevenueContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashSummary]);

    const fmtCents = (cents: number, currency: string) => {
      const sym = currency === 'USD' ? '$' : currency + ' ';
      return `${sym}${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const fmtTime = (iso: string) => {
      const d = new Date(iso);
      return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: coachTimezone || undefined });
    };

    // Custom recharts tooltip for revenue chart
    const RevenueTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: { month: string; total_cents: number; currency: string; by_client: Array<{ client_name: string; cents: number }> } }> }) => {
      if (!active || !payload?.length) return null;
      const d = payload[0].payload;
      const sym = d.currency === 'USD' ? '$' : d.currency + ' ';
      return (
        <div className="bg-dark-card border border-dark-border rounded-lg p-3 text-xs shadow-xl min-w-[140px]">
          <p className="font-semibold text-white mb-1.5">{d.month}</p>
          {d.by_client.map(({ client_name, cents }) => (
            <div key={client_name} className="flex justify-between gap-4 text-dark-muted">
              <span className="truncate max-w-[100px]">{client_name}</span>
              <span className="text-gray-300 font-medium">{sym}{(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
            </div>
          ))}
          {d.by_client.length > 1 && (
            <div className="border-t border-dark-border mt-1.5 pt-1.5 flex justify-between text-white font-semibold">
              <span>Total</span>
              <span>{sym}{(d.total_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
            </div>
          )}
          {d.by_client.length === 0 && <p className="text-dark-muted">No revenue</p>}
        </div>
      );
    };

    if (outstandingClientModal) {
      return (
        <div className="p-8 space-y-6 max-w-5xl">
          {/* Back link */}
          <button
            onClick={() => setOutstandingClientModal(null)}
            className="flex items-center gap-1.5 text-sm text-primary-400 hover:text-primary-300 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Dashboard
          </button>

          {/* Header */}
          <div className="flex items-center gap-3">
            <BanknotesIcon className="w-5 h-5 text-amber-400" />
            <h2 className="text-xl font-bold text-white">Outstanding Payments</h2>
            <span className="text-gray-500">·</span>
            <span className="text-lg text-gray-300">{outstandingClientModal.client_name}</span>
          </div>

          {/* Content */}
          <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
            {outstandingLoading ? (
              <div className="px-6 py-12 text-center text-sm text-dark-muted">Loading…</div>
            ) : outstandingError ? (
              <div className="px-6 py-6 text-sm text-red-400">{outstandingError}</div>
            ) : outstandingAppts.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-dark-muted">No outstanding payments found</div>
            ) : (
              <div className="divide-y divide-dark-border">
                {outstandingAppts.map(appt => {
                  const rowKey = appt.kind === 'session' ? appt.appointment_id : appt.payment_id;
                  const isActing = outstandingActionLoading === rowKey;

                  if (appt.kind === 'session') {
                    const hasInvoice = !!appt.invoice_id;
                    const invoiceSent = hasInvoice && !!appt.invoice_status && ['sent', 'viewed', 'overdue'].includes(appt.invoice_status);
                    const linkedSession = upcomingSessions.find(s => s.appointmentId === appt.appointment_id);
                    return (
                      <div key={rowKey} className="px-6 py-5 flex items-start justify-between gap-6">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-dark-muted uppercase tracking-wide mb-0.5">Session</p>
                          <p className="text-sm font-medium text-white">
                            {new Date(appt.scheduled_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                            {' · '}
                            {new Date(appt.scheduled_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                          </p>
                          {appt.meeting_name && <p className="text-xs text-dark-muted mt-0.5">{appt.meeting_name}</p>}
                          <p className="text-base font-semibold text-amber-400 mt-1">{fmtCents(appt.amount_cents, appt.currency)}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                          {linkedSession && (
                            <Tooltip text="View session details">
                              <button
                                onClick={() => { setOutstandingClientModal(null); setSessionDetailSession(linkedSession); }}
                                className="text-dark-muted hover:text-primary-400 transition-colors"
                              >
                                <InformationCircleIcon className="w-4 h-4" />
                              </button>
                            </Tooltip>
                          )}
                          <Tooltip text="Process payment">
                            <button
                              disabled={isActing}
                              onClick={() => handleOutstandingProcessPay(appt)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
                            >
                              <CreditCardIcon className="w-3.5 h-3.5" />
                              Pay
                            </button>
                          </Tooltip>
                          {hasInvoice && (
                            <Tooltip text="View invoice in Reports">
                              <button
                                onClick={() => { setActiveSection('reports'); setReportsSubSection('invoices'); setOutstandingClientModal(null); }}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-dark-hover hover:bg-gray-700 text-gray-300 hover:text-white text-xs font-medium rounded-lg transition-colors"
                              >
                                <DocumentMagnifyingGlassIcon className="w-3.5 h-3.5" />
                                View Invoice
                              </button>
                            </Tooltip>
                          )}
                          {!hasInvoice && (
                            <Tooltip text="Create and send invoice">
                              <button
                                disabled={isActing}
                                onClick={() => handleOutstandingInvoice(appt)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-dark-hover hover:bg-gray-700 disabled:opacity-50 text-gray-300 hover:text-white text-xs font-medium rounded-lg transition-colors"
                              >
                                <DocumentTextIcon className="w-3.5 h-3.5" />
                                {isActing ? 'Sending…' : 'Create Invoice'}
                              </button>
                            </Tooltip>
                          )}
                          {invoiceSent && (
                            <Tooltip text="Resend invoice email">
                              <button
                                disabled={isActing}
                                onClick={() => handleOutstandingResendInvoice(appt)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-dark-hover hover:bg-gray-700 disabled:opacity-50 text-gray-300 hover:text-white text-xs font-medium rounded-lg transition-colors"
                              >
                                <EnvelopeIcon className="w-3.5 h-3.5" />
                                {isActing ? 'Sending…' : 'Resend Invoice'}
                              </button>
                            </Tooltip>
                          )}
                          {hasInvoice && !invoiceSent && appt.invoice_status && (
                            <span className="text-xs text-dark-muted capitalize px-1">Invoice {appt.invoice_status}</span>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Package payment row
                  return (
                    <div key={rowKey} className="px-6 py-5 flex items-start justify-between gap-6">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-dark-muted uppercase tracking-wide mb-0.5">Package Payment</p>
                        <p className="text-sm font-medium text-white">{appt.label}</p>
                        {appt.due_date && (
                          <p className="text-xs text-dark-muted mt-0.5">
                            Due {new Date(appt.due_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                            {appt.payment_status === 'failed' && <span className="ml-2 text-red-400 font-medium">· Failed</span>}
                          </p>
                        )}
                        <p className="text-base font-semibold text-amber-400 mt-1">{fmtCents(appt.amount_cents, appt.currency)}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Tooltip text="View package in Offerings">
                          <button
                            onClick={() => { setActiveSection('services'); setOutstandingClientModal(null); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-dark-hover hover:bg-gray-700 text-gray-300 hover:text-white text-xs font-medium rounded-lg transition-colors"
                          >
                            <RectangleStackIcon className="w-3.5 h-3.5" />
                            View Package
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {outstandingError && outstandingAppts.length > 0 && (
              <div className="px-6 py-3 border-t border-dark-border text-xs text-red-400">{outstandingError}</div>
            )}
          </div>
        </div>
      );
    }

    return (
    <div className="p-8 space-y-6 max-w-5xl">
      {/* Greeting */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">
            {getGreeting()}, {coachName.split(' ')[0]}.
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="relative group flex-shrink-0">
          <button
            onClick={() => setInviteOpen(o => !o)}
            disabled={!onboardingStatus?.verify_contact_info || !onboardingStatus?.connect_calendar}
            className="bg-primary-600 hover:bg-primary-700 disabled:bg-dark-border disabled:text-dark-muted disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            Invite Client
          </button>
          {(!onboardingStatus?.verify_contact_info || !onboardingStatus?.connect_calendar) && (
            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-dark-card border border-dark-border text-xs text-dark-muted rounded-lg px-3 py-2 w-48 text-center hidden group-hover:block z-10 pointer-events-none">
              {!onboardingStatus?.verify_contact_info ? 'Verify your email first' : 'Connect your calendar first'}
            </div>
          )}
        </div>
      </div>

      <InvitePanel open={inviteOpen} coachId={coachId} onClose={() => setInviteOpen(false)} onSuccess={fetchClients} />

      {/* ── DST shift notice ──────────────────────────────────────────────── */}
      {!dstNoticeDismissed && (
        <div className="flex items-start gap-3 bg-amber-900/30 border border-amber-700/50 rounded-xl px-4 py-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-300">Daylight saving time change</p>
            <p className="text-xs text-amber-200/70 mt-0.5">
              Some sessions may have shifted by one hour due to the upcoming Daylight savings time change. Please review your upcoming sessions and reschedule any that need adjustment. Use CoachSight's reschedule functionality to preserve the video meeting links.
            </p>
            <button
              onClick={() => setActiveSection('schedules')}
              className="mt-2 text-xs font-medium text-amber-300 hover:text-amber-200 underline underline-offset-2"
            >
              Review sessions
            </button>
          </div>
          <button
            onClick={dismissDstNotice}
            className="flex-shrink-0 text-amber-500 hover:text-amber-300 transition-colors"
            aria-label="Dismiss"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Phone Verification Modal ───────────────────────────────────────── */}
      {phoneVerifyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={() => setPhoneVerifyOpen(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative bg-dark-card border border-dark-border rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-white mb-1">Verify your phone number</h3>
            <p className="text-sm text-dark-muted mb-4">
              {pvPhone ? <span>We'll send a code to <span className="text-gray-300 font-medium">{pvPhone}</span></span> : 'Add a phone number in Settings → Profile first.'}
            </p>
            {pvError && <p className="text-xs text-red-400 bg-red-900/20 rounded-lg px-3 py-2 mb-3">{pvError}</p>}
            {!pvOtpSent ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={pvSendCode}
                  disabled={pvSending || !pvPhone}
                  className="flex-1 text-sm bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white rounded-lg px-4 py-2 transition-colors"
                >
                  {pvSending ? 'Sending…' : 'Send verification code'}
                </button>
                <button type="button" onClick={() => setPhoneVerifyOpen(false)} className="text-sm text-dark-muted hover:text-gray-300 px-3 py-2 transition-colors">Cancel</button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-dark-muted">Enter the 6-digit code sent to your phone.</p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={pvOtp}
                  onChange={e => setPvOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="······"
                  className="w-full px-3 py-2 text-sm bg-dark-canvas border border-dark-border rounded-lg text-gray-200 placeholder-dark-muted focus:outline-none focus:border-primary-500 tracking-widest text-center"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={pvVerify}
                    disabled={pvVerifying || pvOtp.length !== 6}
                    className="flex-1 text-sm bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg px-4 py-2 transition-colors"
                  >
                    {pvVerifying ? 'Verifying…' : 'Verify'}
                  </button>
                  <button type="button" onClick={() => { setPvOtpSent(false); setPvOtp(''); setPvError(null); }} className="text-xs text-dark-muted hover:text-gray-300 px-3 py-2 transition-colors">Resend</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Setup card (hidden when fully complete) ───────────────────────── */}
      {onboardingStatus && !setupComplete && (() => {
        const phoneItem = { key: 'is_phone_verified' as const, done: onboardingStatus.is_phone_verified };
        const doneCount = [
          onboardingStatus.verify_contact_info,
          onboardingStatus.connect_calendar,
          onboardingStatus.setup_payments,
          onboardingStatus.complete_profile,
          onboardingStatus.upload_agreement_template,
          phoneItem.done,
        ].filter(Boolean).length;
        const totalCount = 6;
        return (
          <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
            <button
              onClick={() => setSetupOpen(o => !o)}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center gap-2">
                {hasBlockers
                  ? <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                  : <div className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />}
                <span className="text-sm font-medium text-white">Setup</span>
                <span className="text-xs text-dark-muted">{doneCount} / {totalCount} complete</span>
              </div>
              <ChevronDownIcon className={`w-4 h-4 text-dark-muted transition-transform duration-200 ${setupOpen ? 'rotate-180' : ''}`} />
            </button>
            {setupOpen && (
              <ul className="divide-y divide-dark-border border-t border-dark-border">
                {[
                  { key: 'verify_contact_info' as const,       label: 'Verify your email',                required: true,  note: null,                                                             action: () => window.location.href = '/verify' },
                  { key: 'connect_calendar' as const,          label: 'Set up availability and calendar', required: true,  note: null,                                                             action: () => { setActiveSection('settings'); setSettingsSubSection('scheduling'); } },
                  { key: 'setup_payments' as const,            label: 'Set up payments',                  required: false, note: null,                                                             action: () => { setActiveSection('settings'); setSettingsSubSection('payments'); } },
                  { key: 'complete_profile' as const,          label: 'Complete your profile',            required: false, note: null,                                                             action: () => { setActiveSection('settings'); setSettingsSubSection('profile'); } },
                  { key: 'upload_agreement_template' as const, label: 'Upload an agreement template',     required: false, note: null,                                                             action: () => setActiveSection('documents') },
                  { key: 'is_phone_verified' as const,         label: 'Verify your phone number',         required: false, note: 'SMS notifications won\'t be delivered until your phone is verified', action: openPhoneVerifyModal },
                ].map(({ key, label, required, note, action }) => {
                  const done = onboardingStatus[key];
                  if (done) return null;
                  return (
                    <li key={key} className="flex items-start gap-3 px-5 py-2.5">
                      {required
                        ? <div className="w-3.5 h-3.5 mt-0.5 rounded-full border-2 border-red-500 flex-shrink-0" />
                        : <div className="w-3.5 h-3.5 mt-0.5 rounded-full border-2 border-amber-500/60 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm ${required ? 'text-red-400' : 'text-amber-400/80'}`}>{label}</span>
                        {note && <p className="text-xs text-dark-muted mt-0.5">{note}</p>}
                      </div>
                      <button onClick={action} className="text-xs text-primary-400 hover:text-primary-300 flex-shrink-0 mt-0.5">Set up →</button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })()}

      {/* ── Row 1: Sessions | Outstanding ─────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Sessions */}
        <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden flex flex-col">
          <div
            className="px-5 py-3 border-b border-dark-border flex items-center justify-between cursor-pointer hover:bg-dark-hover/30 transition-colors"
            onClick={() => setSessionsOpen(o => !o)}
          >
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-primary-400" />
              <span className="text-sm font-semibold text-white">Sessions</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={e => { e.stopPropagation(); setActiveSection('schedules'); }} className="text-xs text-primary-400 hover:text-primary-300">View all →</button>
              <ChevronDownIcon className={`w-4 h-4 text-dark-muted transition-transform duration-200 ${sessionsOpen ? 'rotate-180' : ''}`} />
            </div>
          </div>

          {sessionsOpen && (
            <>
              {/* Today */}
              <div className="px-5 py-3 border-b border-dark-border flex-1">
                <p className="text-xs font-medium text-dark-muted uppercase tracking-wide mb-2">Today</p>
                {!dashSummary ? (
                  <p className="text-xs text-dark-muted">Loading…</p>
                ) : dashSummary.today_sessions.length === 0 ? (
                  <p className="text-xs text-dark-muted">No sessions today</p>
                ) : (
                  <div className="space-y-2">
                    {dashSummary.today_sessions.map(s => (
                      <div key={s.appointment_id} className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <button
                            onClick={() => navigate(`/dashboard/coach/session/${s.appointment_id}`)}
                            className="text-sm font-medium text-white truncate hover:text-primary-300 transition-colors text-left w-full"
                          >
                            {s.client_name}
                          </button>
                          <p className="text-xs text-dark-muted">{fmtTime(s.scheduled_at)}{s.meeting_name ? ` · ${s.meeting_name}` : ''}</p>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          {s.meeting_url ? (
                            <Tooltip text="Join meeting">
                              <button
                                onClick={() => openJoinWindow(buildJoinUrl(s.meeting_url!, s.appointment_id, s.client_name || s.meeting_name || undefined))}
                                className="p-1.5 rounded text-primary-400 hover:text-primary-300 hover:bg-dark-hover transition-colors">
                                <VideoCameraIcon className="w-4 h-4" />
                              </button>
                            </Tooltip>
                          ) : (
                            <Tooltip text="No meeting link">
                              <span className="p-1.5 rounded text-dark-muted cursor-default">
                                <VideoCameraIcon className="w-4 h-4" />
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip text="View session notes">
                            <button
                              onClick={() => setSummarySession({
                                id: s.appointment_id,
                                appointmentId: s.appointment_id,
                                relationshipId: s.coach_client_id,
                                clientName: s.client_name,
                                meetingName: s.meeting_name || s.client_name,
                                scheduledAt: s.scheduled_at,
                                date: s.scheduled_at.slice(0, 10),
                                time: s.scheduled_at.slice(11, 16),
                                duration: 60,
                                type: 'virtual',
                                status: 'confirmed',
                                appointmentStatus: s.status,
                                appointmentType: 'billable',
                                googleMeetLink: s.meeting_url ?? undefined,
                                source: 'live',
                              })}
                              className="p-1.5 rounded text-blue-400 hover:text-blue-300 hover:bg-dark-hover transition-colors"
                            >
                              <ClipboardDocumentListIcon className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip text="Cancel / reschedule">
                            <button onClick={() => setActiveSection('schedules')}
                              className="p-1.5 rounded text-amber-400 hover:text-amber-300 hover:bg-dark-hover transition-colors">
                              <ClockIcon className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip text="Edit session">
                            <button onClick={() => setActiveSection('schedules')}
                              className="p-1.5 rounded text-gray-400 hover:text-gray-300 hover:bg-dark-hover transition-colors">
                              <PencilSquareIcon className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Week / month counts */}
              <div className="grid grid-cols-3 divide-x divide-dark-border">
                {[
                  { label: 'This week',  value: dashSummary?.this_week_count },
                  { label: 'Next week',  value: dashSummary?.next_week_count },
                  { label: 'This month', value: dashSummary?.this_month_count },
                ].map(({ label, value }) => (
                  <div key={label} className="px-3 py-3 text-center">
                    <p className="text-lg font-bold text-white">{value ?? '—'}</p>
                    <p className="text-xs text-dark-muted mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Outstanding Payments */}
        <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden flex flex-col">
          <div
            className="px-5 py-3 border-b border-dark-border flex items-center justify-between cursor-pointer hover:bg-dark-hover/30 transition-colors"
            onClick={() => setOutstandingOpen(o => !o)}
          >
            <div className="flex items-center gap-2">
              <BanknotesIcon className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-semibold text-white">Outstanding Payments</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={e => { e.stopPropagation(); setActiveSection('reports'); }} className="text-xs text-primary-400 hover:text-primary-300">Cashflow →</button>
              <ChevronDownIcon className={`w-4 h-4 text-dark-muted transition-transform duration-200 ${outstandingOpen ? 'rotate-180' : ''}`} />
            </div>
          </div>
          {outstandingOpen && (
            <>
              <div className="px-5 py-3 flex-1">
                {!dashSummary ? (
                  <p className="text-xs text-dark-muted">Loading…</p>
                ) : dashSummary.outstanding_by_client.length === 0 ? (
                  <p className="text-xs text-dark-muted">All payments up to date</p>
                ) : (
                  <div className="space-y-2">
                    {dashSummary.outstanding_by_client.map(({ client_id, client_name, total_cents, currency }) => (
                      <div key={client_id} className="flex items-center justify-between gap-2">
                        <button
                          onClick={() => { setOutstandingClientModal({ client_id, client_name }); fetchOutstandingAppts(client_id); }}
                          className="text-sm text-gray-300 hover:text-white truncate text-left underline-offset-2 hover:underline"
                        >
                          {client_name}
                        </button>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-sm font-medium text-amber-400">{fmtCents(total_cents, currency)}</span>
                          <button
                            onClick={() => { setOutstandingClientModal({ client_id, client_name }); fetchOutstandingAppts(client_id); }}
                            className="text-dark-muted hover:text-primary-400 transition-colors"
                            title="View session details"
                          >
                            <InformationCircleIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {dashSummary && dashSummary.outstanding_total_cents > 0 && (
                <div className="px-5 py-3 border-t border-dark-border flex justify-between">
                  <span className="text-xs text-dark-muted">Total</span>
                  <span className="text-sm font-bold text-amber-400">{fmtCents(dashSummary.outstanding_total_cents, dashSummary.outstanding_currency)}</span>
                </div>
              )}
            </>
          )}
        </div>

      </div>

      {/* ── Row 2: Active Enrollments | Revenue ───────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Active Package Enrollments */}
        <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden flex flex-col">
          <div
            className="px-5 py-3 border-b border-dark-border flex items-center justify-between cursor-pointer hover:bg-dark-hover/30 transition-colors"
            onClick={() => setEnrollmentsOpen(o => !o)}
          >
            <div className="flex items-center gap-2">
              <RectangleStackIcon className="w-4 h-4 text-indigo-400" />
              <span className="text-sm font-semibold text-white">Active Package Enrollments</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={e => { e.stopPropagation(); setActiveSection('services'); }} className="text-xs text-primary-400 hover:text-primary-300">Packages →</button>
              <ChevronDownIcon className={`w-4 h-4 text-dark-muted transition-transform duration-200 ${enrollmentsOpen ? 'rotate-180' : ''}`} />
            </div>
          </div>
          {enrollmentsOpen && (
            <>
              <div className="px-5 py-3 flex-1">
                {!dashSummary ? (
                  <p className="text-xs text-dark-muted">Loading…</p>
                ) : dashSummary.active_enrollments.length === 0 ? (
                  <p className="text-xs text-dark-muted">No active enrollments</p>
                ) : (
                  <button onClick={() => setEnrollmentsModalOpen(true)} className="w-full text-left space-y-2 group">
                    {dashSummary.active_enrollments.map(e => (
                      <div key={e.enrollment_id} className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm text-gray-300 truncate group-hover:text-white">{e.client_name}</p>
                          <p className="text-xs text-dark-muted truncate">{e.package_name}</p>
                        </div>
                        <span className="text-xs text-indigo-400 flex-shrink-0">
                          {e.sessions_completed}{e.total_sessions ? `/${e.total_sessions}` : ''} sessions
                        </span>
                      </div>
                    ))}
                  </button>
                )}
              </div>
              {dashSummary && dashSummary.active_enrollments_count > 0 && (
                <div className="px-5 py-3 border-t border-dark-border">
                  <button onClick={() => setEnrollmentsModalOpen(true)} className="text-xs text-indigo-400 hover:text-indigo-300">
                    View all {dashSummary.active_enrollments_count} enrollment{dashSummary.active_enrollments_count !== 1 ? 's' : ''} →
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Revenue */}
        <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden flex flex-col">
          <div
            className="px-5 py-3 border-b border-dark-border flex items-center justify-between cursor-pointer hover:bg-dark-hover/30 transition-colors"
            onClick={() => setRevenueOpen(o => !o)}
          >
            <div className="flex items-center gap-2">
              <ChartBarIcon className="w-4 h-4 text-green-400" />
              <span className="text-sm font-semibold text-white">Revenue</span>
              {dashSummary && (
                <span className="text-sm font-bold text-green-400 ml-1">
                  {fmtCents(dashSummary.all_time_revenue_cents, dashSummary.all_time_revenue_currency)} all time
                </span>
              )}
            </div>
            <ChevronDownIcon className={`w-4 h-4 text-dark-muted transition-transform duration-200 ${revenueOpen ? 'rotate-180' : ''}`} />
          </div>
          {revenueOpen && (
            <>
              <p className="text-xs text-dark-muted px-5 pt-3 pb-0">Last 3 months</p>
              <div className="flex-1 px-2 py-2" style={{ minHeight: 160 }}>
                {!dashSummary ? (
                  <p className="text-xs text-dark-muted px-3">Loading…</p>
                ) : (
                  <ResponsiveContainer width="100%" height={150}>
                    <BarChart data={dashSummary.revenue_by_month} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis hide />
                      <RechartsTooltip content={<RevenueTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                      <Bar dataKey="total_cents" radius={[4, 4, 0, 0]}>
                        {dashSummary.revenue_by_month.map((entry, idx) => (
                          <Cell key={idx} fill={entry.total_cents > 0 ? '#22c55e' : '#374151'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
              <button onClick={() => setActiveSection('reports')} className="px-5 py-3 border-t border-dark-border text-xs text-primary-400 hover:text-primary-300 text-left">
                View reports →
              </button>
            </>
          )}
        </div>

      </div>

      {/* ── Enrollments modal ──────────────────────────────────────────────── */}
      {enrollmentsModalOpen && dashSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setEnrollmentsModalOpen(false)}>
          <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <RectangleStackIcon className="w-4 h-4 text-indigo-400" />
                Active Package Enrollments
              </h3>
              <button onClick={() => setEnrollmentsModalOpen(false)} className="text-dark-muted hover:text-white">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="divide-y divide-dark-border max-h-96 overflow-y-auto">
              {dashSummary.active_enrollments.map(e => (
                <div key={e.enrollment_id} className="px-6 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-white">{e.client_name}</p>
                      <p className="text-xs text-dark-muted mt-0.5">{e.package_name}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-indigo-400">
                        {e.sessions_completed}{e.total_sessions ? ` / ${e.total_sessions}` : ''} sessions
                      </p>
                      <p className="text-xs text-dark-muted">completed{e.total_sessions ? ` of ${e.total_sessions} total` : ''}</p>
                    </div>
                  </div>
                  {e.total_sessions && (
                    <div className="mt-2 h-1.5 bg-dark-border rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full"
                        style={{ width: `${Math.min(100, (e.sessions_completed / e.total_sessions) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-dark-border">
              <button
                onClick={() => { setEnrollmentsModalOpen(false); setActiveSection('services'); }}
                className="text-sm text-primary-400 hover:text-primary-300"
              >
                Manage packages →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Onboarding Pipeline ────────────────────────────────────────────── */}
      {onboardingPipeline.length > 0 && (
        <div className="bg-dark-card rounded-2xl border border-dark-border overflow-hidden">
          <div className="px-5 py-4 border-b border-dark-border flex items-center gap-2">
            <UserGroupIcon className="w-4 h-4 text-amber-400" />
            <h2 className="font-semibold text-white text-sm">Client Onboarding</h2>
            <span className="ml-1 px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">{onboardingPipeline.length}</span>
          </div>
          <div className="divide-y divide-dark-border">
            {onboardingPipeline.map((c) => {
              const consentBlocked = c.ai_consent === null && c.onboarding_step !== 'invited';
              const stepLabel =
                c.onboarding_step === 'invited'     ? 'Invite sent — awaiting registration' :
                c.onboarding_step === 'registered'  ? 'Registered — send their Welcome Packet' :
                c.onboarding_step === 'packet_sent' ? 'Packet sent — awaiting signature' : '';
              const fullLabel = consentBlocked ? (stepLabel ? `${stepLabel} · AI consent not answered` : 'AI consent not answered') : stepLabel;
              return (
                <div key={c.coach_client_id} className={`px-5 py-3 flex items-center justify-between gap-4 ${consentBlocked ? 'bg-red-950/30 border-l-2 border-red-500' : ''}`}>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${consentBlocked ? 'text-red-300' : 'text-white'}`}>{c.client_name}</p>
                    <p className={`text-xs ${consentBlocked ? 'text-red-400 font-medium' : 'text-dark-muted'}`}>{fullLabel}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {c.onboarding_step === 'invited' && (
                      <button
                        disabled={resendingInviteId === c.client_id}
                        onClick={async () => {
                          setResendingInviteId(c.client_id);
                          try {
                            const res = await fetch(
                              `${(await import('../config/environment')).config.apiBaseUrl}/coaches/clients/${c.client_id}/resend-invite`,
                              { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('authToken') ?? ''}` } },
                            );
                            if (!res.ok) { const err = await res.json(); alert(err.detail || 'Failed to resend invite.'); }
                          } catch { alert('Network error. Please try again.'); }
                          finally { setResendingInviteId(null); }
                        }}
                        className="px-3 py-1.5 border border-dark-border text-gray-300 hover:text-white text-xs font-medium rounded-lg whitespace-nowrap disabled:opacity-50"
                      >
                        {resendingInviteId === c.client_id ? 'Sending…' : 'Resend Invite'}
                      </button>
                    )}
                    {(c.onboarding_step === 'registered' || c.onboarding_step === 'invited') && (
                      <button
                        onClick={async () => {
                          setPipelineSendModal(c); setPipelinePacketTitle('Welcome Packet');
                          setPipelineSelectedDocIds(new Set()); setPipelineSendError(''); setPipelineSendSuccess('');
                          try { setPipelineDocs(await listDocuments()); } catch { setPipelineDocs([]); }
                          try { setPipelineCustomTags(await listTemplateTags()); } catch { setPipelineCustomTags([]); }
                        }}
                        className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg whitespace-nowrap"
                      >
                        Send Packet →
                      </button>
                    )}
                    {c.onboarding_step === 'packet_sent' && (
                      <button
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/sign/`).catch(() => {}); alert('Signing link copied'); }}
                        className="px-3 py-1.5 border border-dark-border text-gray-300 hover:text-white text-xs font-medium rounded-lg whitespace-nowrap"
                      >
                        Copy link
                      </button>
                    )}
                    {(c.onboarding_step === 'registered' || c.onboarding_step === 'invited') && (
                      <button
                        disabled={markingOfflineId === c.client_id}
                        onClick={async () => {
                          if (!window.confirm(`Mark agreement for ${c.client_name} as sent and signed offline? This will move them to active and remove them from this list.`)) return;
                          setMarkingOfflineId(c.client_id);
                          try {
                            const { config } = await import('../config/environment');
                            const res = await fetch(
                              `${config.apiBaseUrl}/coaches/clients/${c.client_id}/mark-agreement-offline`,
                              { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('authToken') ?? ''}` } },
                            );
                            if (res.ok) {
                              setOnboardingPipeline(prev => prev.filter(x => x.client_id !== c.client_id));
                            } else {
                              const err = await res.json();
                              alert(err.detail || 'Failed to mark agreement.');
                            }
                          } catch { alert('Network error. Please try again.'); }
                          finally { setMarkingOfflineId(null); }
                        }}
                        className="px-3 py-1.5 border border-dark-border text-gray-300 hover:text-white text-xs font-medium rounded-lg whitespace-nowrap disabled:opacity-50"
                      >
                        {markingOfflineId === c.client_id ? 'Marking…' : 'Sent & Signed Offline'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
    );
  };

  const clientsFiltered = clients.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        c.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const clientsDetailSchedule = () => {
    if (!detailClient) return;
    const c = clients.find(x => x.client_id === detailClient.client_id) || {
      relationship_id: '', client_id: detailClient.client_id,
      client_name: detailClient.name, client_email: detailClient.email,
      start_date: detailClient.start_date || '', is_active: detailClient.status === 'active',
      name: detailClient.name, email: detailClient.email, status: detailClient.status,
    };
    setDetailClient(null);
    openSchedule(c);
  };

  const clientsDetailPayment = (appointmentId: string) => {
    if (!detailClient) return;
    const session = upcomingSessions.find(s => s.appointmentId === appointmentId) ?? {
      id: appointmentId, appointmentId,
      clientId: detailClient.client_id, clientName: detailClient.name,
      meetingName: detailClient.name, date: '', time: '', duration: 60,
      scheduledAt: '', type: 'virtual' as const, status: 'confirmed' as const,
    };
    setDetailClient(null);
    openPaymentPanel(session);
  };

  const clientsSectionJsx = (
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0 px-8 pt-8 pb-4">
          <h1 className="text-2xl font-bold text-white">Clients</h1>
          <div className="relative group">
            <button
              onClick={() => setInviteOpen(o => !o)}
              disabled={!onboardingStatus?.verify_contact_info || !onboardingStatus?.connect_calendar}
              className="bg-primary-600 hover:bg-primary-700 disabled:bg-dark-border disabled:text-dark-muted disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <PlusIcon className="w-4 h-4" />
              Invite Client
            </button>
            {(!onboardingStatus?.verify_contact_info || !onboardingStatus?.connect_calendar) && (
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-dark-card border border-dark-border text-xs text-dark-muted rounded-lg px-3 py-2 w-48 text-center hidden group-hover:block z-10 pointer-events-none">
                {!onboardingStatus?.verify_contact_info ? 'Verify your email first' : 'Connect your calendar first'}
              </div>
            )}
          </div>
        </div>

        <div className="px-8 flex-shrink-0">
          <InvitePanel
            open={inviteOpen}
            coachId={coachId}
            onClose={() => setInviteOpen(false)}
            onSuccess={fetchClients}
          />
        </div>

        {/* Split body */}
        <div className="flex flex-1 min-h-0">
          {/* ── Client list column ── */}
          <div className={`flex flex-col overflow-hidden ${
            detailClient
              ? 'w-72 flex-shrink-0 border-r border-dark-border'
              : 'flex-1 px-8 pb-8'
          }`}>
            {/* Search + filter */}
            <div className={`flex gap-3 flex-shrink-0 border-b border-dark-border bg-dark-card ${
              detailClient ? 'px-3 py-3' : 'rounded-t-xl p-4'
            }`}>
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted" />
                <input
                  type="text"
                  placeholder="Search…"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-dark-canvas text-gray-200 placeholder-dark-muted"
                />
              </div>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="px-2 py-2 text-sm border border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* List */}
            <div className={`flex-1 overflow-y-auto bg-dark-card ${
              detailClient ? '' : 'rounded-b-xl border border-t-0 border-dark-border shadow-sm'
            }`}>
              {clientsLoading ? (
                <Spinner size="md" className="py-12" />
              ) : clientsError ? (
                <div className="p-8 text-center">
                  <p className="text-sm text-red-500 mb-2">{clientsError}</p>
                  <button onClick={fetchClients} className="text-sm text-primary-600 hover:underline">Retry</button>
                </div>
              ) : clients.length === 0 ? (
                <EmptyState
                  icon={UserGroupIcon}
                  title="No clients yet"
                  description="Register your first client to get started."
                  action={{ label: 'Invite Client', onClick: () => setInviteOpen(true) }}
                />
              ) : clientsFiltered.length === 0 ? (
                <div className="p-12 text-center text-sm text-dark-muted">No clients match your filter.</div>
              ) : (
                <div className="divide-y divide-dark-border">
                  {clientsFiltered.map(client => {
                    const isSelected = detailClient?.client_id === client.client_id;
                    return (
                      <div
                        key={client.client_id}
                        className={`flex items-center justify-between px-4 py-3 hover:bg-dark-hover transition-colors ${
                          isSelected ? 'bg-primary-900/30 border-l-2 border-primary-500' : ''
                        }`}
                      >
                        <button
                          className="flex items-center gap-3 min-w-0 text-left flex-1"
                          onClick={() => {
                            if (config.features.clientDetailV2) {
                              navigate(`/dashboard/coach/clients/${client.client_id}?rel=${client.relationship_id}`);
                            } else {
                              setDetailClient(isSelected ? null : {
                                client_id: client.client_id,
                                relationship_id: client.relationship_id,
                                name: client.name,
                                email: client.email,
                                status: client.status,
                                start_date: client.start_date,
                                ai_consent: client.ai_consent ?? null,
                              });
                            }
                          }}
                        >
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-medium text-primary-700">{initials(client.name)}</span>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <p className={`text-sm font-medium truncate ${isSelected ? 'text-primary-300' : 'text-white'}`}>{client.name}</p>
                              {client.registration_source === 'external_booking' && (
                                <span className="flex-shrink-0 text-xs font-medium px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 whitespace-nowrap">Self-booked</span>
                              )}
                              {client.ai_consent !== true && (
                                <span className={`flex-shrink-0 w-2 h-2 rounded-full ${client.ai_consent === false ? 'bg-red-400' : 'bg-amber-400'}`} />
                              )}
                            </div>
                            {!detailClient && <p className="text-xs text-gray-400 truncate">{client.email}</p>}
                            {!detailClient && client.ai_consent === null && (
                              <p className="text-xs text-amber-400">Note-taking consent pending</p>
                            )}
                            {!detailClient && client.ai_consent === false && (
                              <p className="text-xs text-red-400">Note-taking declined</p>
                            )}
                          </div>
                        </button>
                        {!detailClient && (
                          <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                            {/* Send Agreement envelope — appears when a pending "Send agreement" task exists */}
                            {coachPendingTasksByRelId[client.relationship_id] && (
                              <Tooltip text="Send agreement">
                                <button
                                  onClick={async () => {
                                    const syntheticClient: import('../services/documentService').OnboardingClient = {
                                      coach_client_id: Number(client.relationship_id),
                                      client_id: client.client_id,
                                      client_name: client.name,
                                      client_email: client.email,
                                      onboarding_step: 'registered',
                                      created_at: null,
                                      packet_status: 'none',
                                      ai_consent: client.ai_consent ?? null,
                                    };
                                    setPipelineSendModal(syntheticClient);
                                    setPipelinePacketTitle('Welcome Packet');
                                    setPipelineSelectedDocIds(new Set());
                                    setPipelineSendError('');
                                    setPipelineSendSuccess('');
                                    try { setPipelineDocs(await listDocuments()); } catch { setPipelineDocs([]); }
                                    try { setPipelineCustomTags(await listTemplateTags()); } catch { setPipelineCustomTags([]); }
                                  }}
                                  className="p-1.5 rounded transition-all duration-150 text-amber-400 hover:text-white hover:bg-amber-600 hover:scale-110"
                                >
                                  <EnvelopeIcon className="w-4 h-4" />
                                </button>
                              </Tooltip>
                            )}
                            {client.is_active && (
                              <div className="relative group/sched">
                                <Tooltip text={client.ai_consent === null ? "AI consent pending" : "Schedule session"}>
                                <button
                                  onClick={() => { if (client.ai_consent !== null) openSchedule(client); }}
                                  disabled={client.ai_consent === null}
                                  className={`p-1.5 rounded transition-all duration-150 ${client.ai_consent === null ? 'text-gray-400 cursor-not-allowed opacity-50' : 'text-sky-400 hover:text-white hover:bg-sky-600 hover:scale-110'}`}
                                >
                                  <CalendarDaysIcon className="w-4 h-4" />
                                </button>
                                </Tooltip>
                              </div>
                            )}
                            <Tooltip text="View / edit notes">
                              <button
                                onClick={() => navigate(`/dashboard/coach/clients/${client.client_id}?rel=${client.relationship_id}&section=notes`)}
                                className="p-1.5 rounded transition-all duration-150 text-violet-400 hover:text-white hover:bg-violet-600 hover:scale-110"
                              >
                                <PencilSquareIcon className="w-4 h-4" />
                              </button>
                            </Tooltip>
                            <Tooltip text={client.is_active ? 'Deactivate client' : 'Activate client'}>
                              <button
                                onClick={() => setConfirmToggleClient(client)}
                                disabled={togglingClientId === client.client_id}
                                className={`p-1.5 rounded transition-all duration-150 ${
                                  client.is_active
                                    ? 'text-red-500 hover:text-white hover:bg-red-600 hover:scale-110'
                                    : 'text-green-500 hover:text-white hover:bg-green-600 hover:scale-110'
                                } disabled:opacity-50`}
                              >
                                {togglingClientId === client.client_id
                                  ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                  : client.is_active
                                    ? (
                                      <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                                        <path d="M3 3.5 C4.5 5 10.5 10.5 13 12.5" />
                                        <path d="M13 3.5 C11 5.5 5 10 3 12.5" />
                                      </svg>
                                    )
                                    : (
                                      <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M2.5 8.5 C4 10 5.5 12 6.5 12.5 C8 10 11.5 5 13.5 3" />
                                      </svg>
                                    )
                                }
                              </button>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Inline detail panel ── */}
          {detailClient && (
            <div className="flex-1 min-w-0 overflow-hidden">
              <ClientDetailPanel
                inline
                client={detailClient}
                coachId={coachId}
                onClose={() => setDetailClient(null)}
                onDeleted={() => { setDetailClient(null); fetchClients(); }}
                onSchedule={clientsDetailSchedule}
                apptPaymentStatuses={apptPaymentStatuses}
                onProcessPayment={clientsDetailPayment}
              />
            </div>
          )}
        </div>
      </div>
  );

  // ── Calendar event handlers (used by both Schedules calendar and Schedule Session modal) ──

  const handleEventSelect = (event: CalendarEvent) => {
    if (event.resource?.type === 'appointment' && event.resource?.appointmentId) {
      const found = upcomingSessions.find(s => s.appointmentId === event.resource!.appointmentId);
      if (found) {
        setSessionDetailSession(found);
        return;
      }
    }
    // Fall back to simple popover for gcal events or appointments not in the list
    setCalendarEventPopover(event);
  };

  const handleRightClickEvent = (event: CalendarEvent, x: number, y: number) => {
    setCalendarContextMenu({ event, x, y });
  };

  const handleRequestCancel = (session: Session) => {
    if (session.recurringSeriesId) {
      setCancelScopeSession(session);
    } else {
      setConfirmCancelId(session.appointmentId ?? null);
    }
  };

  const handleCancelAppointment = async (appointmentId: string) => {
    setConfirmCancelId(null);
    setCancellingAppointmentId(appointmentId);
    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}/coaches/sessions/${appointmentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setCalendarContextMenu(null);
        setCalendarEventPopover(null);
        fetchSessions();
        fetchGoogleCalendarEvents();
      }
    } finally {
      setCancellingAppointmentId(null);
    }
  };

  const handleCancelWithScope = async (session: Session, scope: 'this_only' | 'this_and_future' | 'all') => {
    setCancelScopeSession(null);
    const appointmentId = session.appointmentId;
    if (!appointmentId) return;

    if (scope === 'this_only') {
      await handleCancelAppointment(appointmentId);
      return;
    }

    setCancellingAppointmentId(appointmentId);
    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}/recurring-series/${session.recurringSeriesId}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointment_id: appointmentId, scope }),
      });
      if (res.ok) {
        setCalendarContextMenu(null);
        setCalendarEventPopover(null);
        fetchSessions();
      }
    } finally {
      setCancellingAppointmentId(null);
    }
  };

  const schedulesTodayDefault = new Date().toLocaleDateString('en-CA');
  const schedulesHasFilters = scheduleSearch || scheduleDateFrom !== schedulesTodayDefault || scheduleDateTo;
  const schedulesFiltered = {
    past:     applyScheduleFilters(pastSessions),
    today:    applyScheduleFilters(todaySessions),
    tomorrow: applyScheduleFilters(tomorrowSessions),
    thisWeek: applyScheduleFilters(thisWeekSessions),
    nextWeek: applyScheduleFilters(nextWeekSessions),
    later:    applyScheduleFilters(laterSessions),
  };
  const schedulesTotalFiltered = schedulesFiltered.past.length + schedulesFiltered.today.length + schedulesFiltered.tomorrow.length + schedulesFiltered.thisWeek.length + schedulesFiltered.nextWeek.length + schedulesFiltered.later.length;

  const handleSlotSelect = (slotInfo: SlotInfo) => {
    setCalendarSlotDate(slotInfo.start as Date);
    setClientPickerSearch('');
    setShowClientPickerForSlot(true);
  };

  const schedulesBackLink = (() => {
    const p = new URLSearchParams(window.location.search);
    const cid = p.get('clientId');
    const rel = p.get('rel');
    const name = p.get('client');
    if (!cid || !name) return null;
    const href = `/dashboard/coach/clients/${cid}${rel ? `?rel=${rel}` : ''}`;
    return { href, name };
  })();

  const schedulesSectionJsx = (
      <div className="flex flex-col h-full">
        {/* Back to client breadcrumb */}
        {schedulesBackLink && (
          <div className="px-8 pt-4 pb-0">
            <button
              onClick={() => navigate(schedulesBackLink.href)}
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back to {schedulesBackLink.name}
            </button>
          </div>
        )}
        {/* Toolbar */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-dark-border flex-shrink-0">
          <h1 className="text-2xl font-bold text-white">Schedules</h1>
          <div className="flex items-center gap-3">
            {/* View toggle */}
            <div className="flex items-center bg-dark-card border border-dark-border rounded-lg p-0.5">
              {(['calendar', 'list'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => setCalendarView(v)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                    calendarView === v
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {v === 'calendar' ? 'Calendar View' : 'List View'}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowLogPastSession(true)}
              className="flex items-center gap-2 px-3 py-2 bg-dark-surface border border-dark-border text-gray-300 rounded-lg text-sm font-medium hover:border-gray-500 hover:text-white transition-colors"
            >
              Log Past Session
            </button>
            <button
              onClick={() => { setCalendarSlotDate(null); setClientPickerSearch(''); setShowClientPickerForSlot(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Schedule Session
            </button>
            <button onClick={fetchSessions} className="p-2 text-gray-400 hover:text-gray-200 transition-colors">
              <ArrowPathIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Client picker modal — used by toolbar button and calendar slot click */}
        {showClientPickerForSlot && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowClientPickerForSlot(false)}>
            <div className="bg-dark-card border border-dark-border rounded-2xl p-6 w-80 shadow-xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-white font-semibold">Schedule Session</h3>
                <button onClick={() => setShowClientPickerForSlot(false)} className="text-gray-400 hover:text-white transition-colors">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              {calendarSlotDate && (
                <p className="text-xs text-gray-400 mb-3">
                  {calendarSlotDate.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </p>
              )}
              <div className="relative mb-4">
                <input
                  autoFocus
                  type="text"
                  value={clientPickerSearch}
                  onChange={e => setClientPickerSearch(e.target.value)}
                  placeholder="Search clients…"
                  className="w-full px-3 py-2 text-sm bg-dark-canvas border border-dark-border rounded-lg text-gray-200 placeholder-dark-muted focus:outline-none focus:border-primary-500"
                />
                <div className="absolute left-0 right-0 top-full mt-1 bg-dark-card border border-dark-border rounded-lg shadow-xl z-10 max-h-48 overflow-y-auto">
                  {(() => {
                    const filtered = clients.filter(c =>
                      c.is_active &&
                      c.ai_consent !== null &&
                      (!clientPickerSearch || c.client_name.toLowerCase().includes(clientPickerSearch.toLowerCase()))
                    );
                    if (filtered.length === 0) return <p className="text-sm text-dark-muted px-3 py-2">No clients found</p>;
                    return filtered.map(c => (
                      <button
                        key={c.client_id}
                        onClick={() => {
                          setShowClientPickerForSlot(false);
                          openSchedule(c);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-dark-hover transition-colors first:rounded-t-lg last:rounded-b-lg"
                      >
                        {c.client_name}
                      </button>
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {calendarView === 'calendar' ? (
          /* Calendar view */
          <div className="flex-1 p-6 overflow-hidden min-h-0">

            <div style={{ height: 'calc(100vh - 160px)' }} className="bg-dark-card rounded-xl border border-dark-border overflow-hidden p-4">
              <CoachCalendarView
                events={calendarEvents}
                onSelectSlot={handleSlotSelect}
                onSelectEvent={handleEventSelect}
                onRightClickEvent={handleRightClickEvent}
                onDeleteEvent={ev => {
                  if (!ev.resource?.appointmentId) return;
                  const s = upcomingSessions.find(x => x.appointmentId === ev.resource!.appointmentId);
                  if (s) handleRequestCancel(s);
                }}
              />
            </div>
          </div>
        ) : (
          /* List view — existing content */
          <div className="p-8">
            {/* Search & filter bar */}
            <div className="bg-dark-card rounded-xl border border-dark-border shadow-sm mb-4">
              <div className="p-4 flex flex-wrap gap-3 items-end">
                <div className="relative flex-1 min-w-[180px]">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted" />
                  <input
                    type="text"
                    placeholder="Search by client name…"
                    value={scheduleSearch}
                    onChange={e => setScheduleSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-dark-canvas text-gray-200 placeholder-dark-muted"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-400 whitespace-nowrap">From</label>
                  <input type="date" value={scheduleDateFrom} onChange={e => setScheduleDateFrom(e.target.value)} className="text-sm border border-dark-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200 [color-scheme:dark]" />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-400 whitespace-nowrap">To</label>
                  <input type="date" value={scheduleDateTo} onChange={e => setScheduleDateTo(e.target.value)} className="text-sm border border-dark-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200 [color-scheme:dark]" />
                </div>
                {schedulesHasFilters && (
                  <button onClick={() => { setScheduleSearch(''); setScheduleDateFrom(new Date().toLocaleDateString('en-CA')); setScheduleDateTo(''); }} className="text-sm text-gray-400 hover:text-gray-200 underline whitespace-nowrap">Clear</button>
                )}
              </div>
            </div>
            <div className="bg-dark-card rounded-xl border border-dark-border shadow-sm">
              <div className="p-5 space-y-6">
                {sessionsLoading ? (
                  <Spinner size="md" className="py-10" />
                ) : schedulesHasFilters && schedulesTotalFiltered === 0 ? (
                  <EmptyState icon={CalendarIcon} title="No sessions match your search" />
                ) : upcomingSessions.length === 0 ? (
                  <EmptyState
                    icon={CalendarIcon}
                    title="No sessions found"
                    action={{ label: 'Schedule a Session', onClick: () => setActiveSection('clients') }}
                  />
                ) : (
                  <>
                    {schedulesFiltered.past.length > 0 && (
                      <SessionGroup
                        label="Past" sessions={schedulesFiltered.past} showDate groupByClient
                        collapsible open={pastOpen} onToggle={() => setPastOpen(o => !o)}
                        expandedClients={groupExpandedClients.get('Past')} onToggleClient={n => toggleGroupClient('Past', n)}
                      />
                    )}
                    <SessionGroup label="Today"     sessions={schedulesFiltered.today}    />
                    <SessionGroup label="Tomorrow"  sessions={schedulesFiltered.tomorrow} showDate />
                    <SessionGroup
                      label="This Week" sessions={schedulesFiltered.thisWeek} showDate groupByClient
                      collapsible open={thisWeekOpen} onToggle={() => setThisWeekOpen(o => !o)}
                      expandedClients={groupExpandedClients.get('This Week')} onToggleClient={n => toggleGroupClient('This Week', n)}
                    />
                    <SessionGroup
                      label="Next Week" sessions={schedulesFiltered.nextWeek} showDate groupByClient
                      collapsible open={nextWeekOpen} onToggle={() => setNextWeekOpen(o => !o)}
                      expandedClients={groupExpandedClients.get('Next Week')} onToggleClient={n => toggleGroupClient('Next Week', n)}
                    />
                    <SessionGroup
                      label="Upcoming" sessions={schedulesFiltered.later} showDate groupByClient
                      collapsible open={upcomingOpen} onToggle={() => setUpcomingOpen(o => !o)}
                      expandedClients={groupExpandedClients.get('Upcoming')} onToggleClient={n => toggleGroupClient('Upcoming', n)}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
  );

  const handleGoogleConnect = async () => {
    setGoogleConnecting(true);
    setGoogleConnectError('');
    try {
      const url = await getAuthUrl();
      window.location.href = url;
    } catch (err: any) {
      setGoogleConnectError(err?.message || 'Failed to open Google consent screen');
    } finally {
      setGoogleConnecting(false);
    }
  };

  const handleGoogleDisconnect = async () => {
    try {
      await disconnect();
      setGoogleStatus(s => s ? { ...s, connected: false, email: null } : s);
      fetchOnboardingStatus();
    } catch { /* ignore */ }
  };

  const handleZoomConnect = async () => {
    setZoomConnecting(true);
    setZoomConnectError('');
    try {
      const token = localStorage.getItem('authToken') || '';
      const resp = await fetch(`${config.apiBaseUrl}/zoom/auth-url`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to get Zoom auth URL');
      }
      const { auth_url } = await resp.json();
      window.location.href = auth_url;
    } catch (err: any) {
      setZoomConnectError(err?.message || 'Failed to connect Zoom');
    } finally {
      setZoomConnecting(false);
    }
  };

  const [showZoomDisconnectConfirm, setShowZoomDisconnectConfirm] = useState(false);

  const handleZoomDisconnect = async () => {
    setShowZoomDisconnectConfirm(false);
    try {
      const token = localStorage.getItem('authToken') || '';
      await fetch(`${config.apiBaseUrl}/zoom/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setZoomStatus(s => s ? { ...s, connected: false, zoom_email: null } : s);
    } catch { /* ignore */ }
  };

  const handleCalendlyConnect = async () => {
    setCalendlyConnecting(true);
    setCalendlyConnectError('');
    try {
      const token = localStorage.getItem('authToken') || '';
      const resp = await fetch(`${config.apiBaseUrl}/calendly/auth-url`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to get Calendly auth URL');
      }
      const { auth_url } = await resp.json();
      window.location.href = auth_url;
    } catch (err: any) {
      setCalendlyConnectError(err?.message || 'Failed to connect Calendly');
    } finally {
      setCalendlyConnecting(false);
    }
  };

  const handleCalendlyDisconnect = async () => {
    try {
      const token = localStorage.getItem('authToken') || '';
      await fetch(`${config.apiBaseUrl}/calendly/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setCalendlyStatus(s => s ? { ...s, connected: false, email: null, scheduling_type: 'none' } : s);
      setCalendlyEventTypes([]);
      setCalendlySelectedUris([]);
    } catch { /* ignore */ }
  };

  const handleCalendlyLoadEventTypes = async () => {
    setCalendlyEventTypesLoading(true);
    try {
      const token = localStorage.getItem('authToken') || '';
      const resp = await fetch(`${config.apiBaseUrl}/calendly/event-types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setCalendlyEventTypes(data.event_types || []);
      }
    } catch { /* ignore */ }
    finally { setCalendlyEventTypesLoading(false); }
  };

  const handleCalendlySaveSettings = async () => {
    if (calendlySelectedUris.length === 0) return;
    setCalendlySaving(true);
    setCalendlySaveMsg('');
    try {
      const token = localStorage.getItem('authToken') || '';
      const resp = await fetch(`${config.apiBaseUrl}/calendly/settings`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type_uris: calendlySelectedUris }),
      });
      if (resp.ok) {
        setCalendlySaveMsg('Calendly scheduling activated!');
        const tok = localStorage.getItem('authToken') || '';
        fetch(`${config.apiBaseUrl}/calendly/status`, { headers: { Authorization: `Bearer ${tok}` } })
          .then(r => r.ok ? r.json() : null).then(d => { if (d) setCalendlyStatus(d); }).catch(() => {});
        setTimeout(() => setCalendlySaveMsg(''), 4000);
      }
    } catch { /* ignore */ }
    finally { setCalendlySaving(false); }
  };

  const handleGoogleEtCreate = async () => {
    if (!googleEtForm) return;
    const dur = parseInt(googleEtForm.duration_minutes, 10);
    if (!googleEtForm.name.trim() || isNaN(dur) || dur < 5) {
      setGoogleEtError('Name and duration (≥ 5 min) are required.');
      return;
    }
    setGoogleEtSaving(true);
    setGoogleEtError('');
    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}/event-types`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: googleEtForm.name.trim(),
          duration_minutes: dur,
          description: googleEtForm.description.trim() || null,
          billing_type: googleEtForm.billing_type || 'billable',
          default_price_cents: googleEtForm.billing_type === 'billable' && googleEtForm.default_price ? Math.round(parseFloat(googleEtForm.default_price) * 100) : null,
          default_currency: googleEtForm.billing_type === 'billable' && googleEtForm.default_price ? (googleEtForm.default_currency || null) : null,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      const created = await res.json();
      setGoogleEtList(prev => [...prev, { id: created.id, name: created.name, duration_minutes: created.duration_minutes, description: created.description, billing_type: created.billing_type ?? 'billable', is_active: created.is_active, allow_external_booking: created.allow_external_booking ?? false, default_price_cents: created.default_price_cents ?? null, default_currency: created.default_currency ?? null }]);
      setGoogleEtForm(null);
    } catch (e: any) {
      setGoogleEtError(e.message || 'Failed to create event type.');
    } finally {
      setGoogleEtSaving(false);
    }
  };

  const handleGoogleEtDelete = async (etId: string) => {
    const token = localStorage.getItem('authToken') || '';
    await fetch(`${config.apiBaseUrl}/event-types/${etId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setGoogleEtList(prev => prev.filter(et => et.id !== etId));
  };

  const handleGoogleEtUpdate = async (etId: string) => {
    if (!googleEtEditForm.name.trim()) { setGoogleEtEditError('Name is required.'); return; }
    setGoogleEtEditSaving(true);
    setGoogleEtEditError('');
    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}/event-types/${etId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: googleEtEditForm.name.trim(),
          description: googleEtEditForm.description.trim() || null,
          billing_type: googleEtEditForm.billing_type,
          default_price_cents: googleEtEditForm.billing_type === 'billable' && googleEtEditForm.default_price ? Math.round(parseFloat(googleEtEditForm.default_price) * 100) : null,
          default_currency: googleEtEditForm.billing_type === 'billable' && googleEtEditForm.default_currency ? googleEtEditForm.default_currency : null,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      setGoogleEtList(prev => prev.map(et => et.id === etId ? { ...et, name: googleEtEditForm.name.trim(), description: googleEtEditForm.description.trim() || null, billing_type: googleEtEditForm.billing_type, default_price_cents: googleEtEditForm.billing_type === 'billable' && googleEtEditForm.default_price ? Math.round(parseFloat(googleEtEditForm.default_price) * 100) : null, default_currency: googleEtEditForm.billing_type === 'billable' && googleEtEditForm.default_currency ? googleEtEditForm.default_currency : null } : et));
      setGoogleEtEditId(null);
    } catch (e: any) {
      setGoogleEtEditError(e.message || 'Failed to update.');
    } finally {
      setGoogleEtEditSaving(false);
    }
  };

  const handleGoogleEtToggleDirect = async (etId: string, currentValue: boolean) => {
    const token = localStorage.getItem('authToken') || '';
    const newValue = !currentValue;
    setGoogleEtExternalSaving(prev => ({ ...prev, [etId]: true }));
    try {
      const res = await fetch(`${config.apiBaseUrl}/event-types/${etId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ allow_external_booking: newValue }),
      });
      if (res.ok) {
        setGoogleEtList(prev => prev.map(et => et.id === etId ? { ...et, allow_external_booking: newValue } : et));
      }
    } finally {
      setGoogleEtExternalSaving(prev => { const { [etId]: _, ...rest } = prev; return rest; });
    }
  };

  const handleGoogleEtToggleExternal = (etId: string, savedValue: boolean) => {
    // Toggle local pending state — not saved until the checkmark is clicked
    setGoogleEtPendingExternal(prev => {
      const hasPending = etId in prev;
      if (hasPending) {
        // If pending equals saved value, no change — remove pending
        const { [etId]: _, ...rest } = prev;
        return prev[etId] === savedValue ? rest : { ...rest, [etId]: !prev[etId] };
      }
      return { ...prev, [etId]: !savedValue };
    });
  };

  const handleGoogleEtSaveExternal = async (etId: string) => {
    const newValue = googleEtPendingExternal[etId];
    if (newValue === undefined) return;
    setGoogleEtExternalSaving(prev => ({ ...prev, [etId]: true }));
    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}/event-types/${etId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ allow_external_booking: newValue }),
      });
      if (res.ok) {
        setGoogleEtList(prev => prev.map(et => et.id === etId ? { ...et, allow_external_booking: newValue } : et));
        setGoogleEtPendingExternal(prev => { const { [etId]: _, ...rest } = prev; return rest; });
      }
    } finally {
      setGoogleEtExternalSaving(prev => { const { [etId]: _, ...rest } = prev; return rest; });
    }
  };

  const handleEnforcePaymentSessionsToggle = async () => {
    const newVal = !enforcePaymentSessions;
    setEnforcePaymentSessionsSaving(true);
    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}/coaches/${coachId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enforce_payment_sessions: newVal }),
      });
      if (res.ok) setEnforcePaymentSessions(newVal);
    } finally {
      setEnforcePaymentSessionsSaving(false);
    }
  };

  const handleSchedulingConfigSave = async () => {
    setSchedulingConfigSaving(true);
    setSchedulingConfigMsg('');
    try {
      const token = localStorage.getItem('authToken') || '';
      const [configRes, scheduleRes] = await Promise.all([
        fetch(`${config.apiBaseUrl}/event-types/scheduling-config/me`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ slot_increment_minutes: slotIncrement, meeting_buffer_minutes: meetingBuffer, min_booking_notice_days: minBookingNoticeDays, timezone: coachTimezone }),
        }),
        fetch(`${config.apiBaseUrl}/coaches/me/working-hours`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(weeklySchedule.map(d => ({
            day_of_week: d.day_of_week,
            blocks: d.blocks.map(b => ({ start_time: b.start_time, end_time: b.end_time })),
          }))),
        }),
      ]);
      if (configRes.ok && scheduleRes.ok) {
        const updated = await scheduleRes.json() as DaySchedule[];
        setWeeklySchedule(updated);
        setSchedulingConfigMsg('Saved!');
        setTimeout(() => setSchedulingConfigMsg(''), 3000);
      }
    } catch { /* ignore */ }
    finally { setSchedulingConfigSaving(false); }
  };

  const REMINDER_OPTIONS: { label: string; value: number }[] = [
    { label: '24 hours before', value: 1440 },
    { label: '12 hours before', value: 720 },
    { label: '2 hours before', value: 120 },
    { label: '1 hour before', value: 60 },
    { label: '15 minutes before', value: 15 },
    { label: 'At appointment time', value: 0 },
  ];

  const handleReminderToggle = (value: number) => {
    setReminderSlots(prev => {
      if (prev.includes(value)) return prev.filter(v => v !== value);
      if (prev.length >= 3) return prev;
      return [...prev, value];
    });
  };

  const handleReminderSave = async () => {
    setReminderSaving(true);
    setReminderMsg('');
    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`${config.apiBaseUrl}/event-types/reminder-settings/me`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reminder_minutes_before: reminderSlots }),
      });
      if (res.ok) {
        setReminderMsg('Saved!');
        setTimeout(() => setReminderMsg(''), 3000);
      }
    } catch { /* ignore */ }
    finally { setReminderSaving(false); }
  };

  const ChangePasswordCard = () => {
    const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
    const [pwShowPw, setPwShowPw] = useState(false);
    const [pwError, setPwError] = useState('');
    const [pwSuccess, setPwSuccess] = useState('');
    const [pwLoading, setPwLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setPwError('');
      setPwSuccess('');
      if (pwForm.newPw !== pwForm.confirm) { setPwError('New passwords do not match'); return; }
      if (pwForm.newPw.length < 12) { setPwError('Password must be at least 12 characters'); return; }
      if (!/[A-Z]/.test(pwForm.newPw)) { setPwError('Password must contain at least one uppercase letter'); return; }
      if (!/[a-z]/.test(pwForm.newPw)) { setPwError('Password must contain at least one lowercase letter'); return; }
      if (!/[0-9]/.test(pwForm.newPw)) { setPwError('Password must contain at least one number'); return; }
      if (!/[^A-Za-z0-9]/.test(pwForm.newPw)) { setPwError('Password must contain at least one special character'); return; }
      setPwLoading(true);
      try {
        const token = localStorage.getItem('authToken') || '';
        const res = await fetch(`${config.apiBaseUrl}/auth/change-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ current_password: pwForm.current, new_password: pwForm.newPw }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed to change password');
        setPwSuccess('Password changed successfully');
        setPwForm({ current: '', newPw: '', confirm: '' });
      } catch (err) {
        setPwError(err instanceof Error ? err.message : 'Failed to change password');
      } finally {
        setPwLoading(false);
      }
    };

    return (
      <div className="bg-dark-card rounded-xl border border-dark-border shadow-sm">
        <div className="px-5 py-4 border-b border-dark-border">
          <h2 className="font-semibold text-white">Change Password</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {pwSuccess && <p className="text-xs text-green-400">{pwSuccess}</p>}
          {pwError && <p className="text-xs text-red-400">{pwError}</p>}
          {[
            { key: 'current', label: 'Current Password' },
            { key: 'newPw',   label: 'New Password' },
            { key: 'confirm', label: 'Confirm New Password' },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-200 mb-1">{label}</label>
              <div className="relative">
                <input
                  type={pwShowPw ? 'text' : 'password'}
                  value={(pwForm as any)[key]}
                  onChange={e => setPwForm(p => ({ ...p, [key]: e.target.value }))}
                  className="w-full text-sm border border-dark-border rounded-lg p-2.5 pr-10 focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200"
                />
                <button type="button" onClick={() => setPwShowPw(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                  {pwShowPw ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
          <button
            type="submit"
            disabled={pwLoading}
            className="text-sm bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {pwLoading ? 'Saving…' : 'Update Password'}
          </button>
        </form>
      </div>
    );
  };

  const PrivacyDataSection = ({ subNav }: { subNav: React.ReactNode }) => {
    const [downloading, setDownloading] = React.useState(false);
    const [downloadError, setDownloadError] = React.useState('');
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [deletePassword, setDeletePassword] = React.useState('');
    const [deleteError, setDeleteError] = React.useState('');
    const [deleting, setDeleting] = React.useState(false);
    const token = localStorage.getItem('authToken') || '';

    const handleDownload = async () => {
      setDownloading(true);
      setDownloadError('');
      try {
        const res = await fetch(`${config.apiBaseUrl}/gdpr/export`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Export failed');
        const blob = await res.blob();
        const disposition = res.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="([^"]+)"/);
        const filename = match ? match[1] : 'coachsight-export.json';
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        setDownloadError('Download failed. Please try again.');
      } finally {
        setDownloading(false);
      }
    };

    const handleDeleteAccount = async () => {
      setDeleting(true);
      setDeleteError('');
      try {
        const res = await fetch(`${config.apiBaseUrl}/coaches/me`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ current_password: deletePassword }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || 'Deletion failed. Please try again.');
        }
        localStorage.clear();
        navigate('/login', { state: { accountDeleted: true } });
      } catch (err: any) {
        setDeleteError(err.message || 'Something went wrong. Please try again.');
      } finally {
        setDeleting(false);
      }
    };

    return (
      <div className="p-8 max-w-xl space-y-6">
        {subNav}
        <div>
          <h1 className="text-2xl font-bold text-white">Privacy & Data</h1>
          <p className="text-sm text-gray-400 mt-1">Download a copy of all personal data CoachSight ECR holds about you.</p>
        </div>
        <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-3">
          <div className="flex items-start gap-3">
            <ShieldCheckIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-white">Download My Data</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Exports your profile, clients, appointments, session notes, and AI settings as a JSON file.
                Payment account details are not included.
              </p>
            </div>
          </div>
          {downloadError && <p className="text-xs text-red-400">{downloadError}</p>}
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="text-xs bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {downloading ? 'Preparing download…' : 'Download My Data'}
          </button>
        </div>

        {/* Danger zone */}
        <div className="bg-dark-card border border-red-800/50 rounded-xl p-5 space-y-3">
          <div className="flex items-start gap-3">
            <TrashIcon className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-300">Delete My Account</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Permanently deletes your coach account, all session notes, action items, client relationships, and settings.
                Your clients' accounts are not affected.
              </p>
            </div>
          </div>
          <button
            onClick={() => { setShowDeleteModal(true); setDeletePassword(''); setDeleteError(''); }}
            className="text-xs bg-red-700 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Delete My Account
          </button>
        </div>

        {/* Delete confirmation modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-dark-card border border-dark-border rounded-2xl p-6 w-full max-w-md space-y-5">
              <div className="flex items-start justify-between">
                <h2 className="text-lg font-bold text-white">Delete your account?</h2>
                <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-white">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-400">This action is permanent and cannot be undone. The following will be deleted:</p>
              <ul className="text-xs text-gray-400 list-disc list-inside space-y-1">
                <li>Your coach profile and login credentials</li>
                <li>All session notes and AI summaries</li>
                <li>All action items and nudge schedules</li>
                <li>All session recordings and payment records</li>
                <li>Your client relationships (client accounts are not deleted)</li>
              </ul>
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Enter your current password to confirm</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)}
                  className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Current password"
                  autoComplete="current-password"
                />
              </div>
              {deleteError && <p className="text-xs text-red-400">{deleteError}</p>}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 text-sm border border-dark-border text-gray-300 rounded-lg hover:bg-dark-bg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting || !deletePassword}
                  className="flex-1 px-4 py-2 text-sm bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                >
                  {deleting ? 'Deleting…' : 'Yes, delete my account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const DocumentTypesSettings = ({ subNav }: { subNav: React.ReactNode }) => {
    const [types, setTypes] = React.useState<{ id: string; name: string }[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [newName, setNewName] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const token = localStorage.getItem('authToken') || '';
    const headers = { Authorization: `Bearer ${token}` };

    React.useEffect(() => {
      fetch(`${config.apiBaseUrl}/documents/types`, { headers })
        .then(r => r.ok ? r.json() : [])
        .then(setTypes)
        .catch(() => setTypes([]))
        .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAdd = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newName.trim()) return;
      setSaving(true);
      setError('');
      try {
        const res = await fetch(`${config.apiBaseUrl}/documents/types`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newName.trim() }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed to create');
        setTypes(prev => [...prev, data]);
        setNewName('');
      } catch (e: any) {
        setError(e.message || 'Failed to create type');
      } finally {
        setSaving(false);
      }
    };

    const handleDelete = async (id: string, name: string) => {
      if (!window.confirm(`Delete document type "${name}"?`)) return;
      try {
        const res = await fetch(`${config.apiBaseUrl}/documents/types/${id}`, {
          method: 'DELETE', headers,
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          alert(d.detail || 'Cannot delete — type may be in use.');
          return;
        }
        setTypes(prev => prev.filter(t => t.id !== id));
      } catch { alert('Delete failed'); }
    };

    return (
      <div className="p-8 max-w-xl space-y-6">
        {subNav}
        <h1 className="text-2xl font-bold text-white">Document Types</h1>
        <p className="text-sm text-gray-400">
          Custom categories used to tag documents in your library (e.g. "Intake Form", "Contract", "Worksheet").
        </p>
        {loading ? (
          <div className="animate-pulse h-8 bg-dark-card rounded-lg" />
        ) : (
          <div className="space-y-2">
            {types.map(t => (
              <div key={t.id} className="flex items-center justify-between bg-dark-card border border-dark-border rounded-xl px-4 py-2.5">
                <span className="text-sm text-white">{t.name}</span>
                <button
                  onClick={() => handleDelete(t.id, t.name)}
                  className="text-xs text-gray-500 hover:text-red-400 transition-colors"
                >
                  Delete
                </button>
              </div>
            ))}
            {types.length === 0 && <p className="text-sm text-gray-500">No document types yet.</p>}
          </div>
        )}
        <form onSubmit={handleAdd} className="flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="New type name…"
            className="flex-1 border border-dark-border rounded-lg px-3 py-2 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-1 focus:ring-primary-500 focus:outline-none"
          />
          <button
            type="submit"
            disabled={saving || !newName.trim()}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
        </form>
        {error && <p className="text-xs text-red-400">{error}</p>}
      </div>
    );
  };

  // ── Coach Notification Preferences ────────────────────────────────────────────
  const CoachNotificationPreferences = ({ subNav }: { subNav: React.ReactNode }) => {
    type NotifPref = { event_type: string; channel: string; email_override: string | null };
    const COACH_EVENTS: NotifEventDef[] = [
      { key: 'session_reminder',    label: 'Session Reminders',   desc: 'Reminders before each upcoming coaching session' },
      { key: 'form_submitted',      label: 'Form submitted',      desc: 'A client submits a form or questionnaire you assigned' },
      { key: 'agreement_signed',    label: 'Agreement signed',    desc: 'A client signs a document packet' },
      { key: 'subscription_invoice',label: 'CoachSight invoice',  desc: 'Your monthly CoachSight subscription invoice is generated' },
      { key: 'payment_received',    label: 'Payment received',    desc: 'A client completes a payment for a session or package' },
    ];

    const [prefs, setPrefs] = React.useState<NotifPref[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState('');
    const [saveSuccess, setSaveSuccess] = React.useState(false);
    const [coachPhone, setCoachPhone] = React.useState<string | null>(null);
    const [phoneVerified, setPhoneVerified] = React.useState(false);
    const token = localStorage.getItem('authToken') || '';

    React.useEffect(() => {
      setLoading(true);
      Promise.all([
        fetch(`${config.apiBaseUrl}/coaches/me/notification-preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.ok ? r.json() : []),
        fetch(`${config.apiBaseUrl}/coaches/${coachId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.ok ? r.json() : null),
      ]).then(([prefData, profile]) => {
        setPrefs(Array.isArray(prefData) ? prefData : []);
        if (profile) {
          setCoachPhone(profile.phone || null);
          setPhoneVerified(!!profile.is_phone_verified);
        }
      }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getPref = (key: string): NotifPref =>
      prefs.find(p => p.event_type === key) ?? { event_type: key, channel: 'email', email_override: null };

    const updatePref = (key: string, field: 'channel' | 'email_override', value: string) => {
      setPrefs(prev => {
        const existing = prev.find(p => p.event_type === key);
        if (existing) {
          return prev.map(p => p.event_type === key ? { ...p, [field]: value || null } : p);
        }
        return [...prev, { event_type: key, channel: 'email', email_override: null, [field]: value || null }];
      });
    };

    const smsIneligible = !phoneVerified || !coachPhone || !coachPhone.startsWith('+1');

    const handleSave = async () => {
      setSaving(true);
      setSaveError('');
      setSaveSuccess(false);
      try {
        const payload = COACH_EVENTS.map(ev => {
          const p = getPref(ev.key);
          return { event_type: ev.key, channel: p.channel, email_override: p.email_override || null };
        });
        const res = await fetch(`${config.apiBaseUrl}/coaches/me/notification-preferences`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setSaveError(err.detail || 'Save failed. Please try again.');
          return;
        }
        const updated = await res.json();
        setPrefs(updated);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } finally {
        setSaving(false);
      }
    };

    if (loading) return (
      <div className="p-8 max-w-2xl">
        {subNav}
        <p className="text-sm text-gray-400">Loading...</p>
      </div>
    );

    return (
      <div className="p-8 max-w-2xl space-y-6">
        {subNav}
        <h1 className="text-2xl font-bold text-white">Notification Preferences</h1>
        <p className="text-sm text-gray-400">Choose how you receive each type of notification. Leave the email address field blank to use your account email.</p>

        <NotificationPrefsPanel
          events={COACH_EVENTS}
          prefs={prefs}
          onChange={updatePref}
          smsIneligible={smsIneligible}
          onSave={handleSave}
          saving={saving}
          saved={saveSuccess}
          error={saveError}
          showSmsDisclosure
          onSmsSettingsClick={() => setSettingsSubSection('profile')}
        />
      </div>
    );
  };

  const SettingsSection = () => {
    const subNav = settingsSubSection && (
      <div className="flex items-center gap-1.5 text-sm mb-6">
        <button
          onClick={() => setSettingsSubSection(null)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          Settings
        </button>
        <ChevronRightIcon className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
        <span className="text-white font-medium">
          {settingsSubSection === 'scheduling' ? 'Scheduling'
            : settingsSubSection === 'payments' ? 'Payments'
            : settingsSubSection === 'profile' ? 'My Profile'
            : settingsSubSection === 'documents' ? 'Document Types'
            : settingsSubSection === 'privacy' ? 'Privacy & Data'
            : settingsSubSection === 'notifications' ? 'Notification Preferences'
            : settingsSubSection === 'subscription' ? 'My Subscription'
            : 'Notes Generation'}
        </span>
      </div>
    );

    // ── My Profile ─────────────────────────────────────────────────────────────
    if (settingsSubSection === 'profile') return (
      <ProfileSubSection
          subNav={subNav}
          coachId={coachId}
          companyLogoUrl={companyLogoUrl}
          setCompanyLogoUrl={setCompanyLogoUrl}
          brandColor={brandColor}
          setBrandColor={setBrandColor}
        />
    );

    // ── Notes Generation ───────────────────────────────────────────────────────────────
    if (settingsSubSection === 'ai-notes') return (
      <div className="p-8 max-w-4xl space-y-4">
        {subNav}
        <h1 className="text-2xl font-bold text-white">Notes Generation</h1>
        <CoachPromptSettingsPanel />
      </div>
    );

    // ── Scheduling ─────────────────────────────────────────────────────────────
    if (settingsSubSection === 'scheduling') {
      const googleActive = !!googleStatus?.connected;
      const calendlyActive = !!calendlyStatus?.connected;
      const anyActive = googleActive || calendlyActive;
      return (
        <div className="p-8 max-w-xl space-y-6">
          {subNav}
          <div>
            <h1 className="text-2xl font-bold text-white">Scheduling</h1>
            <p className="text-sm text-gray-400 mt-1">Set your availability first, then connect a calendar provider.</p>
          </div>

          {/* Availability */}
          <div className="bg-dark-card rounded-xl border border-primary-700/60 shadow-sm">
            <button
              className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-dark-hover/40 rounded-t-xl transition-colors"
              onClick={() => setAvailabilityCardExpanded(e => !e)}
            >
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-medium bg-primary-600/20 text-primary-400 px-1.5 py-0.5 rounded">Step 1</span>
                  <h2 className="font-semibold text-white text-sm">Availability</h2>
                </div>
                <p className="text-xs text-gray-400">Set your working hours and timezone before connecting a calendar.</p>
              </div>
              <ChevronRightIcon className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${availabilityCardExpanded ? 'rotate-90' : ''}`} />
            </button>
            {availabilityCardExpanded && (
              <div className="border-t border-dark-border p-5 space-y-5">
                {calendlyActive ? (
                  <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2.5">
                    <span className="text-amber-400 text-base leading-none mt-0.5">⚠</span>
                    <p className="text-xs text-amber-300">You&apos;re using Calendly — manage your availability directly in Calendly. These settings only apply when using Google Calendar.</p>
                  </div>
                ) : googleActive ? (
                  <div className="flex items-start gap-2 bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2.5">
                    <span className="text-green-400 text-base leading-none mt-0.5">✓</span>
                    <p className="text-xs text-green-300">These settings are applied to your Google Calendar scheduling.</p>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 bg-dark-hover border border-dark-border rounded-lg px-3 py-2.5">
                    <span className="text-gray-400 text-base leading-none mt-0.5">ℹ</span>
                    <p className="text-xs text-gray-400">These settings apply when using Google Calendar. If you use Calendly, manage availability there instead.</p>
                  </div>
                )}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Timezone</label>
                  <select
                    value={coachTimezone}
                    onChange={e => setCoachTimezone(e.target.value)}
                    className="w-full bg-dark-canvas border border-dark-border text-gray-200 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-primary-500"
                  >
                    {/* Show detected timezone if it's not in the predefined list */}
                    {coachTimezone && ![
                      'America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
                      'America/Anchorage','Pacific/Honolulu','America/Toronto','America/Vancouver',
                      'Europe/London','Europe/Paris','Europe/Berlin','Europe/Amsterdam',
                      'Asia/Kolkata','Asia/Singapore','Asia/Tokyo',
                      'Australia/Sydney','Australia/Melbourne','Australia/Brisbane',
                      'Australia/Adelaide','Australia/Darwin','Australia/Perth','Australia/Hobart',
                      'Africa/Johannesburg','UTC',
                    ].includes(coachTimezone) && (
                      <optgroup label="Detected">
                        <option value={coachTimezone}>{coachTimezone}</option>
                      </optgroup>
                    )}
                    <optgroup label="United States">
                      <option value="America/New_York">America/New_York</option>
                      <option value="America/Chicago">America/Chicago</option>
                      <option value="America/Denver">America/Denver</option>
                      <option value="America/Los_Angeles">America/Los_Angeles</option>
                      <option value="America/Anchorage">America/Anchorage</option>
                      <option value="Pacific/Honolulu">Pacific/Honolulu</option>
                    </optgroup>
                    <optgroup label="Canada">
                      <option value="America/Toronto">America/Toronto</option>
                      <option value="America/Vancouver">America/Vancouver</option>
                    </optgroup>
                    <optgroup label="Europe">
                      <option value="Europe/London">Europe/London</option>
                      <option value="Europe/Paris">Europe/Paris</option>
                      <option value="Europe/Berlin">Europe/Berlin</option>
                      <option value="Europe/Amsterdam">Europe/Amsterdam</option>
                    </optgroup>
                    <optgroup label="Asia">
                      <option value="Asia/Kolkata">Asia/Kolkata</option>
                      <option value="Asia/Singapore">Asia/Singapore</option>
                      <option value="Asia/Tokyo">Asia/Tokyo</option>
                    </optgroup>
                    <optgroup label="Australia">
                      <option value="Australia/Sydney">Australia/Sydney</option>
                      <option value="Australia/Melbourne">Australia/Melbourne</option>
                      <option value="Australia/Brisbane">Australia/Brisbane</option>
                      <option value="Australia/Adelaide">Australia/Adelaide</option>
                      <option value="Australia/Darwin">Australia/Darwin</option>
                      <option value="Australia/Perth">Australia/Perth</option>
                      <option value="Australia/Hobart">Australia/Hobart</option>
                    </optgroup>
                    <optgroup label="Africa">
                      <option value="Africa/Johannesburg">Africa/Johannesburg</option>
                    </optgroup>
                    <optgroup label="Other">
                      <option value="UTC">UTC</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-2">Weekly schedule</label>
                  <div className="space-y-2">
                    {weeklySchedule.map((day, di) => (
                      <div key={day.day_of_week} className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const updated = weeklySchedule.map((d, i) =>
                                i === di
                                  ? { ...d, blocks: d.blocks.length === 0 ? [{ start_time: '09:00', end_time: '17:00' }] : [] }
                                  : d
                              );
                              setWeeklySchedule(updated);
                            }}
                            className={`text-xs px-2 py-0.5 rounded border transition-colors ${day.blocks.length > 0 ? 'bg-primary-600 border-primary-600 text-white' : 'border-dark-border text-gray-500 hover:border-gray-400'}`}
                          >
                            {day.blocks.length > 0 ? 'Working' : 'Off'}
                          </button>
                          <span className="text-xs text-gray-400 w-20">{day.day_name}</span>
                          {day.blocks.length > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                const updated = weeklySchedule.map((d, i) =>
                                  i === di ? { ...d, blocks: [...d.blocks, { start_time: '09:00', end_time: '17:00' }] } : d
                                );
                                setWeeklySchedule(updated);
                              }}
                              className="text-xs text-primary-400 hover:text-primary-300 ml-auto"
                            >
                              + Add block
                            </button>
                          )}
                        </div>
                        {day.blocks.map((blk, bi) => (
                          <div key={bi} className="flex items-center gap-2 ml-24">
                            <input
                              type="time"
                              value={blk.start_time}
                              onChange={e => {
                                const updated = weeklySchedule.map((d, i) =>
                                  i === di ? { ...d, blocks: d.blocks.map((b, j) => j === bi ? { ...b, start_time: e.target.value } : b) } : d
                                );
                                setWeeklySchedule(updated);
                              }}
                              className="bg-dark-canvas border border-dark-border text-gray-200 text-xs rounded-lg px-2 py-1 focus:outline-none focus:border-primary-500"
                            />
                            <span className="text-xs text-gray-500">–</span>
                            <input
                              type="time"
                              value={blk.end_time}
                              onChange={e => {
                                const updated = weeklySchedule.map((d, i) =>
                                  i === di ? { ...d, blocks: d.blocks.map((b, j) => j === bi ? { ...b, end_time: e.target.value } : b) } : d
                                );
                                setWeeklySchedule(updated);
                              }}
                              className="bg-dark-canvas border border-dark-border text-gray-200 text-xs rounded-lg px-2 py-1 focus:outline-none focus:border-primary-500"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const updated = weeklySchedule.map((d, i) =>
                                  i === di ? { ...d, blocks: d.blocks.filter((_, j) => j !== bi) } : d
                                );
                                setWeeklySchedule(updated);
                              }}
                              className="text-xs text-gray-500 hover:text-red-400 px-1"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>

                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-2">Slot start interval</label>
                  <div className="flex gap-2">
                    {([15, 30, 60] as const).map(v => (
                      <button key={v} onClick={() => setSlotIncrement(v)}
                        className={`px-4 py-1.5 rounded-lg text-xs font-medium border transition-colors ${slotIncrement === v ? 'bg-primary-600 border-primary-600 text-white' : 'border-dark-border text-gray-300 hover:border-gray-500'}`}>
                        Every {v} min
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-2">Buffer between meetings</label>
                  <div className="flex gap-2">
                    {[0, 15, 30].map(v => (
                      <button key={v} onClick={() => setMeetingBuffer(v)}
                        className={`px-4 py-1.5 rounded-lg text-xs font-medium border transition-colors ${meetingBuffer === v ? 'bg-primary-600 border-primary-600 text-white' : 'border-dark-border text-gray-300 hover:border-gray-500'}`}>
                        {v === 0 ? 'None' : `${v} min`}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-2">Minimum booking notice</label>
                  <div className="flex gap-2 flex-wrap">
                    {([0, 1, 2, 3, 7] as const).map(v => (
                      <button key={v} onClick={() => setMinBookingNoticeDays(v)}
                        className={`px-4 py-1.5 rounded-lg text-xs font-medium border transition-colors ${minBookingNoticeDays === v ? 'bg-primary-600 border-primary-600 text-white' : 'border-dark-border text-gray-300 hover:border-gray-500'}`}>
                        {v === 0 ? 'Same day' : v === 1 ? '1 day' : v === 7 ? '1 week' : `${v} days`}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">How far in advance clients must book. "1 day" means the earliest available slot is tomorrow.</p>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={handleSchedulingConfigSave} disabled={schedulingConfigSaving}
                    className="text-xs bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg">
                    {schedulingConfigSaving ? 'Saving…' : 'Save'}
                  </button>
                  {schedulingConfigMsg && <p className="text-xs text-green-400">{schedulingConfigMsg}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Session Reminders */}
          <div className="bg-dark-card rounded-xl border border-dark-border shadow-sm p-5">
            <h2 className="font-semibold text-white text-sm mb-1">Session Reminders</h2>
            <p className="text-xs text-gray-400 mb-4">Choose when clients and you receive reminders. Select up to 3.</p>
            <div className="space-y-2">
              {REMINDER_OPTIONS.map(opt => {
                const selected = reminderSlots.includes(opt.value);
                const disabled = !selected && reminderSlots.length >= 3;
                return (
                  <label key={opt.value} className={`flex items-center gap-3 cursor-pointer ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selected}
                      disabled={disabled}
                      onChange={() => handleReminderToggle(opt.value)}
                      className="w-4 h-4 rounded accent-primary-500"
                    />
                    <span className="text-xs text-gray-200">{opt.label}</span>
                  </label>
                );
              })}
            </div>
            {reminderSlots.length >= 3 && (
              <p className="text-xs text-yellow-400 mt-2">Maximum 3 reminders selected.</p>
            )}
            {reminderSlots.length === 0 && (
              <p className="text-xs text-yellow-400 mt-2">No reminders will be sent.</p>
            )}
            <div className="flex items-center gap-3 mt-4">
              <button onClick={handleReminderSave} disabled={reminderSaving}
                className="text-xs bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg">
                {reminderSaving ? 'Saving…' : 'Save'}
              </button>
              {reminderMsg && <p className="text-xs text-green-400">{reminderMsg}</p>}
            </div>
          </div>

          {/* Google Calendar */}
          <div className={`bg-dark-card rounded-xl border shadow-sm ${googleActive ? 'border-primary-700' : 'border-dark-border'}`}>
            {/* Card header — always visible, click to expand */}
            <button
              className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-dark-hover/40 rounded-t-xl transition-colors"
              onClick={() => setGoogleCardExpanded(e => !e)}
            >
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-medium bg-dark-border text-gray-400 px-1.5 py-0.5 rounded">Step 2</span>
                    <h2 className="font-semibold text-white text-sm">Google Calendar</h2>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {googleActive
                      ? zoomStatus?.connected
                        ? 'Using Zoom for video · Google Calendar for availability'
                        : 'Using Google Meet for video · Google Calendar for availability'
                      : 'Connect to enable scheduling, calendar invites, and video links.'
                    }
                  </p>
                  {/* Status + Connect shown under subtitle when not loading and card is collapsed */}
                  {!googleCardExpanded && googleStatus !== null && (
                    <div className="flex items-center gap-2 mt-1.5" onClick={e => e.stopPropagation()}>
                      {googleActive ? (
                        <span className="flex items-center gap-1 text-xs font-medium text-green-400">
                          <CheckCircleIcon className="w-3 h-3" /> Connected
                        </span>
                      ) : googleStatus?.token_expired ? (
                        <>
                          <span className="text-xs text-yellow-400">Token expired</span>
                          <button
                            onClick={() => setGoogleCardExpanded(true)}
                            className="text-xs font-medium text-white bg-green-600 hover:bg-green-700 px-2.5 py-0.5 rounded-md transition-colors"
                          >
                            Reconnect
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="text-xs text-gray-500">Not connected</span>
                          <button
                            onClick={() => setGoogleCardExpanded(true)}
                            className="text-xs font-medium text-white bg-green-600 hover:bg-green-700 px-2.5 py-0.5 rounded-md transition-colors"
                          >
                            Connect
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {googleActive && (
                  <span className="flex items-center gap-1 text-xs font-medium text-green-400">
                    <CheckCircleIcon className="w-3.5 h-3.5" /> Connected
                  </span>
                )}
                <ChevronRightIcon className={`w-4 h-4 text-gray-500 transition-transform ${googleCardExpanded ? 'rotate-90' : ''}`} />
              </div>
            </button>

            {/* Expanded body */}
            {googleCardExpanded && (
              <div className="border-t border-dark-border px-5 py-4 space-y-5">
                {/* Session types */}
                <div>
                  <p className="text-xs font-medium text-gray-400 mb-1">Session Types <span className="font-normal text-dark-muted">(optional)</span></p>
                  <p className="text-xs text-gray-500 mb-3">Name and duration shown to clients in the booking flow.</p>
                  <div className="space-y-2">
                    {googleEtLoading ? (
                      <p className="text-xs text-gray-400">Loading…</p>
                    ) : googleEtList.length === 0 && !googleEtForm ? (
                      <p className="text-xs text-gray-500 italic">No session types yet.</p>
                    ) : (
                      googleEtList.map(et => (
                        <div key={et.id} className="flex items-center justify-between bg-dark-canvas rounded-lg px-3 py-2 gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm text-gray-200 font-medium">{et.name}</p>
                              <span className={`text-[10px] rounded-full px-1.5 py-0.5 border flex-shrink-0 ${
                                et.billing_type === 'complimentary' ? 'bg-green-900/30 border-green-700/50 text-green-400' :
                                et.billing_type === 'pro_bono' ? 'bg-blue-900/30 border-blue-700/50 text-blue-400' :
                                et.billing_type === 'barter' ? 'bg-yellow-900/30 border-yellow-700/50 text-yellow-400' :
                                'bg-dark-hover border-dark-border text-gray-400'
                              }`}>
                                {et.billing_type === 'pro_bono' ? 'Pro Bono' : et.billing_type === 'complimentary' ? 'Complimentary' : et.billing_type === 'barter' ? 'Barter' : 'Billable'}
                              </span>
                              {et.billing_type === 'billable' && et.default_price_cents != null && (
                                <span className="text-[10px] text-primary-400 flex-shrink-0">
                                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: (et.default_currency || 'USD').toUpperCase() }).format(et.default_price_cents / 100)}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{et.duration_minutes} min{et.description ? ` · ${et.description}` : ''}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={et.id in googleEtPendingExternal ? googleEtPendingExternal[et.id] : et.allow_external_booking}
                                onChange={() => handleGoogleEtToggleExternal(et.id, et.allow_external_booking)}
                                className="accent-primary-500 w-3.5 h-3.5"
                              />
                              <span className="text-xs text-gray-400">External booking</span>
                            </label>
                            {et.id in googleEtPendingExternal && (
                              <button
                                onClick={() => handleGoogleEtSaveExternal(et.id)}
                                disabled={googleEtExternalSaving[et.id]}
                                className="text-green-400 hover:text-green-300 disabled:opacity-50 transition-colors"
                                title="Save"
                              >
                                <CheckCircleIcon className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleGoogleEtDelete(et.id)}
                              disabled={et.id in googleEtPendingExternal ? googleEtPendingExternal[et.id] : et.allow_external_booking}
                              className="text-gray-500 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              title={(et.id in googleEtPendingExternal ? googleEtPendingExternal[et.id] : et.allow_external_booking) ? "Disable external booking before deleting" : "Delete"}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                    {googleEtForm ? (
                      <div className="space-y-2 pt-1">
                        <input
                          ref={googleEtNameRef}
                          type="text"
                          placeholder="Session type name (e.g. Weekly Check-in)"
                          value={googleEtForm.name}
                          onChange={e => setGoogleEtForm(f => f ? { ...f, name: e.target.value } : f)}
                          className="w-full border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-1 focus:ring-primary-500 focus:outline-none"
                        />
                        <div className="flex gap-2">
                          <input
                            type="number" min="5" step="5"
                            placeholder="Duration (min)"
                            value={googleEtForm.duration_minutes}
                            onChange={e => setGoogleEtForm(f => f ? { ...f, duration_minutes: e.target.value } : f)}
                            className="w-32 border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-1 focus:ring-primary-500 focus:outline-none"
                          />
                          <select
                            value={googleEtForm.billing_type}
                            onChange={e => setGoogleEtForm(f => f ? { ...f, billing_type: e.target.value, default_price: e.target.value !== 'billable' ? '' : f.default_price } : f)}
                            className="border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 focus:ring-1 focus:ring-primary-500 focus:outline-none"
                          >
                            <option value="billable">Billable</option>
                            <option value="complimentary">Complimentary</option>
                            <option value="pro_bono">Pro Bono</option>
                            <option value="barter">Barter</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Description (optional)"
                            value={googleEtForm.description}
                            onChange={e => setGoogleEtForm(f => f ? { ...f, description: e.target.value } : f)}
                            className="flex-1 border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-1 focus:ring-primary-500 focus:outline-none"
                          />
                        </div>
                        {googleEtForm.billing_type === 'billable' && (
                          <div className="flex gap-2">
                            <input
                              type="number" min="0" step="0.01"
                              placeholder="Price (optional)"
                              value={googleEtForm.default_price}
                              onChange={e => setGoogleEtForm(f => f ? { ...f, default_price: e.target.value } : f)}
                              className="w-36 border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-1 focus:ring-primary-500 focus:outline-none"
                            />
                            <select
                              value={googleEtForm.default_currency}
                              onChange={e => setGoogleEtForm(f => f ? { ...f, default_currency: e.target.value } : f)}
                              className="border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 focus:ring-1 focus:ring-primary-500 focus:outline-none"
                            >
                              {['USD','CAD','GBP','EUR','AUD','NZD'].map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                        )}
                        {googleEtError && <p className="text-xs text-red-400">{googleEtError}</p>}
                        <div className="flex gap-2">
                          <button onClick={handleGoogleEtCreate} disabled={googleEtSaving} className="text-xs bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg">
                            {googleEtSaving ? 'Saving…' : 'Add'}
                          </button>
                          <button onClick={() => { setGoogleEtForm(null); setGoogleEtError(''); }} className="text-xs text-gray-400 hover:text-gray-200 px-2 py-1.5">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => setGoogleEtForm({ name: '', duration_minutes: '60', description: '', billing_type: 'billable', default_price: '', default_currency: coachCurrency || 'USD' })} className="text-xs text-primary-400 hover:text-primary-300">
                        + Add session type
                      </button>
                    )}
                  </div>
                </div>


                {/* Enforce payment at booking — sessions */}
                <div className="border-t border-dark-border pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-300">Require payment to book</p>
                      <p className="text-xs text-gray-500 mt-0.5">When on, clients must pay at booking time for billable sessions that have a price set.</p>
                    </div>
                    <button
                      onClick={handleEnforcePaymentSessionsToggle}
                      disabled={enforcePaymentSessionsSaving}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-50 ${enforcePaymentSessions ? 'bg-primary-600' : 'bg-gray-600'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${enforcePaymentSessions ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  </div>
                </div>

                {/* Booking page */}
                {googleActive && (
                  <div className="border-t border-dark-border pt-4">
                    <p className="text-xs font-medium text-gray-400 mb-1">Your Booking Page</p>
                    {googleEtList.some(et => et.allow_external_booking && et.is_active) && (
                      <p className="text-xs text-gray-500 mb-3">
                        Share this link or embed it on your website so new clients can book directly.
                        Only session types with <strong className="text-gray-400">External booking</strong> enabled appear.
                      </p>
                    )}
                    {googleEtList.some(et => et.allow_external_booking && et.is_active) ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 bg-dark-canvas border border-dark-border rounded-lg px-3 py-2">
                          <span className="flex-1 text-xs text-gray-300 font-mono truncate">
                            {window.location.origin}/book/{coachId}
                          </span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`${window.location.origin}/book/${coachId}`);
                            }}
                            className="text-xs text-primary-400 hover:text-primary-300 flex-shrink-0"
                          >
                            Copy
                          </button>
                          <a
                            href={`/book/${coachId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-gray-400 hover:text-gray-200 flex-shrink-0"
                          >
                            Preview ↗
                          </a>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-yellow-400 bg-yellow-900/20 border border-yellow-700/50 rounded-lg px-3 py-2">
                        Enable <strong>External booking</strong> on at least one session type above to activate your booking page.
                      </p>
                    )}
                  </div>
                )}

                {/* Connection row */}
                <div className="border-t border-dark-border pt-4">
                  <p className="text-xs font-medium text-gray-400 mb-2">Connection</p>
                  {googleSuccessMsg && (
                    <div className="flex items-center gap-2 bg-green-900/40 border border-green-800 text-green-300 text-xs px-3 py-2 rounded-lg mb-2">
                      <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />{googleSuccessMsg}
                    </div>
                  )}
                  {anyActive && !googleActive ? (
                    <p className="text-xs text-dark-muted">Disconnect {calendlyActive ? 'Calendly' : 'current provider'} first to use Google Calendar.</p>
                  ) : googleStatus === null ? (
                    <p className="text-xs text-dark-muted">Loading…</p>
                  ) : !googleStatus.app_configured ? (
                    <p className="text-xs text-dark-muted">Google Calendar integration is not available on this server.</p>
                  ) : googleStatus.connected ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between bg-green-900/30 border border-green-800 rounded-lg px-3 py-2">
                        <div>
                          <p className="text-xs font-medium text-green-300 flex items-center gap-1"><CheckCircleIcon className="w-3.5 h-3.5" /> Connected</p>
                          {googleStatus.email && <p className="text-xs text-gray-400 mt-0.5">{googleStatus.email}</p>}
                        </div>
                        <button onClick={handleGoogleDisconnect} className="text-xs border border-dark-border bg-dark-canvas text-gray-200 px-2.5 py-1 rounded-lg hover:bg-dark-hover">Disconnect</button>
                      </div>
                      {googleStatus.email && coachEmail && googleStatus.email.toLowerCase() !== coachEmail.toLowerCase() && (
                        <p className="text-xs text-yellow-400 bg-yellow-900/30 border border-yellow-700 rounded-lg px-3 py-2">
                          The connected Google account ({googleStatus.email}) is different from your CoachSight email ({coachEmail}). Calendar invites will appear to come from {googleStatus.email}.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {googleStatus.token_expired && (
                        <p className="text-xs text-yellow-400 bg-yellow-900/30 border border-yellow-700 rounded-lg px-3 py-2">Your Google authorization has expired. Please reconnect.</p>
                      )}
                      <button onClick={handleGoogleConnect} disabled={googleConnecting} className="flex items-center gap-2 bg-dark-canvas border border-dark-border text-gray-200 px-3 py-2 rounded-lg text-xs font-medium hover:bg-dark-hover disabled:opacity-40">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                        {googleConnecting ? 'Opening…' : googleStatus.token_expired ? 'Reconnect Google' : 'Sign in with Google'}
                      </button>
                      {googleConnectError && <p className="text-xs text-red-500">{googleConnectError}</p>}
                    </div>
                  )}
                </div>

                {/* Video provider — only shown when Google Calendar is connected */}
                {googleStatus?.connected && zoomStatus !== null && zoomStatus.app_configured && (
                  <div className="border-t border-dark-border pt-4">
                    <p className="text-xs font-medium text-gray-400 mb-2">Video</p>
                    {zoomStatus.connected ? (
                      <div className="flex items-center justify-between bg-blue-900/20 border border-blue-800/50 rounded-lg px-3 py-2">
                        <div>
                          <p className="text-xs font-medium text-blue-300 flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="4" fill="#2D8CFF"/><path d="M4 8.5C4 7.67 4.67 7 5.5 7h8C14.33 7 15 7.67 15 8.5v7c0 .83-.67 1.5-1.5 1.5h-8C4.67 17 4 16.33 4 15.5v-7zM15.5 10.25l3.8-2.53A.75.75 0 0120 8.37v7.26a.75.75 0 01-1.2.6L15.5 13.5v-3.25z" fill="white"/></svg>
                            Zoom
                          </p>
                          {zoomStatus.zoom_email && <p className="text-xs text-gray-400 mt-0.5">{zoomStatus.zoom_email}</p>}
                        </div>
                        <button onClick={() => setShowZoomDisconnectConfirm(true)} className="text-xs border border-dark-border bg-dark-canvas text-gray-200 px-2.5 py-1 rounded-lg hover:bg-dark-hover">
                          Switch to Meet
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-300 flex items-center gap-1.5">
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                            Google Meet
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">Links auto-created via Google Calendar</p>
                        </div>
                        <button onClick={handleZoomConnect} disabled={zoomConnecting} className="text-xs border border-dark-border bg-dark-canvas text-gray-200 px-2.5 py-1 rounded-lg hover:bg-dark-hover disabled:opacity-40">
                          {zoomConnecting ? 'Opening…' : 'Switch to Zoom'}
                        </button>
                      </div>
                    )}
                    {zoomConnectError && <p className="text-xs text-red-400 mt-1">{zoomConnectError}</p>}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Calendly — hidden when config.hideCalendly is true */}
          {!config.hideCalendly && <div className={`bg-dark-card rounded-xl border shadow-sm ${calendlyActive ? 'border-primary-700' : 'border-dark-border'}`}>
            <div className="px-5 py-4 border-b border-dark-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 40 40" fill="none"><rect width="40" height="40" rx="8" fill="#006BFF"/><path d="M20 8C13.373 8 8 13.373 8 20s5.373 12 12 12 12-5.373 12-12S26.627 8 20 8zm0 4a8 8 0 110 16 8 8 0 010-16zm1 3h-2v6l5.25 3.15.75-1.23-4-2.42V15z" fill="white"/></svg>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-medium bg-dark-border text-gray-400 px-1.5 py-0.5 rounded">Step 2</span>
                    <h2 className="font-semibold text-white text-sm">Calendly</h2>
                  </div>
                  <p className="text-xs text-gray-400">Clients book via your Calendly link. CoachSight records sessions with a video meeting configured.</p>
                </div>
              </div>
              {calendlyActive && calendlyStatus?.scheduling_type === 'calendly' && <span className="flex items-center gap-1 text-xs font-medium text-green-400 flex-shrink-0"><CheckCircleIcon className="w-3.5 h-3.5" /> Active</span>}
            </div>
            <div className="mx-4 mb-1 px-3 py-2 bg-amber-900/20 border border-amber-700/40 rounded-lg">
              <p className="text-xs text-amber-300">
                Requires a paid Calendly plan (Standard or above). Free accounts are limited to 1 session type and do not support in-app booking.
              </p>
            </div>
            <div className="p-4 space-y-3">
              {anyActive && !calendlyActive ? (
                <p className="text-xs text-dark-muted">Disconnect {googleActive ? 'Google Calendar' : 'current provider'} first to use Calendly.</p>
              ) : !calendlyStatus?.connected ? (
                <div className="space-y-2">
                  <button onClick={handleCalendlyConnect} disabled={calendlyConnecting} className="flex items-center gap-2 bg-dark-canvas border border-dark-border text-gray-200 px-3 py-2 rounded-lg text-xs font-medium hover:bg-dark-hover disabled:opacity-40">
                    <svg className="w-4 h-4" viewBox="0 0 40 40" fill="none"><rect width="40" height="40" rx="8" fill="#006BFF"/><path d="M20 8C13.373 8 8 13.373 8 20s5.373 12 12 12 12-5.373 12-12S26.627 8 20 8zm0 4a8 8 0 110 16 8 8 0 010-16zm1 3h-2v6l5.25 3.15.75-1.23-4-2.42V15z" fill="white"/></svg>
                    {calendlyConnecting ? 'Opening…' : 'Connect Calendly'}
                  </button>
                  {calendlyConnectError && <p className="text-xs text-red-400">{calendlyConnectError}</p>}
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between bg-blue-900/20 border border-blue-800/50 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-xs font-medium text-blue-300">Connected</p>
                      {calendlyStatus.email && <p className="text-xs text-gray-400 mt-0.5">{calendlyStatus.email}</p>}
                    </div>
                    <button onClick={handleCalendlyDisconnect} className="text-xs border border-dark-border bg-dark-canvas text-gray-200 px-2.5 py-1 rounded-lg hover:bg-dark-hover">Disconnect</button>
                  </div>
                  {/* Event type selector */}
                  {calendlyEventTypes.length === 0 ? (
                    calendlyEventTypesLoading ? (
                      <p className="text-xs text-gray-400">Loading event types…</p>
                    ) : null
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-300">Select session types to offer clients:</p>
                      {calendlyEventTypes.map(et => {
                        const checked = calendlySelectedUris.includes(et.uri);
                        return (
                          <label key={et.uri} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-primary-500 bg-dark-hover' : 'border-dark-border hover:border-gray-600'}`}>
                            <input
                              type="checkbox"
                              value={et.uri}
                              checked={checked}
                              onChange={() => setCalendlySelectedUris(prev =>
                                prev.includes(et.uri) ? prev.filter(u => u !== et.uri) : [...prev, et.uri]
                              )}
                              className="mt-0.5 accent-primary-500"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-200">{et.name}</p>
                              <p className="text-xs text-gray-500">{et.duration} min</p>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${et.video_configured ? 'bg-green-900/40 text-green-300' : 'bg-yellow-900/40 text-yellow-300'}`}>
                              {et.video_configured ? `✓ ${et.video_type ?? 'video'}` : 'No video'}
                            </span>
                          </label>
                        );
                      })}
                      {calendlySelectedUris.length > 0 && (
                        <button onClick={handleCalendlySaveSettings} disabled={calendlySaving} className="mt-1 text-xs bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg">
                          {calendlySaving ? 'Saving…' : 'Save'}
                        </button>
                      )}
                      {calendlySaveMsg && <p className="text-xs text-green-400">{calendlySaveMsg}</p>}
                    </div>
                  )}

                  {/* Booking page — shown once Calendly is active */}
                  {calendlyStatus?.scheduling_type === 'calendly' && (
                    <div className="border-t border-dark-border pt-3 mt-1">
                      <p className="text-xs font-medium text-gray-400 mb-1">Your Booking Page</p>
                      <p className="text-xs text-gray-500 mb-3">
                        Share this link so clients can book directly. All active session types from your Calendly account appear automatically.
                      </p>
                      <div className="flex items-center gap-2 bg-dark-canvas border border-dark-border rounded-lg px-3 py-2">
                        <span className="flex-1 text-xs text-gray-300 font-mono truncate">
                          {window.location.origin}/book/{coachId}
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/book/${coachId}`);
                          }}
                          className="text-xs text-primary-400 hover:text-primary-300 flex-shrink-0"
                        >
                          Copy
                        </button>
                        <a
                          href={`/book/${coachId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-gray-400 hover:text-gray-200 flex-shrink-0"
                        >
                          Preview ↗
                        </a>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>}


        </div>
      );
    }

    // ── Payments ───────────────────────────────────────────────────────────────
    if (settingsSubSection === 'payments') return (
      <div className="p-8 max-w-xl space-y-6">
        {subNav}
        <h1 className="text-2xl font-bold text-white">Payments</h1>
        {stripeSuccessMsg && (
          <div className="text-xs text-green-400 bg-green-900/20 rounded-lg px-3 py-2">{stripeSuccessMsg}</div>
        )}
        <PaymentSettings />
        <PaymentMethodsCard />
      </div>
    );

    // ── Document Types ─────────────────────────────────────────────────────────
    if (settingsSubSection === 'documents') return (
      <DocumentTypesSettings subNav={subNav} />
    );

    // ── Privacy & Data ─────────────────────────────────────────────────────────
    if (settingsSubSection === 'privacy') return (
      <PrivacyDataSection subNav={subNav} />
    );

    // ── Notification Preferences ───────────────────────────────────────────────
    if (settingsSubSection === 'notifications') return (
      <CoachNotificationPreferences subNav={subNav} />
    );

    // ── My Subscription ────────────────────────────────────────────────────────
    if (settingsSubSection === 'subscription') {
      if (!billingChargingActive) {
        setSettingsSubSection(null);
        return null;
      }
      return (
        <div className="p-8 max-w-3xl space-y-4">
          {subNav}
          <h1 className="text-2xl font-bold text-white">My Subscription</h1>
          <BillingTab />
        </div>
      );
    }

    // ── Landing page ───────────────────────────────────────────────────────────
    return (
    <div className="p-8 max-w-2xl space-y-8">
      <h1 className="text-2xl font-bold text-white">Settings</h1>

      {/* Configuration group */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Configuration</p>
        <div className="grid grid-cols-3 gap-3">
          {([
            { key: 'profile'       as const, label: 'My Profile',              Icon: UserCircleIcon,   desc: 'Name, bio, contact & address' },
            ...(billingChargingActive ? [{ key: 'subscription' as const, label: 'My Subscription', Icon: CreditCardIcon, desc: 'Plan, payment method & invoices' }] : []),
            { key: 'scheduling'    as const, label: 'Scheduling',              Icon: CalendarDaysIcon, desc: 'Google Calendar, Calendly, Zoom' },
            { key: 'payments'      as const, label: 'Payments',                Icon: CreditCardIcon,   desc: 'Stripe & session billing' },
            { key: 'notifications' as const, label: 'Notification Preferences',Icon: EnvelopeIcon,     desc: 'Email and SMS delivery per event type' },
            { key: 'documents'     as const, label: 'Document Types',          Icon: DocumentIcon,     desc: 'Manage custom document categories' },
            { key: 'privacy'       as const, label: 'Privacy & Data',          Icon: ShieldCheckIcon,  desc: 'Download or manage your personal data' },
          ]).map(({ key, label, Icon, desc }) => (
            <button
              key={key}
              onClick={() => setSettingsSubSection(key)}
              className="bg-dark-card border border-dark-border rounded-xl p-4 text-left hover:border-primary-700 hover:bg-dark-hover transition-colors group"
            >
              <Icon className="w-5 h-5 text-gray-500 group-hover:text-primary-400 mb-2 transition-colors" />
              <p className="text-sm font-medium text-white">{label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Notes Generation group */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Notes</p>
        <button
          onClick={() => setSettingsSubSection('ai-notes')}
          className="w-full bg-dark-card border border-dark-border rounded-xl p-4 text-left hover:border-primary-700 hover:bg-dark-hover transition-colors group flex items-start gap-4"
        >
          <SparklesIcon className="w-5 h-5 text-gray-500 group-hover:text-primary-400 mt-0.5 transition-colors flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-white">Notes Generation</p>
            <p className="text-xs text-gray-500 mt-0.5">Customise instructions for each section of generated session notes.</p>
          </div>
        </button>
      </div>

      {/* Feature flags — commented out until toggles are enforced
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">Features</p>
        <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
          {([
            { key: 'payments'     as const, label: 'Payments',     description: 'Accept session payments from clients via Stripe.' },
            { key: 'social_media' as const, label: 'Social Media', description: 'Post to LinkedIn, Twitter, Facebook, and Instagram.' },
          ]).map(({ key, label, description }) => {
            const enabled = flags[key] ?? false;
            const saving  = flagSaving === key;
            return (
              <div key={key} className="flex items-center justify-between px-4 py-3 border-b border-dark-border last:border-b-0">
                <div>
                  <p className="text-sm font-medium text-gray-200">{label}</p>
                  <p className="text-xs text-dark-muted mt-0.5">{description}</p>
                </div>
                <button
                  disabled={saving}
                  onClick={async () => {
                    setFlagSaving(key);
                    try { await setFlag(key, !enabled); await refreshFlags(); }
                    finally { setFlagSaving(null); }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${enabled ? 'bg-primary-600' : 'bg-gray-600'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
      */}
    </div>
    );
  };

  const ReportsSection = () => {
    const subNavLabel = reportsSubSection === 'cashflow' ? 'Cashflow'
      : reportsSubSection === 'coaching-log' ? 'Coaching Log'
      : reportsSubSection === 'invoices' ? 'Invoices'
      : '';
    const subNav = reportsSubSection && (
      <div className="flex items-center gap-1.5 text-sm mb-6">
        <button
          onClick={() => setReportsSubSection(null)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          Activity
        </button>
        <ChevronRightIcon className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
        <span className="text-white font-medium">{subNavLabel}</span>
      </div>
    );

    if (reportsSubSection === 'cashflow') return (
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {subNav}
        <CashflowTab clients={clients.map(c => ({ client_id: c.client_id, client_name: c.client_name }))} />
      </div>
    );

    if (reportsSubSection === 'coaching-log') return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-6 pt-6">{subNav}</div>
        <CoachingLogTab />
      </div>
    );

    if (reportsSubSection === 'invoices') return (
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {subNav}
        <InvoicesTab clients={clients.map(c => ({ client_id: c.client_id, client_name: c.client_name }))} />
      </div>
    );

    // ── Landing page ───────────────────────────────────────────────────────────
    return (
      <div className="p-8 max-w-2xl space-y-8">
        <h1 className="text-2xl font-bold text-white">Activity</h1>
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={() => setReportsSubSection('coaching-log')}
            className="bg-dark-card border border-dark-border rounded-xl p-4 text-left hover:border-primary-700 hover:bg-dark-hover transition-colors group"
          >
            <ClipboardDocumentListIcon className="w-5 h-5 text-gray-500 group-hover:text-primary-400 mb-2 transition-colors" />
            <p className="text-sm font-medium text-white">Coaching Log</p>
            <p className="text-xs text-gray-500 mt-0.5">All sessions with client, type, and status</p>
          </button>
        </div>
      </div>
    );
  };

  const MarketingSection = () => {
    const tabs: { key: 'social' | 'email' | 'content' | 'website'; label: string; live: boolean }[] = [
      { key: 'social',   label: 'Social Media',    live: true  },
      { key: 'email',    label: 'Email Campaigns', live: false },
      { key: 'content',  label: 'Content Library', live: false },
      { key: 'website',  label: 'Coach Website',   live: false },
    ];

    return (
      <div className="p-8 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Marketing</h1>
          <p className="text-sm text-gray-400 mt-1">Grow your practice and share your expertise.</p>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-1 mb-8 bg-dark-card border border-dark-border rounded-xl p-1 w-fit">
          {tabs.map(({ key, label, live }) => (
            <button
              key={key}
              onClick={() => live && setMarketingTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                marketingTab === key
                  ? 'bg-primary-600 text-white shadow-sm'
                  : live
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-dark-hover'
                    : 'text-dark-muted cursor-not-allowed'
              }`}
            >
              {label}
              {!live && (
                <span className="text-xs bg-dark-hover text-dark-muted px-1.5 py-0.5 rounded-md">Soon</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Social Media tab ──────────────────────────────── */}
        {marketingTab === 'social' && (
          <div className="space-y-6">
            {/* Hero action row */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">Social Media</h2>
                <p className="text-sm text-gray-400 mt-0.5">Share EF tips and insights directly to your connected accounts.</p>
              </div>
              <button
                onClick={() => setSocialPostModalOpen(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors shadow-sm"
              >
                <SparklesIcon className="w-4 h-4" />
                Create Post
              </button>
            </div>

            {/* Success banner */}
            {socialBanner && (
              <div className="flex items-center gap-2 px-4 py-3 bg-green-900/30 border border-green-800 rounded-xl text-sm text-green-300">
                <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="capitalize">{socialBanner}</span> connected successfully!
                <button onClick={() => setSocialBanner(null)} className="ml-auto text-green-500 hover:text-green-400">
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Platform connection cards */}
            <div className="grid grid-cols-2 gap-4">
              {(['linkedin', 'twitter', 'facebook', 'instagram'] as Platform[]).map(platform => {
                const status = socialStatuses[platform];
                const connected = status?.connected;
                const configured = status?.app_configured;
                const platformLabel = platform === 'twitter' ? 'Twitter / X' : platform.charAt(0).toUpperCase() + platform.slice(1);

                return (
                  <div key={platform} className={`bg-dark-card border rounded-xl p-4 flex flex-col gap-3 transition-colors ${
                    connected ? 'border-primary-700/50' : 'border-dark-border'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${connected ? 'bg-green-500' : 'bg-gray-600'}`} />
                        <span className="text-sm font-semibold text-white capitalize">{platformLabel}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        connected ? 'bg-green-900/40 text-green-300' : 'bg-dark-hover text-gray-500'
                      }`}>
                        {connected ? 'Connected' : 'Not connected'}
                      </span>
                    </div>

                    {status?.handle && (
                      <p className="text-xs text-gray-500 truncate">{status.handle}</p>
                    )}
                    {status?.expiring_soon && (
                      <p className="text-xs text-orange-400">Token expiring soon — reconnect</p>
                    )}

                    <div className="mt-auto pt-1">
                      {connected ? (
                        <button
                          onClick={async () => {
                            setSocialConnecting(platform);
                            try {
                              await disconnectSocial(platform);
                              await loadSocialStatuses();
                            } catch {}
                            setSocialConnecting(null);
                          }}
                          disabled={socialConnecting === platform}
                          className="text-xs text-red-400 hover:text-red-300 font-medium disabled:opacity-50 transition-colors"
                        >
                          {socialConnecting === platform ? 'Disconnecting…' : 'Disconnect'}
                        </button>
                      ) : (
                        <button
                          onClick={async () => {
                            if (!configured) return;
                            setSocialConnecting(platform);
                            try {
                              const url = await getSocialAuthUrl(platform);
                              window.location.href = url;
                            } catch {
                              setSocialConnecting(null);
                            }
                          }}
                          disabled={!configured || socialConnecting === platform}
                          className="text-xs text-primary-400 hover:text-primary-300 font-medium disabled:opacity-50 disabled:text-gray-600 transition-colors"
                          title={!configured ? `${platformLabel} not configured on this server` : undefined}
                        >
                          {socialConnecting === platform ? 'Connecting…' : configured ? '+ Connect account' : 'Not configured'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quick tips */}
            <div className="bg-dark-card border border-dark-border rounded-xl p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Platform tips</p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><span className="text-white font-medium">LinkedIn</span> — Professional tone, up to 1,300 chars. Best for thought leadership.</li>
                <li><span className="text-white font-medium">Twitter/X</span> — Max 280 chars. Punchy hooks, 1–3 hashtags.</li>
                <li><span className="text-white font-medium">Facebook</span> — Warm and conversational, up to 500 chars.</li>
                <li><span className="text-white font-medium">Instagram</span> — Image required. Strong hook first line, 5–10 hashtags.</li>
              </ul>
            </div>
          </div>
        )}

        {/* ── Coming Soon tabs ─────────────────────────────── */}
        {marketingTab !== 'social' && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 bg-dark-card border border-dark-border rounded-2xl flex items-center justify-center mb-4">
              <MegaphoneIcon className="w-8 h-8 text-gray-600" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {marketingTab === 'email' && 'Email Campaigns'}
              {marketingTab === 'content' && 'Content Library'}
              {marketingTab === 'website' && 'Coach Website'}
            </h3>
            <p className="text-sm text-gray-500 max-w-sm">
              {marketingTab === 'email' && 'Send newsletters and drip campaigns to your clients and prospects.'}
              {marketingTab === 'content' && 'A library of EF tip templates and post drafts ready to publish.'}
              {marketingTab === 'website' && 'A public booking page for your practice with your bio and availability.'}
            </p>
            <span className="mt-4 text-xs bg-dark-hover text-gray-500 px-3 py-1.5 rounded-full border border-dark-border">Coming soon</span>
          </div>
        )}
      </div>
    );
  };

  // ── Sidebar ────────────────────────────────────────────────────────────────

  type NavEntry =
    | { kind?: undefined; key: Section; label: string; Icon: React.ElementType; onClick?: () => void; isActive?: boolean; group?: string }
    | { kind: 'header'; label: string; group?: string };

  const navItems: NavEntry[] = [
    { key: 'dashboard',    label: 'Dashboard',    Icon: HomeIcon },
    { key: 'clients',      label: 'Clients',      Icon: UserGroupIcon },
    { key: 'schedules',    label: 'Schedules',    Icon: CalendarIcon },
    ...(flags['coaching_packages'] ? [{ key: 'services' as Section, label: 'Offerings', Icon: BriefcaseIcon }] : []),
    { key: 'documents',    label: 'Documents',    Icon: DocumentIcon },
    { key: 'forms',        label: 'Forms Library', Icon: ClipboardDocumentListIcon },
    ...(flags['social_media'] ? [{ key: 'marketing' as Section, label: 'Marketing', Icon: MegaphoneIcon }] : []),
    { kind: 'header', label: 'Payments', group: 'payments' },
    { key: 'invoices', label: 'Invoices', Icon: DocumentTextIcon, group: 'payments' },
    { key: 'cashflow', label: 'Cashflow', Icon: BanknotesIcon, group: 'payments' },
    {
      key: 'settings' as Section,
      label: 'Payment Settings',
      Icon: CreditCardIcon,
      onClick: () => { setActiveSection('settings'); setSettingsSubSection('payments'); },
      isActive: activeSection === 'settings' && settingsSubSection === 'payments',
      group: 'payments',
    },
    { key: 'reports', label: 'Activity', Icon: ChartBarIcon },
    // { key: 'test-summary', label: 'Test Summary', Icon: BeakerIcon },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  const sidebarTextColor = brandColor ? getContrastColor(brandColor) : undefined;

  return (
    <div className="pm-branded flex bg-dark-canvas overflow-hidden" style={{ height: '100vh' }}>
      <SubscriptionGateModal />
      <TrialBanner onUpgrade={() => { setActiveSection('settings'); setSettingsSubSection('subscription'); }} />

      {/* Dynamic brand color overrides for primary buttons and accents */}
      {brandColor && /^#[0-9a-f]{6}$/i.test(brandColor) && (
        <style>{`
          .pm-branded .bg-primary-600 { background-color: ${brandColor} !important; }
          .pm-branded .hover\\:bg-primary-700:hover { background-color: ${darkenHex(brandColor, 15)} !important; }
          .pm-branded .hover\\:bg-primary-500:hover { background-color: ${darkenHex(brandColor, 5)} !important; }
          .pm-branded .bg-primary-700\\/60 { background-color: ${darkenHex(brandColor, 15)}99 !important; }
          .pm-branded .text-primary-300 { color: ${lightenHex(brandColor, 40)} !important; }
          .pm-branded .text-primary-400 { color: ${lightenHex(brandColor, 20)} !important; }
          .pm-branded .border-primary-500 { border-color: ${brandColor} !important; }
          .pm-branded .border-primary-700 { border-color: ${darkenHex(brandColor, 15)} !important; }
          .pm-branded .focus\\:ring-primary-500:focus { --tw-ring-color: ${brandColor} !important; }
          .pm-branded .ring-primary-500 { --tw-ring-color: ${brandColor} !important; }
          .pm-branded .accent-primary-500 { accent-color: ${brandColor} !important; }
          .pm-branded .accent-primary-600 { accent-color: ${brandColor} !important; }
        `}</style>
      )}

      {/* Sidebar */}
      <aside
        className={`w-56 flex-shrink-0 ${brandColor ? '' : 'bg-dark-sidebar'} border-r border-dark-border flex flex-col`}
        style={brandColor ? { backgroundColor: brandColor } : undefined}
      >
        {/* Logo */}
        <div className="bg-white border-b border-gray-200 flex-shrink-0">
          {companyLogoUrl ? (
            <img
              src={companyLogoUrl}
              alt="Practice logo"
              className="w-full object-contain"
              style={{ height: 72 }}
            />
          ) : (
            <>
              <img
                src="/coachsight-logo.png"
                alt="CoachSight ECR"
                style={{ width: 224, height: 72, objectFit: 'cover', objectPosition: 'center 50%' }}
              />
              <span className="block w-full text-center text-purple-600 text-[13px] font-bold tracking-widest uppercase py-1 border-t border-gray-100">
                See Beyond the Session
              </span>
            </>
          )}
        </div>

        <nav id="tour-sidebar-nav" className="flex-1 px-3 py-4 space-y-0.5 overflow-visible">
          {navItems.map((entry, i) => {
            if (entry.kind === 'header') {
              const isGrouped = entry.group === 'payments';
              const groupOpen = isGrouped ? paymentsOpen : true;
              if (isGrouped) {
                return (
                  <button
                    key={`header-${i}`}
                    onClick={() => setPaymentsOpen(o => !o)}
                    className="w-full flex items-center justify-between px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest"
                    style={{ color: brandColor ? `${sidebarTextColor}80` : '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    <span>{entry.label}</span>
                    <ChevronDownIcon
                      className="w-3 h-3 flex-shrink-0 transition-transform"
                      style={{ transform: groupOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                    />
                  </button>
                );
              }
              return (
                <p
                  key={`header-${i}`}
                  className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest"
                  style={{ color: brandColor ? `${sidebarTextColor}80` : undefined, ...(brandColor ? {} : { color: '#6b7280' }) }}
                >
                  {entry.label}
                </p>
              );
            }
            if (entry.group === 'payments' && !paymentsOpen) return null;
            const { key, label, Icon, onClick: onClickOverride, isActive: isActiveOverride } = entry;
            const isActive = isActiveOverride !== undefined ? isActiveOverride : activeSection === key;
            const isGroupedItem = entry.group === 'payments';
            return (
              <button
                key={`${key}-${label}`}
                id={key === 'clients' ? 'tour-clients-nav' : key === 'schedules' ? 'tour-schedules-nav' : undefined}
                onClick={onClickOverride ?? (() => setActiveSection(key))}
                className={`w-full flex items-center gap-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isGroupedItem ? 'pl-6 pr-3' : 'px-3'} ${
                  !brandColor
                    ? isActive
                      ? 'bg-dark-hover text-primary-300'
                      : 'text-gray-400 hover:bg-dark-hover hover:text-gray-200'
                    : ''
                }`}
                style={brandColor ? {
                  color: sidebarTextColor,
                  backgroundColor: isActive ? `${sidebarTextColor}22` : undefined,
                  opacity: isActive ? 1 : 0.75,
                } : undefined}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {label}
              </button>
            );
          })}
          <button
            id="tour-settings-nav"
            onClick={() => { setActiveSection('settings'); setSettingsSubSection(null); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              !brandColor
                ? activeSection === 'settings'
                  ? 'bg-dark-hover text-primary-300'
                  : 'text-gray-400 hover:bg-dark-hover hover:text-gray-200'
                : ''
            }`}
            style={brandColor ? {
              color: sidebarTextColor,
              backgroundColor: activeSection === 'settings' ? `${sidebarTextColor}22` : undefined,
              opacity: activeSection === 'settings' ? 1 : 0.75,
            } : undefined}
          >
            <Cog6ToothIcon className="w-4 h-4 flex-shrink-0" />
            Settings
          </button>
        </nav>

        <div
          className="px-3 py-4 border-t space-y-0.5"
          style={{ borderColor: sidebarTextColor ? `${sidebarTextColor}30` : undefined }}
        >
          <button
            id="tour-help-btn"
            onClick={() => navigate('/help')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${!brandColor ? 'text-gray-400 hover:bg-dark-hover hover:text-gray-200' : ''}`}
            style={brandColor ? { color: sidebarTextColor, opacity: 0.75 } : undefined}
          >
            <QuestionMarkCircleIcon className="w-4 h-4 flex-shrink-0" />
            Help
          </button>
          <button
            onClick={() => setShowSupportModal(true)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${!brandColor ? 'text-gray-400 hover:bg-dark-hover hover:text-gray-200' : ''}`}
            style={brandColor ? { color: sidebarTextColor, opacity: 0.75 } : undefined}
          >
            <QuestionMarkCircleIcon className="w-4 h-4 flex-shrink-0" />
            Support
          </button>
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${!brandColor ? 'text-gray-400 hover:bg-dark-hover hover:text-gray-200' : ''}`}
            style={brandColor ? { color: sidebarTextColor, opacity: 0.75 } : undefined}
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4 flex-shrink-0" />
            Logout
          </button>

          {/* Coach name button — opens slide-up account menu */}
          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={() => setAccountMenuOpen(v => !v)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${!brandColor ? 'text-gray-400 hover:bg-dark-hover hover:text-gray-200' : ''}`}
              style={brandColor ? { color: sidebarTextColor, opacity: 0.75 } : undefined}
            >
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-fuchsia-400 to-violet-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {coachInitials}
              </div>
              <span className="flex-1 text-left text-xs truncate" style={brandColor ? { color: sidebarTextColor } : undefined}>{coachName}</span>
              <ChevronDownIcon className={`w-3.5 h-3.5 flex-shrink-0 opacity-50 transition-transform duration-200 ${accountMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Slide-up account menu */}
            <div
              className={`absolute bottom-full left-0 mb-2 w-56 bg-dark-card border border-dark-border rounded-xl shadow-xl z-50 overflow-hidden transition-all duration-200 origin-bottom ${
                accountMenuOpen ? 'opacity-100 scale-y-100 translate-y-0' : 'opacity-0 scale-y-95 translate-y-1 pointer-events-none'
              }`}
            >
              <div className="px-4 py-3 border-b border-dark-border">
                <p className="text-xs font-semibold text-white truncate">{coachName}</p>
                {coachEmail && <p className="text-xs text-gray-500 truncate mt-0.5">{coachEmail}</p>}
              </div>
              <div className="py-1">
                {[
                  {
                    label: 'Edit Profile',
                    action: () => { setAccountMenuOpen(false); setActiveSection('settings'); setSettingsSubSection('profile'); },
                  },
                  {
                    label: 'Change Password',
                    action: () => { setAccountMenuOpen(false); setShowChangePasswordModal(true); },
                  },
                  {
                    label: 'My Subscription',
                    action: () => { setAccountMenuOpen(false); setShowMyAccountModal('subscription'); },
                  },
                  {
                    label: 'Payment History',
                    action: () => { setAccountMenuOpen(false); setShowMyAccountModal('payment-history'); },
                  },
                  {
                    label: 'Settings',
                    action: () => { setAccountMenuOpen(false); setActiveSection('settings'); },
                  },
                ].map(item => (
                  <button
                    key={item.label}
                    onClick={item.action}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-dark-hover hover:text-white transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="border-t border-dark-border py-1">
                <button
                  onClick={() => { setAccountMenuOpen(false); handleLogout(); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-gray-400 hover:text-red-400 hover:bg-dark-hover transition-colors flex items-center gap-2"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" />
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">


        {/* ── Section content ───────────────────────────────────────────── */}
        <div id="tour-dashboard-section" className="flex-1 overflow-y-auto">
          {activeSection === 'dashboard' && <DashboardSection />}
          {activeSection === 'clients'   && clientsSectionJsx}
          {activeSection === 'schedules' && schedulesSectionJsx}
          {activeSection === 'documents' && (
            <div className="p-8">
              <h1 className="text-2xl font-bold text-white mb-6">Documents</h1>
              <DocumentLibrary
                clients={clients.map(c => ({
                  relationship_id: c.relationship_id,
                  client_id: c.client_id,
                  client_name: c.client_name,
                  client_email: c.client_email,
                }))}
              />
            </div>
          )}
          {activeSection === 'services'     && (
            <div className="p-8 max-w-4xl space-y-8">
              <div>
                <h1 className="text-2xl font-bold text-white">Offerings</h1>
                {/* Sub-tab nav */}
                <div className="flex mt-4 border-b border-dark-border">
                  {/* Individual Sessions tab */}
                  <div className="flex items-center">
                    <button
                      onClick={() => setServicesSubTab('sessions')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                        servicesSubTab === 'sessions'
                          ? 'border-primary-500 text-white'
                          : 'border-transparent text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      Individual Sessions
                    </button>
                    <button
                      onClick={() => {
                        setSessDocsOpen(true);
                        if (!sessDocsMarkdown) {
                          const _tok = localStorage.getItem('authToken');
                          fetch(`${config.apiBaseUrl}/help/docs/coach/session-types`, {
                            headers: { Authorization: `Bearer ${_tok}` },
                          })
                            .then(r => r.json())
                            .then(d => setSessDocsMarkdown(d.content || ''))
                            .catch(() => setSessDocsMarkdown('Failed to load help content.'));
                        }
                      }}
                      className="mr-3 text-gray-500 hover:text-primary-400 transition-colors"
                    >
                      <InformationCircleIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Packages tab */}
                  <div className="flex items-center">
                    <button
                      onClick={() => setServicesSubTab('packages')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                        servicesSubTab === 'packages'
                          ? 'border-primary-500 text-white'
                          : 'border-transparent text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      Packages
                    </button>
                    <button
                      onClick={() => {
                        setPkgDocsOpen(true);
                        if (!pkgDocsMarkdown) {
                          const _tok = localStorage.getItem('authToken');
                          fetch(`${config.apiBaseUrl}/help/docs/coach/packages`, {
                            headers: { Authorization: `Bearer ${_tok}` },
                          })
                            .then(r => r.json())
                            .then(d => setPkgDocsMarkdown(d.content || ''))
                            .catch(() => setPkgDocsMarkdown('Failed to load help content.'));
                        }
                      }}
                      className="text-gray-500 hover:text-primary-400 transition-colors"
                    >
                      <InformationCircleIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* ── Individual Sessions ─────────────────────────────────── */}
              {servicesSubTab === 'sessions' && <section>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-base font-semibold text-white">Individual Sessions</h2>
                    <p className="text-xs text-gray-500 mt-0.5">Define the types of sessions you offer (used in packages and scheduling)</p>
                  </div>
                  <button
                    onClick={() => setGoogleEtForm({ name: '', duration_minutes: '60', description: '', billing_type: 'billable', default_price: '', default_currency: coachCurrency || 'USD' })}
                    className="text-sm px-3 py-1.5 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors"
                  >
                    + New Individual Session
                  </button>
                </div>

                {/* Require payment to book toggle */}
                <div className="flex items-center justify-between bg-dark-canvas border border-dark-border rounded-xl px-4 py-3 mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-200">Require payment to book</p>
                    <p className="text-xs text-gray-500 mt-0.5">When on, clients must pay at booking time for billable sessions that have a price set.</p>
                  </div>
                  <button
                    onClick={handleEnforcePaymentSessionsToggle}
                    disabled={enforcePaymentSessionsSaving}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-50 ${enforcePaymentSessions ? 'bg-primary-600' : 'bg-gray-600'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${enforcePaymentSessions ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>

                {googleEtLoading && <p className="text-sm text-gray-400">Loading…</p>}

                {googleEtForm && (
                  <div className="bg-dark-card border border-dark-border rounded-xl p-4 mb-4 space-y-3">
                    <h3 className="text-sm font-semibold text-white">New Individual Session</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="block text-xs text-gray-400 mb-1">Name *</label>
                        <input
                          ref={googleEtNameRef}
                          className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                          placeholder="e.g. 60-min Coaching Session"
                          value={googleEtForm.name}
                          onChange={e => setGoogleEtForm(f => f ? { ...f, name: e.target.value } : f)}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Duration (min) *</label>
                        <input
                          type="number" min="5"
                          className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                          value={googleEtForm.duration_minutes}
                          onChange={e => setGoogleEtForm(f => f ? { ...f, duration_minutes: e.target.value } : f)}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Session Type</label>
                        <select
                          className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                          value={googleEtForm.billing_type}
                          onChange={e => setGoogleEtForm(f => f ? { ...f, billing_type: e.target.value, default_price: e.target.value !== 'billable' ? '' : f.default_price } : f)}
                        >
                          <option value="billable">Billable</option>
                          <option value="complimentary">Complimentary</option>
                          <option value="pro_bono">Pro Bono</option>
                          <option value="barter">Barter</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-gray-400 mb-1">Description</label>
                        <input
                          className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                          placeholder="Optional"
                          value={googleEtForm.description}
                          onChange={e => setGoogleEtForm(f => f ? { ...f, description: e.target.value } : f)}
                        />
                      </div>
                      {googleEtForm.billing_type === 'billable' && (<>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Default Price</label>
                        <input
                          type="number" min="0" step="0.01"
                          className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                          placeholder="e.g. 150.00"
                          value={googleEtForm.default_price}
                          onChange={e => setGoogleEtForm(f => f ? { ...f, default_price: e.target.value } : f)}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Currency</label>
                        <select
                          className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                          value={googleEtForm.default_currency}
                          onChange={e => setGoogleEtForm(f => f ? { ...f, default_currency: e.target.value } : f)}
                        >
                          {['USD','CAD','GBP','EUR','AUD','NZD'].map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      </>)}
                    </div>
                    {googleEtError && <p className="text-xs text-red-400">{googleEtError}</p>}
                    <div className="flex gap-2">
                      <button
                        onClick={handleGoogleEtCreate}
                        disabled={googleEtSaving || !googleEtForm.name.trim()}
                        className="px-4 py-1.5 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        {googleEtSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        onClick={() => { setGoogleEtForm(null); setGoogleEtError(''); }}
                        className="px-4 py-1.5 border border-dark-border text-gray-300 hover:text-white text-sm rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {!googleEtLoading && googleEtList.length === 0 && !googleEtForm && (
                  <div className="bg-dark-card border border-dark-border rounded-xl p-6 text-center">
                    <p className="text-sm text-gray-400">No individual sessions yet. Add your first one above.</p>
                  </div>
                )}

                <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
                  {googleEtList.map(et => (
                    <div key={et.id} className="bg-dark-card border border-dark-border rounded-xl p-4 flex flex-col min-h-[160px]">
                      {googleEtEditId === et.id ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-400 mb-1">Name *</label>
                              <input
                                className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                                value={googleEtEditForm.name}
                                onChange={e => setGoogleEtEditForm(f => ({ ...f, name: e.target.value }))}
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-400 mb-1">Session Type</label>
                              <select
                                className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                                value={googleEtEditForm.billing_type}
                                onChange={e => setGoogleEtEditForm(f => ({ ...f, billing_type: e.target.value, default_price: e.target.value !== 'billable' ? '' : f.default_price }))}
                              >
                                <option value="billable">Billable</option>
                                <option value="complimentary">Complimentary</option>
                                <option value="pro_bono">Pro Bono</option>
                                <option value="barter">Barter</option>
                              </select>
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-400 mb-1">Description</label>
                              <input
                                className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                                placeholder="Optional"
                                value={googleEtEditForm.description}
                                onChange={e => setGoogleEtEditForm(f => ({ ...f, description: e.target.value }))}
                              />
                            </div>
                            {googleEtEditForm.billing_type === 'billable' && (<>
                            <div>
                              <label className="block text-xs text-gray-400 mb-1">Default Price</label>
                              <input
                                type="number" min="0" step="0.01"
                                className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-primary-600"
                                placeholder="e.g. 150.00"
                                value={googleEtEditForm.default_price}
                                onChange={e => setGoogleEtEditForm(f => ({ ...f, default_price: e.target.value }))}
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-400 mb-1">Currency</label>
                              <select
                                className="w-full bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-primary-600"
                                value={googleEtEditForm.default_currency}
                                onChange={e => setGoogleEtEditForm(f => ({ ...f, default_currency: e.target.value }))}
                              >
                                {['USD','CAD','GBP','EUR','AUD','NZD'].map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </div>
                            </>)}
                          </div>
                          {googleEtEditError && <p className="text-xs text-red-400">{googleEtEditError}</p>}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleGoogleEtUpdate(et.id)}
                              disabled={googleEtEditSaving || !googleEtEditForm.name.trim()}
                              className="px-4 py-1.5 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                            >
                              {googleEtEditSaving ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              onClick={() => { setGoogleEtEditId(null); setGoogleEtEditError(''); }}
                              className="px-4 py-1.5 border border-dark-border text-gray-300 hover:text-white text-sm rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col h-full gap-3">
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-1 mb-1">
                              <p className="text-sm font-semibold text-white leading-snug">{et.name}</p>
                              {et.is_active
                                ? <span className="text-[10px] bg-green-900/40 text-green-400 px-1.5 py-0.5 rounded-full flex-shrink-0">Active</span>
                                : <span className="text-[10px] bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded-full flex-shrink-0">Inactive</span>
                              }
                            </div>
                            <p className="text-sm font-medium text-primary-300">
                              {et.duration_minutes} min
                              {et.default_price_cents ? ` · ${new Intl.NumberFormat('en-US', { style: 'currency', currency: (et.default_currency || 'USD').toUpperCase() }).format(et.default_price_cents / 100)}` : ''}
                            </p>
                            {et.description && <p className="text-[11px] text-gray-500 mt-1 italic line-clamp-2">{et.description}</p>}
                            <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full mt-2 ${
                              et.allow_external_booking ? 'bg-blue-900/40 text-blue-400' : 'bg-dark-hover text-gray-500 border border-dark-border'
                            }`}>
                              {et.allow_external_booking ? 'External booking on' : 'External booking off'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5 pt-2 border-t border-dark-border">
                            <button
                              onClick={() => handleGoogleEtToggleDirect(et.id, et.allow_external_booking)}
                              disabled={googleEtExternalSaving[et.id]}
                              className={`text-[11px] border px-2 py-1 rounded-lg transition-colors ${
                                et.allow_external_booking
                                  ? 'text-blue-400 hover:text-blue-300 border-blue-800'
                                  : 'text-gray-400 hover:text-white border-dark-border'
                              }`}
                            >
                              {googleEtExternalSaving[et.id] ? 'Saving…' : et.allow_external_booking ? 'Disable Ext. Booking' : 'Enable Ext. Booking'}
                            </button>
                            <button
                              onClick={() => { setGoogleEtEditId(et.id); setGoogleEtEditForm({ name: et.name, description: et.description ?? '', billing_type: et.billing_type || 'billable', default_price: et.default_price_cents ? String(et.default_price_cents / 100) : '', default_currency: et.default_currency || coachCurrency || 'USD' }); setGoogleEtEditError(''); }}
                              className="text-[11px] text-gray-400 hover:text-white border border-dark-border px-2 py-1 rounded-lg transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleGoogleEtDelete(et.id)}
                              className="text-[11px] text-red-400 hover:text-red-300 border border-dark-border px-2 py-1 rounded-lg transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Booking URL */}
                <div className="mt-4 bg-dark-canvas border border-dark-border rounded-xl px-4 py-3">
                  <p className="text-xs font-medium text-gray-400 mb-1">Your Booking Page</p>
                  {googleEtList.some(et => et.allow_external_booking && et.is_active) ? (
                    <>
                      <p className="text-xs text-gray-500 mb-2">Share this link so clients can book directly. Only session types with <strong className="text-gray-400">External booking</strong> enabled appear.</p>
                      <div className="flex items-center gap-2 bg-dark-card border border-dark-border rounded-lg px-3 py-2">
                        <span className="flex-1 text-xs text-gray-300 font-mono truncate">
                          {window.location.origin}/book/{coachId}
                        </span>
                        <button
                          onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/book/${coachId}`); }}
                          className="text-xs text-primary-400 hover:text-primary-300 flex-shrink-0"
                        >
                          Copy
                        </button>
                        <a
                          href={`/book/${coachId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-gray-400 hover:text-gray-200 flex-shrink-0"
                        >
                          Preview ↗
                        </a>
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-yellow-400 bg-yellow-900/20 border border-yellow-700/50 rounded-lg px-3 py-2">
                      Enable <strong>External booking</strong> on at least one session type above to activate your booking page.
                    </p>
                  )}
                </div>
              </section>}

              {/* ── Packages ────────────────────────────────────────────── */}
              {servicesSubTab === 'packages' && <section>
                <CoachPackagesPanel coachId={coachId} eventTypes={googleEtList} clients={clients} defaultCurrency={coachCurrency} enforcePaymentPackages={enforcePaymentPackages} onGoToPaymentSettings={() => { setActiveSection('settings'); setSettingsSubSection('payments'); }} />
                {/* Booking URL */}
                <div className="mt-4 bg-dark-canvas border border-dark-border rounded-xl px-4 py-3">
                  <p className="text-xs font-medium text-gray-400 mb-1">Your Booking Page</p>
                  <p className="text-xs text-gray-500 mb-2">Clients use this same link to browse and enroll in your packages.</p>
                  <div className="flex items-center gap-2 bg-dark-card border border-dark-border rounded-lg px-3 py-2">
                    <span className="flex-1 text-xs text-gray-300 font-mono truncate">
                      {window.location.origin}/book/{coachId}
                    </span>
                    <button
                      onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/book/${coachId}`); }}
                      className="text-xs text-primary-400 hover:text-primary-300 flex-shrink-0"
                    >
                      Copy
                    </button>
                    <a
                      href={`/book/${coachId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-gray-400 hover:text-gray-200 flex-shrink-0"
                    >
                      Preview ↗
                    </a>
                  </div>
                </div>
              </section>}
            </div>
          )}

          {/* ── Individual Sessions help modal ───────────────────────────── */}
          {sessDocsOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setSessDocsOpen(false)}>
              <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-2xl max-h-[82vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <InformationCircleIcon className="w-5 h-5 text-primary-400" />
                    <h3 className="font-semibold text-white">Individual Sessions — Help</h3>
                  </div>
                  <button onClick={() => setSessDocsOpen(false)} className="text-dark-muted hover:text-white transition-colors">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                <div className="overflow-y-auto px-6 py-5 flex-1">
                  {sessDocsMarkdown === null ? (
                    <p className="text-sm text-dark-muted">Loading…</p>
                  ) : (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: ({children}) => <h1 className="text-lg font-bold text-white mb-3 mt-1">{children}</h1>,
                        h2: ({children}) => <h2 className="text-base font-semibold text-white mt-6 mb-2 pb-1 border-b border-dark-border">{children}</h2>,
                        h3: ({children}) => <h3 className="text-sm font-semibold text-gray-200 mt-4 mb-1.5">{children}</h3>,
                        p: ({children}) => <p className="text-sm text-gray-300 mb-2.5 leading-relaxed">{children}</p>,
                        ul: ({children}) => <ul className="mb-2.5 space-y-1">{children}</ul>,
                        ol: ({children}) => <ol className="mb-2.5 space-y-1 list-decimal ml-4">{children}</ol>,
                        li: ({children}) => <li className="text-sm text-gray-300 ml-4 list-disc">{children}</li>,
                        strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
                        hr: () => <hr className="border-dark-border my-4" />,
                        table: ({children}) => <div className="overflow-x-auto my-3"><table className="w-full text-sm border-collapse">{children}</table></div>,
                        thead: ({children}) => <thead className="bg-dark-hover">{children}</thead>,
                        th: ({children}) => <th className="text-left text-gray-400 font-medium border border-dark-border px-3 py-1.5">{children}</th>,
                        td: ({children}) => <td className="text-gray-300 border border-dark-border px-3 py-1.5">{children}</td>,
                        code: ({children}) => <code className="text-primary-400 bg-dark-hover px-1 rounded text-xs">{children}</code>,
                      }}
                    >
                      {sessDocsMarkdown}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Packages help modal ───────────────────────────────────────── */}
          {pkgDocsOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setPkgDocsOpen(false)}>
              <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-2xl max-h-[82vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <InformationCircleIcon className="w-5 h-5 text-primary-400" />
                    <h3 className="font-semibold text-white">Coaching Packages — Help</h3>
                  </div>
                  <button onClick={() => setPkgDocsOpen(false)} className="text-dark-muted hover:text-white transition-colors">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                <div className="overflow-y-auto px-6 py-5 flex-1">
                  {pkgDocsMarkdown === null ? (
                    <p className="text-sm text-dark-muted">Loading…</p>
                  ) : (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: ({children}) => <h1 className="text-lg font-bold text-white mb-3 mt-1">{children}</h1>,
                        h2: ({children}) => <h2 className="text-base font-semibold text-white mt-6 mb-2 pb-1 border-b border-dark-border">{children}</h2>,
                        h3: ({children}) => <h3 className="text-sm font-semibold text-gray-200 mt-4 mb-1.5">{children}</h3>,
                        p: ({children}) => <p className="text-sm text-gray-300 mb-2.5 leading-relaxed">{children}</p>,
                        ul: ({children}) => <ul className="mb-2.5 space-y-1">{children}</ul>,
                        ol: ({children}) => <ol className="mb-2.5 space-y-1 list-decimal ml-4">{children}</ol>,
                        li: ({children}) => <li className="text-sm text-gray-300 ml-4 list-disc">{children}</li>,
                        strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
                        hr: () => <hr className="border-dark-border my-4" />,
                        table: ({children}) => <div className="overflow-x-auto my-3"><table className="w-full text-sm border-collapse">{children}</table></div>,
                        thead: ({children}) => <thead className="bg-dark-hover">{children}</thead>,
                        th: ({children}) => <th className="text-left text-gray-400 font-medium border border-dark-border px-3 py-1.5">{children}</th>,
                        td: ({children}) => <td className="text-gray-300 border border-dark-border px-3 py-1.5">{children}</td>,
                        code: ({children}) => <code className="text-primary-400 bg-dark-hover px-1 rounded text-xs">{children}</code>,
                      }}
                    >
                      {pkgDocsMarkdown}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            </div>
          )}
          {activeSection === 'forms'        && <FormsLibrarySection />}
          {activeSection === 'marketing'    && <MarketingSection />}
          {activeSection === 'settings'     && SettingsSection()}
          {activeSection === 'reports'      && ReportsSection()}
          {activeSection === 'invoices'     && (
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <h1 className="text-2xl font-bold text-white">Invoices</h1>
              <InvoicesTab clients={clients.map(c => ({ client_id: c.client_id, client_name: c.client_name }))} />
            </div>
          )}
          {activeSection === 'cashflow'     && (
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <h1 className="text-2xl font-bold text-white">Cashflow</h1>
              <CashflowTab clients={clients.map(c => ({ client_id: c.client_id, client_name: c.client_name }))} />
            </div>
          )}
          {activeSection === 'test-summary' && (
            <TestSummaryPage
              coachId={coachId}
              authToken={localStorage.getItem('authToken') || ''}
            />
          )}
        </div>
      </main>

      {/* ── Modals ── */}

      {/* Send Welcome Packet modal — kept at top level so DashboardSection re-renders don't unmount it */}
      {pipelineSendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-semibold text-gray-900">Send Welcome Packet to {pipelineSendModal.client_name}</h3>
              <button onClick={() => setPipelineSendModal(null)} className="p-1 text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Packet title</label>
                <input
                  type="text"
                  value={pipelinePacketTitle}
                  onChange={(e) => setPipelinePacketTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select documents</label>
                {pipelineDocs.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No documents in library. Upload documents in the Documents section first.</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {pipelineDocs.map((doc) => (
                      <label key={doc.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pipelineSelectedDocIds.has(doc.id)}
                          onChange={() => setPipelineSelectedDocIds(prev => {
                            const next = new Set(prev);
                            next.has(doc.id) ? next.delete(doc.id) : next.add(doc.id);
                            return next;
                          })}
                          className="rounded border-gray-300 text-primary-600"
                        />
                        <span className="text-sm text-gray-800">{doc.name}</span>
                        {doc.is_template && <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded ml-auto">Template</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <PipelineTagFields
                ref={pipelineTagFieldsRef}
                tags={pipelineCustomTags}
                initialValues={Object.fromEntries(pipelineCustomTags.map(t => [t.key, t.default_value]))}
              />
              {pipelineSendError && <p className="text-red-600 text-sm">{pipelineSendError}</p>}
              {pipelineSendSuccess && <p className="text-green-600 text-sm">{pipelineSendSuccess}</p>}
              <div className="flex gap-3">
                <button
                  onClick={() => setPipelineSendModal(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >Cancel</button>
                <button
                  disabled={pipelineSending || pipelineSelectedDocIds.size === 0}
                  onClick={async () => {
                    if (!pipelineSendModal || !pipelinePacketTitle.trim() || pipelineSelectedDocIds.size === 0) return;
                    setPipelineSending(true);
                    setPipelineSendError('');
                    try {
                      const allTagValues = pipelineTagFieldsRef.current?.getValues() ?? {};
                      const nonEmptyTagValues = Object.fromEntries(Object.entries(allTagValues).filter(([, v]) => v.trim() !== ''));
                      await createPacket(
                        String(pipelineSendModal.coach_client_id),
                        pipelinePacketTitle.trim(),
                        Array.from(pipelineSelectedDocIds),
                        undefined,
                        Object.keys(nonEmptyTagValues).length > 0 ? nonEmptyTagValues : undefined,
                      );
                      setPipelineSendSuccess('Welcome Packet sent!');
                      await fetchOnboardingPipeline();
                      // Mark "Send agreement" action item complete if it exists for this client
                      const relId = String(pipelineSendModal.coach_client_id);
                      const pendingTask = coachPendingTasksByRelId[relId];
                      if (pendingTask) {
                        fetch(`${config.apiBaseUrl}/action-items/${pendingTask.id}`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken') ?? ''}` },
                          body: JSON.stringify({ status: 'completed' }),
                        }).then(() => {
                          setCoachPendingTasksByRelId(prev => {
                            const next = { ...prev };
                            delete next[relId];
                            return next;
                          });
                        }).catch(() => {});
                      }
                      setTimeout(() => setPipelineSendModal(null), 1500);
                    } catch (e: any) {
                      setPipelineSendError(e.message || 'Send failed');
                    } finally {
                      setPipelineSending(false);
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                >
                  {pipelineSending ? 'Sending…' : 'Send Welcome Packet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client detail panel — overlay only when not on the clients section (split-view handles it there) */}
      <AnimatePresence>
        {detailClient && activeSection !== 'clients' && (
          <ClientDetailPanel
            client={detailClient}
            coachId={coachId}
            onClose={() => setDetailClient(null)}
            onDeleted={() => { setDetailClient(null); fetchClients(); }}
            onSchedule={() => {
              const c = clients.find(x => x.client_id === detailClient.client_id) || {
                relationship_id: '',
                client_id: detailClient.client_id,
                client_name: detailClient.name,
                client_email: detailClient.email,
                start_date: detailClient.start_date || '',
                is_active: detailClient.status === 'active',
                name: detailClient.name,
                email: detailClient.email,
                status: detailClient.status,
              };
              setDetailClient(null);
              openSchedule(c);
            }}
            apptPaymentStatuses={apptPaymentStatuses}
            onProcessPayment={(appointmentId) => {
              const session = upcomingSessions.find(s => s.appointmentId === appointmentId) ?? {
                id: appointmentId,
                appointmentId,
                clientId: detailClient.client_id,
                clientName: detailClient.name,
                meetingName: detailClient.name,
                date: '', time: '', duration: 60,
                scheduledAt: '', type: 'virtual' as const, status: 'confirmed' as const,
              };
              setDetailClient(null);
              openPaymentPanel(session);
            }}
          />
        )}
      </AnimatePresence>

      {/* Session Summary */}
      {summarySession?.appointmentId && (
        <SessionSummaryPanel
          appointmentId={summarySession.appointmentId}
          meetingName={summarySession.clientName}
          role="coach"
          clientName={summarySession.clientName}
          coachClientId={summarySession.relationshipId}
          appointmentStatus={summarySession.status}
          appointmentType={summarySession.appointmentType}
          barterNote={summarySession.barterNote}
          onClose={() => setSummarySession(null)}
        />
      )}


      {/* Upload Transcript */}
      {uploadSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeUploadDialog} />
          <div className="relative z-10 w-full max-w-sm bg-dark-card rounded-xl shadow-xl border border-dark-border">
            <div className="flex items-center justify-between px-5 py-4 border-b border-dark-border">
              <h3 className="font-semibold text-white">Upload Transcript</h3>
              <button onClick={closeUploadDialog} className="text-dark-muted hover:text-gray-300"><XMarkIcon className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-dark-muted">{uploadSession.meetingName || uploadSession.clientName}</p>

              {/* File picker */}
              <label className={`block w-full cursor-pointer border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                uploadFile ? 'border-primary-500 bg-primary-900/10' : 'border-dark-border hover:border-primary-500'
              }`}>
                {uploadFile ? (
                  <>
                    <span className="block text-sm text-primary-400 font-medium mb-1">{uploadFile.name}</span>
                    <span className="block text-xs text-dark-muted">Click to choose a different file</span>
                  </>
                ) : (
                  <>
                    <span className="block text-sm text-gray-300 mb-1">Click to select a transcript file</span>
                    <span className="block text-xs text-dark-muted">.txt, .md, or plain text</span>
                  </>
                )}
                <input
                  type="file"
                  accept=".txt,.md,text/plain"
                  className="sr-only"
                  onChange={e => {
                    setUploadFile(e.target.files?.[0] ?? null);
                    setUploadConfirming(false);
                    setUploadError('');
                  }}
                />
              </label>

              {/* Overwrite warning */}
              {uploadConfirming && (
                <div className="rounded-lg border border-amber-600/40 bg-amber-900/20 px-4 py-3 text-sm text-amber-300">
                  <p className="font-medium mb-1">Overwrite existing summary?</p>
                  <p className="text-xs text-amber-400/80">Any existing AI summary and notes for this session will be replaced. This cannot be undone.</p>
                </div>
              )}

              {uploadError && <p className="text-xs text-red-400">{uploadError}</p>}
              {uploadLoading && <p className="text-xs text-primary-400 animate-pulse">Generating summary…</p>}
            </div>

            <div className="px-5 py-4 border-t border-dark-border bg-dark-canvas flex items-center justify-end gap-3">
              <button onClick={closeUploadDialog} className="px-4 py-2 text-sm border border-dark-border rounded-lg text-gray-200 hover:bg-dark-hover">
                Cancel
              </button>
              {!uploadConfirming ? (
                <button
                  disabled={!uploadFile || uploadLoading}
                  onClick={() => setUploadConfirming(true)}
                  className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-40 transition-colors"
                >
                  Generate Summary
                </button>
              ) : (
                <button
                  disabled={uploadLoading}
                  onClick={() => uploadFile && handleTranscriptUpload(uploadFile)}
                  className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-lg disabled:opacity-40 transition-colors"
                >
                  Yes, overwrite
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Video provider selection modal — shown after Google Calendar OAuth */}
      {showVideoProviderModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60">
          <div className="bg-dark-card border border-dark-border rounded-2xl p-6 w-96 shadow-xl space-y-4">
            <h3 className="text-white font-semibold text-base">Which video tool do you use?</h3>
            <p className="text-sm text-gray-400">Choose how session video links are generated when you schedule with clients.</p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  const tok = localStorage.getItem('authToken') || '';
                  fetch(`${config.apiBaseUrl}/coaches/me/video-provider`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
                    body: JSON.stringify({ video_provider: 'google_meet' }),
                  }).catch(() => {});
                  setShowVideoProviderModal(false);
                }}
                className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-dark-border hover:border-primary-500 hover:bg-dark-hover transition-colors"
              >
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                <div>
                  <p className="text-sm font-medium text-white">Google Meet</p>
                  <p className="text-xs text-gray-400">Video links created automatically in Google Calendar</p>
                </div>
              </button>
              <button
                onClick={() => { setShowVideoProviderModal(false); handleZoomConnect(); }}
                className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-dark-border hover:border-primary-500 hover:bg-dark-hover transition-colors"
              >
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="4" fill="#2D8CFF"/><path d="M4 8.5C4 7.67 4.67 7 5.5 7h8C14.33 7 15 7.67 15 8.5v7c0 .83-.67 1.5-1.5 1.5h-8C4.67 17 4 16.33 4 15.5v-7zM15.5 10.25l3.8-2.53A.75.75 0 0120 8.37v7.26a.75.75 0 01-1.2.6L15.5 13.5v-3.25z" fill="white"/></svg>
                <div>
                  <p className="text-sm font-medium text-white">Zoom</p>
                  <p className="text-xs text-gray-400">Connect your Zoom account next — Zoom links auto-created at booking</p>
                </div>
              </button>
            </div>
            <p className="text-xs text-gray-500">You can change this later in Settings → Scheduling.</p>
          </div>
        </div>
      )}

      {/* Zoom disconnect confirmation */}
      {showZoomDisconnectConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60">
          <div className="bg-dark-card border border-dark-border rounded-2xl p-6 w-80 shadow-xl space-y-4">
            <h3 className="text-white font-semibold text-sm">Disconnect Zoom?</h3>
            <p className="text-xs text-gray-400">Future sessions will use Google Meet for video links. Your existing sessions are unaffected.</p>
            <div className="flex gap-3">
              <button onClick={handleZoomDisconnect} className="flex-1 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors">Disconnect</button>
              <button onClick={() => setShowZoomDisconnectConfirm(false)} className="flex-1 py-2 text-sm border border-dark-border text-gray-300 hover:text-white rounded-lg transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Meeting link banner */}
      {createdMeetLink && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-green-900/40 border border-green-800 rounded-lg px-4 py-3 shadow-md">
          <span className="text-sm text-green-300 font-medium">Session created!</span>
          <a href={createdMeetLink} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 underline truncate max-w-xs">{createdMeetLink}</a>
          <button onClick={() => setCreatedMeetLink(null)} className="text-green-500 hover:text-green-700"><XMarkIcon className="w-5 h-5" /></button>
        </div>
      )}

      {/* Social Media Post Modal */}
      <SocialMediaPostModal
        open={socialPostModalOpen}
        onClose={() => setSocialPostModalOpen(false)}
        socialStatuses={socialStatuses}
      />

      {/* ── Calendar overlays (used by Schedules calendar AND Schedule Session modal) ── */}

      {/* Cancel confirmation dialog (non-recurring sessions) */}
      {confirmCancelId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60" onClick={() => setConfirmCancelId(null)}>
          <div className="bg-dark-card border border-dark-border rounded-2xl p-6 w-80 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-semibold text-sm mb-1">Cancel session?</h3>
            <p className="text-xs text-gray-400 mb-5">This will mark the session as cancelled. This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmCancelId(null)}
                className="flex-1 text-sm px-4 py-2 rounded-lg border border-dark-border text-gray-300 hover:bg-dark-hover transition-colors"
              >
                Keep session
              </button>
              <button
                onClick={() => handleCancelAppointment(confirmCancelId)}
                disabled={cancellingAppointmentId === confirmCancelId}
                className="flex-1 text-sm px-4 py-2 rounded-lg bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white font-medium transition-colors"
              >
                {cancellingAppointmentId === confirmCancelId ? 'Cancelling…' : 'Yes, cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel scope picker (recurring series sessions) */}
      {cancelScopeSession && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60" onClick={() => setCancelScopeSession(null)}>
          <div className="bg-dark-card border border-dark-border rounded-2xl p-6 w-80 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-semibold text-sm mb-1">Cancel recurring session?</h3>
            <p className="text-xs text-gray-400 mb-5">
              {cancelScopeSession.meetingName || 'Session'} with {cancelScopeSession.clientName}
              {' · '}{cancelScopeSession.date} at {cancelScopeSession.time}
            </p>
            <div className="space-y-2.5">
              <button
                onClick={() => handleCancelWithScope(cancelScopeSession, 'this_only')}
                disabled={!!cancellingAppointmentId}
                className="w-full text-left px-4 py-3 rounded-lg border border-dark-border hover:bg-dark-hover transition-colors disabled:opacity-50"
              >
                <p className="text-sm text-white font-medium">This session only</p>
                <p className="text-xs text-gray-400 mt-0.5">Cancel just this occurrence</p>
              </button>
              <button
                onClick={() => handleCancelWithScope(cancelScopeSession, 'this_and_future')}
                disabled={!!cancellingAppointmentId}
                className="w-full text-left px-4 py-3 rounded-lg border border-dark-border hover:bg-dark-hover transition-colors disabled:opacity-50"
              >
                <p className="text-sm text-white font-medium">This and future sessions</p>
                <p className="text-xs text-gray-400 mt-0.5">Cancel this session and all upcoming ones in the series</p>
              </button>
              <button
                onClick={() => handleCancelWithScope(cancelScopeSession, 'all')}
                disabled={!!cancellingAppointmentId}
                className="w-full text-left px-4 py-3 rounded-lg border border-red-900/40 hover:bg-red-900/20 transition-colors disabled:opacity-50"
              >
                <p className="text-sm text-red-400 font-medium">All sessions in series</p>
                <p className="text-xs text-gray-400 mt-0.5">Cancel every session in this recurring series</p>
              </button>
              <button
                onClick={() => setCancelScopeSession(null)}
                className="w-full text-sm px-4 py-2 rounded-lg text-gray-400 hover:text-gray-200 transition-colors"
              >
                Keep as is
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel / reschedule chooser (combined session-row icon) */}
      {manageSession && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60" onClick={() => setManageSession(null)}>
          <div className="bg-dark-card border border-dark-border rounded-2xl p-6 w-80 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-semibold text-sm mb-1">Manage session</h3>
            <p className="text-xs text-gray-400 mb-5">
              {manageSession.meetingName || 'Session'} with {manageSession.clientName}
              {' · '}{manageSession.date} at {manageSession.time}
            </p>
            <div className="space-y-2.5">
              {manageSession.recurringSeriesId ? (
                <>
                  <p className="text-xs text-gray-500 pb-0.5">This is part of a recurring series.</p>
                  <button
                    onClick={() => { const s = manageSession; setManageSession(null); openReschedule(s, 'this_only'); }}
                    className="w-full flex items-center justify-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white font-medium transition-colors"
                  >
                    <ArrowPathIcon className="w-4 h-4" />
                    Reschedule this session only
                  </button>
                  <button
                    onClick={() => { const s = manageSession; setManageSession(null); openReschedule(s, 'this_and_future'); }}
                    className="w-full flex items-center justify-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-primary-700 hover:bg-primary-600 text-white font-medium transition-colors"
                  >
                    <ArrowPathIcon className="w-4 h-4" />
                    Reschedule this and all future sessions
                  </button>
                </>
              ) : (
                <button
                  onClick={() => { const s = manageSession; setManageSession(null); openReschedule(s); }}
                  className="w-full flex items-center justify-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white font-medium transition-colors"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  Reschedule Session
                </button>
              )}
              <button
                onClick={() => { const s = manageSession; setManageSession(null); handleRequestCancel(s); }}
                className="w-full flex items-center justify-center gap-2 text-sm px-4 py-2.5 rounded-lg border border-red-800 text-red-400 hover:bg-red-900/20 font-medium transition-colors"
              >
                <XCircleIcon className="w-4 h-4" />
                Cancel Session
              </button>
              <button
                onClick={() => setManageSession(null)}
                className="w-full text-sm px-4 py-2 rounded-lg text-gray-400 hover:text-gray-200 transition-colors"
              >
                Keep as is
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Right-click context menu */}
      {calendarContextMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setCalendarContextMenu(null)} />
          <div
            className="fixed z-50 bg-dark-card border border-dark-border rounded-xl shadow-xl py-1 w-44"
            style={{ left: calendarContextMenu.x, top: calendarContextMenu.y }}
          >
            <button
              onClick={() => { setCalendarEventPopover(calendarContextMenu.event); setCalendarContextMenu(null); }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-200 hover:bg-dark-hover transition-colors"
            >
              <InformationCircleIcon className="w-4 h-4 text-gray-400" />
              View details
            </button>
            {calendarContextMenu.event.resource?.appointmentId && calendarContextMenu.event.resource?.type === 'appointment' && (
              <>
                <div className="my-1 border-t border-dark-border" />
                <button
                  onClick={() => {
                    const apptId = calendarContextMenu.event.resource!.appointmentId!;
                    const s = upcomingSessions.find(x => x.appointmentId === apptId);
                    setCalendarContextMenu(null);
                    if (s) openReschedule(s);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-amber-400 hover:bg-dark-hover transition-colors"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  Reschedule session
                </button>
                <button
                  onClick={() => {
                    const appt = calendarContextMenu.event.resource!;
                    const s = upcomingSessions.find(x => x.appointmentId === appt.appointmentId);
                    if (s) { setCalendarContextMenu(null); handleRequestCancel(s); }
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-dark-hover transition-colors"
                >
                  <XCircleIcon className="w-4 h-4" />
                  Cancel session
                </button>
              </>
            )}
          </div>
        </>
      )}

      {/* Session detail modal — calendar appointment click */}
      {sessionDetailSession && (
        <SessionDetailModal
          session={sessionDetailSession}
          paymentStatus={apptPaymentStatuses[sessionDetailSession.appointmentId!] ?? null}
          onClose={() => setSessionDetailSession(null)}
          onRefresh={() => { fetchSessions(); }}
          onOpenStripePayment={() => openPaymentPanel(sessionDetailSession)}
          onCancelSession={() => { handleRequestCancel(sessionDetailSession); }}
          onJoinCall={() => openJoinWindow(buildJoinUrl(sessionDetailSession.googleMeetLink!, sessionDetailSession.appointmentId, sessionDetailSession.clientName || sessionDetailSession.meetingName))}
          coachCurrency={coachCurrency}
        />
      )}

      {/* Event detail popover (gcal / non-appointment events only) */}
      {calendarEventPopover && (() => {
        const popoverApptId = calendarEventPopover.resource?.appointmentId;
        const popoverPs = popoverApptId ? apptPaymentStatuses[popoverApptId] : undefined;
        const popoverPayStatus = popoverPs?.status;
        const popoverSession = popoverApptId
          ? upcomingSessions.find(s => s.appointmentId === popoverApptId) ?? null
          : null;
        return (
          <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/50" onClick={() => setCalendarEventPopover(null)}>
            <div className="bg-dark-card border border-dark-border rounded-2xl shadow-xl w-80" onClick={e => e.stopPropagation()}>
              {/* Header bar */}
              <div className={`rounded-t-2xl px-5 py-3 flex items-center justify-between ${
                calendarEventPopover.resource?.status === 'completed' ? 'bg-green-800/60' :
                calendarEventPopover.resource?.status === 'cancelled' ? 'bg-gray-700/60' :
                calendarEventPopover.resource?.type === 'gcal' ? 'bg-sky-700/60' :
                'bg-primary-700/60'
              }`}>
                <p className="text-white font-semibold text-sm truncate pr-2">{calendarEventPopover.title}</p>
                <button onClick={() => setCalendarEventPopover(null)} className="text-gray-300 hover:text-white flex-shrink-0">
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="p-5 space-y-3">
                {/* Date / time / duration */}
                <div className="flex items-start gap-3">
                  <CalendarDaysIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-200">
                      {calendarEventPopover.start.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {calendarEventPopover.start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      {' – '}
                      {calendarEventPopover.end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      {' · '}
                      {Math.round((calendarEventPopover.end.getTime() - calendarEventPopover.start.getTime()) / 60000)} min
                    </p>
                  </div>
                </div>
                {/* Client */}
                {calendarEventPopover.resource?.clientName && (
                  <div className="flex items-center gap-3">
                    <UserCircleIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-200">{calendarEventPopover.resource.clientName}</span>
                  </div>
                )}
                {/* Status */}
                {calendarEventPopover.resource?.type === 'appointment' && (
                  <div className="flex items-center gap-3">
                    <span className="w-4 h-4 flex-shrink-0" />
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                      calendarEventPopover.resource?.status === 'completed' ? 'bg-green-900/40 text-green-300' :
                      calendarEventPopover.resource?.status === 'cancelled' ? 'bg-gray-800 text-gray-400' :
                      calendarEventPopover.resource?.status === 'pending' ? 'bg-amber-900/40 text-amber-300' :
                      'bg-primary-900/40 text-primary-300'
                    }`}>
                      {calendarEventPopover.resource?.status || 'scheduled'}
                    </span>
                    {popoverPayStatus === 'unpaid' && <span className="text-xs font-medium text-red-400 bg-red-900/30 border border-red-800 px-2 py-0.5 rounded-full">Unpaid</span>}
                    {popoverPayStatus === 'succeeded' && <span className="text-xs font-medium text-green-400 bg-green-900/30 border border-green-800 px-2 py-0.5 rounded-full">Paid</span>}
                    {popoverPayStatus === 'pending' && <span className="text-xs font-medium text-yellow-400 bg-yellow-900/30 border border-yellow-800 px-2 py-0.5 rounded-full">Pending</span>}
                    {popoverPayStatus === 'refunded' && <span className="text-xs font-medium text-gray-400 bg-gray-800 border border-gray-600 px-2 py-0.5 rounded-full">Refunded</span>}
                  </div>
                )}
                {/* Actions */}
                <div className="flex flex-col gap-2 pt-1">
                  {popoverApptId && popoverPayStatus === 'unpaid' && popoverSession && (
                    <button
                      onClick={() => { setCalendarEventPopover(null); openPaymentPanel(popoverSession); }}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Process Payment
                    </button>
                  )}
                  {popoverApptId && calendarEventPopover.resource?.type === 'appointment' &&
                    calendarEventPopover.resource?.status !== 'cancelled' &&
                    calendarEventPopover.resource?.status !== 'completed' && (
                    <button
                      onClick={() => { const s = upcomingSessions.find(x => x.appointmentId === popoverApptId); if (s) handleRequestCancel(s); }}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-dark-hover hover:bg-gray-700 text-red-400 rounded-lg text-sm font-medium transition-colors"
                    >
                      <XCircleIcon className="w-4 h-4" />
                      Cancel session
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Activate / Deactivate confirmation */}
      {confirmToggleClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-dark-sidebar border border-dark-border rounded-xl shadow-2xl p-6 w-80">
            <h3 className="text-white font-semibold text-base mb-2">
              {confirmToggleClient.is_active ? 'Deactivate client?' : 'Activate client?'}
            </h3>
            <p className="text-sm text-dark-muted mb-5">
              {confirmToggleClient.is_active
                ? `${confirmToggleClient.name} will lose access to their client dashboard.`
                : `${confirmToggleClient.name} will regain access to their client dashboard.`
              }
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmToggleClient(null)}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white border border-dark-border rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { toggleClientStatus(confirmToggleClient); setConfirmToggleClient(null); }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  confirmToggleClient.is_active
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {confirmToggleClient.is_active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Session — unified modal */}
      <ScheduleSessionModal
        isOpen={isScheduleOpen}
        onClose={() => { setIsScheduleOpen(false); setRescheduleTarget(null); setRescheduleScope(undefined); }}
        onSessionCreated={(meetLink) => {
          fetchSessions();
          fetchGoogleCalendarEvents();
          if (meetLink) { setCreatedMeetLink(meetLink); setTimeout(() => setCreatedMeetLink(null), 5000); }
        }}
        reschedule={rescheduleTarget?.appointmentId ? {
          appointmentId: rescheduleTarget.appointmentId,
          meetingName: rescheduleTarget.meetingName || rescheduleTarget.clientName,
          scheduledAt: rescheduleTarget.scheduledAt,
          durationMinutes: rescheduleTarget.duration || 60,
          recurringScope: rescheduleScope,
        } : null}
        client={selectedClientForSession ? {
          client_id: selectedClientForSession.client_id,
          name: selectedClientForSession.name,
          email: selectedClientForSession.email,
          coachClientId: selectedClientForSession.relationship_id,
          ai_consent: selectedClientForSession.ai_consent ?? null,
        } : null}
        coachId={coachId}
        authToken={localStorage.getItem('authToken') ?? ''}
        schedulingType={calendlyStatus?.scheduling_type === 'calendly' ? 'calendly' : googleStatus?.connected ? 'google' : 'none'}
        coachTimezone={coachTimezone}
        coachCurrency={coachCurrency}
        existingEvents={calendarEvents}
        onCancelAppointment={id => { const s = upcomingSessions.find(x => x.appointmentId === id); if (s) handleRequestCancel(s); }}
        calendlyEventTypes={calendlyEventTypes}
        calendlySelectedUris={calendlySelectedUris}
        calendlyEventTypesLoading={calendlyEventTypesLoading}
        onLoadCalendlyEventTypes={handleCalendlyLoadEventTypes}
      />


      {/* Log Past Sessions panel */}
      {showLogPastSession && (
        <LogPastSessionModal
          clients={clients}
          onClose={() => setShowLogPastSession(false)}
          onSaved={() => { fetchSessions(); }}
          coachCurrency={coachCurrency}
        />
      )}

      {/* ── Payment Panel ── */}
      {paymentPanelSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPaymentPanelSession(null)} />
          <div className="relative z-10 w-full max-w-sm bg-dark-card rounded-xl shadow-xl border border-dark-border">
            <div className="flex items-center justify-between px-5 py-4 border-b border-dark-border">
              <h3 className="font-semibold text-white text-sm">
                Payment — {paymentPanelSession.meetingName || paymentPanelSession.clientName}
              </h3>
              <button onClick={() => setPaymentPanelSession(null)} className="text-dark-muted hover:text-gray-300">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {panelError && <div className="text-xs text-red-400 bg-red-900/20 rounded-lg px-3 py-2">{panelError}</div>}
              {panelSuccess && <div className="text-xs text-green-400 bg-green-900/20 rounded-lg px-3 py-2">{panelSuccess}</div>}

              {panelPricingLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-violet-400" />
                </div>
              ) : (
                <>
                  {/* Rate section */}
                  <div className="bg-dark-hover rounded-lg p-3">
                    <span className="text-xs font-medium text-dark-muted uppercase tracking-wide block mb-2">Session Rate</span>
                    {panelEditingRate ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={panelRateInput}
                            onChange={e => setPanelRateInput(e.target.value)}
                            placeholder="150.00"
                            autoFocus
                            className="flex-1 border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200 placeholder-dark-muted focus:ring-2 focus:ring-primary-500"
                          />
                          <select
                            value={panelRateCurrency}
                            onChange={e => setPanelRateCurrency(e.target.value)}
                            className="border border-dark-border rounded-lg p-2 text-sm bg-dark-canvas text-gray-200"
                          >
                            <option value="usd">USD</option>
                            <option value="gbp">GBP</option>
                            <option value="eur">EUR</option>
                            <option value="aud">AUD</option>
                          </select>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handlePanelSaveRate}
                            disabled={panelActionLoading}
                            className="flex-1 text-sm bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg px-3 py-1.5 transition-colors"
                          >
                            {panelActionLoading ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            onClick={() => { setPanelEditingRate(false); setPanelError(''); }}
                            className="text-sm text-dark-muted hover:text-gray-300 px-3 py-1.5"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className="text-white font-semibold">
                          {panelPricing
                            ? `${parseFloat(String(panelPricing.rate)).toFixed(2)} ${panelPricing.currency.toUpperCase()}`
                            : <span className="text-dark-muted font-normal text-sm">Not set</span>
                          }
                        </span>
                        <button
                          onClick={() => { setPanelEditingRate(true); setPanelSuccess(''); setPanelError(''); }}
                          className="text-xs text-violet-400 hover:text-violet-300 transition-colors"
                        >
                          {panelPricing ? 'Edit' : 'Set rate'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Payment status */}
                  {panelPricing && (
                    <div className="bg-dark-hover rounded-lg p-3">
                      <span className="text-xs font-medium text-dark-muted uppercase tracking-wide block mb-2">Payment Status</span>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium capitalize ${
                          panelPricing.status === 'paid' ? 'text-green-400' :
                          panelPricing.status === 'unpaid' ? 'text-red-400' :
                          panelPricing.status === 'pending' ? 'text-yellow-400' :
                          panelPricing.status === 'waived' ? 'text-blue-400' :
                          panelPricing.status === 'refunded' ? 'text-gray-400' :
                          'text-dark-muted'
                        }`}>
                          {panelPricing.status}
                        </span>
                        {(() => {
                          const ps = paymentPanelSession.appointmentId
                            ? apptPaymentStatuses[paymentPanelSession.appointmentId]
                            : null;
                          if (ps?.payment?.paid_at) {
                            return <span className="text-xs text-dark-muted">{new Date(ps.payment.paid_at).toLocaleDateString()}</span>;
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {panelPricing && panelPricing.status === 'unpaid' && !panelEditingRate && (
                    <div className="space-y-2">
                      {panelHasCard ? (
                        <div className="flex gap-2">
                          <button
                            onClick={handlePanelCharge}
                            disabled={panelActionLoading}
                            className="flex-1 text-sm bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white rounded-lg px-3 py-2 transition-colors"
                          >
                            {panelActionLoading ? 'Charging…' : `Charge card on file${panelCardLast4 ? ` ···${panelCardLast4}` : ''}`}
                          </button>
                          <button
                            onClick={handlePanelWaive}
                            disabled={panelActionLoading}
                            className="text-sm border border-dark-border text-dark-muted hover:text-gray-300 hover:border-gray-500 rounded-lg px-3 py-2 transition-colors"
                          >
                            Waive
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="bg-yellow-900/20 border border-yellow-800/40 rounded-lg px-3 py-2">
                            <p className="text-xs text-yellow-400 font-medium">No card on file</p>
                            <p className="text-xs text-dark-muted mt-0.5">Send an invoice so the client can pay via their portal.</p>
                          </div>
                          <button
                            onClick={handlePanelSendInvoice}
                            disabled={panelActionLoading}
                            className="w-full text-sm bg-indigo-700 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-lg px-3 py-2 transition-colors"
                          >
                            {panelActionLoading ? 'Sending…' : 'Send Invoice'}
                          </button>
                          <button
                            onClick={handlePanelWaive}
                            disabled={panelActionLoading}
                            className="w-full text-sm border border-dark-border text-dark-muted hover:text-gray-300 hover:border-gray-500 rounded-lg px-3 py-2 transition-colors"
                          >
                            Waive
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Charge without invoice — confirmation dialog ──────────────────── */}
      {panelChargeConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPanelChargeConfirm(false)} />
          <div className="relative z-10 w-full max-w-sm bg-dark-card rounded-xl shadow-xl border border-dark-border p-6 space-y-4">
            <h3 className="font-semibold text-white text-sm">No Invoice on File</h3>
            <p className="text-sm text-dark-muted">
              No invoice exists for this session. An invoice will be created and sent to the client before charging their card.
            </p>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setPanelChargeConfirm(false)}
                className="flex-1 text-sm border border-dark-border text-dark-muted hover:text-gray-300 rounded-lg px-3 py-2 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePanelChargeConfirmed}
                className="flex-1 text-sm bg-green-700 hover:bg-green-600 text-white rounded-lg px-3 py-2 transition-colors"
              >
                Create Invoice and Charge
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Change Password modal ──────────────────────────────────────────── */}
      {showChangePasswordModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-dark-card border border-dark-border rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
              <h2 className="text-base font-semibold text-white">Change Password</h2>
              <button onClick={() => setShowChangePasswordModal(false)} className="text-gray-500 hover:text-gray-300">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <ChangePasswordCard />
            </div>
          </div>
        </div>
      )}

      {/* ── Support modal ──────────────────────────────────────────────────── */}
      {showSupportModal && (
        <SupportModal onClose={() => setShowSupportModal(false)} />
      )}

      {/* ── My Account modal ───────────────────────────────────────────────── */}
      {showMyAccountModal && (
        <MyAccountModal
          coachName={coachName}
          coachInitials={coachInitials}
          coachEmail={coachEmail || ''}
          chargingPaused={!!flags.billing_charging_paused}
          view={showMyAccountModal}
          onClose={() => setShowMyAccountModal(null)}
        />
      )}

    </div>
  );
};

// ── SupportModal ───────────────────────────────────────────────────────────────

function SupportModal({ onClose }: { onClose: () => void }) {
  function getPrefill() {
    try {
      const data = JSON.parse(localStorage.getItem('userData') || '{}');
      return {
        name: [data.firstName, data.lastName].filter(Boolean).join(' '),
        email: data.email || '',
        userId: data.id || '',
      };
    } catch { return { name: '', email: '', userId: '' }; }
  }

  const prefill = getPrefill();
  const [form, setForm] = React.useState({
    subject: '', issue_type: '' as 'bug' | 'feature_request' | 'question' | '',
    description: '', reporter_name: prefill.name, reporter_email: prefill.email,
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${config.apiBaseUrl.replace('/api', '')}/support/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, user_id: prefill.userId || undefined }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Submission failed.'); }
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally { setSubmitting(false); }
  };

  const inputClass = 'w-full px-3 py-2 border border-dark-border rounded-lg bg-dark-canvas text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 placeholder-gray-600';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-dark-card border border-dark-border rounded-2xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
          <p className="text-sm font-semibold text-white">Contact Support</p>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5">
          {success ? (
            <div className="text-center py-6">
              <p className="text-green-400 font-medium mb-1">Report submitted</p>
              <p className="text-xs text-gray-400 mb-4">We'll follow up at the email you provided.</p>
              <button onClick={onClose} className="text-sm text-primary-400 hover:underline">Close</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Subject <span className="text-red-400">*</span></label>
                <input required type="text" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className={inputClass} placeholder="Brief summary" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Type <span className="text-red-400">*</span></label>
                <select required value={form.issue_type} onChange={e => setForm(f => ({ ...f, issue_type: e.target.value as any }))} className={inputClass}>
                  <option value="">Select…</option>
                  <option value="bug">Bug</option>
                  <option value="feature_request">Feature Request</option>
                  <option value="question">Question</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Description <span className="text-red-400">*</span></label>
                <textarea required rows={4} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={inputClass} placeholder="Describe the issue…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Your Name</label>
                  <input type="text" value={form.reporter_name} onChange={e => setForm(f => ({ ...f, reporter_name: e.target.value }))} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Email</label>
                  <input type="email" value={form.reporter_email} onChange={e => setForm(f => ({ ...f, reporter_email: e.target.value }))} className={inputClass} />
                </div>
              </div>
              {error && <p className="text-xs text-red-400">{error}</p>}
              <button type="submit" disabled={submitting} className="w-full py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 text-sm">
                {submitting ? 'Submitting…' : 'Submit Report'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ── MyAccountModal ─────────────────────────────────────────────────────────────

interface MyAccountModalProps {
  coachName: string;
  coachInitials: string;
  coachEmail: string;
  chargingPaused: boolean;
  view: 'subscription' | 'payment-history';
  onClose: () => void;
}

const TIER_LABELS: Record<string, string> = {
  starter: 'Starter',
  growth: 'Growth',
  elite: 'Elite',
};

const TIER_PRICES: Record<string, number> = {
  starter: 2499,
  growth: 3999,
  elite: 6999,
};

function formatCentsMA(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function tierColorMA(tier: string): string {
  if (tier === 'elite') return 'text-yellow-400';
  if (tier === 'growth') return 'text-primary-400';
  return 'text-gray-300';
}

interface BillingInvoiceRow {
  id: string;
  billing_month: string;
  session_count: number;
  tier: string;
  base_price_cents: number;
  discount_pct: number;
  net_amount_cents: number;
  status: 'issued' | 'paid' | 'failed' | 'void';
  created_at: string;
}

function invoiceStatusBadge(status: string) {
  const map: Record<string, string> = {
    paid:   'bg-green-900/40 text-green-400 border-green-800/50',
    issued: 'bg-yellow-900/30 text-yellow-400 border-yellow-800/40',
    failed: 'bg-red-900/30 text-red-400 border-red-800/40',
    void:   'bg-gray-800/60 text-gray-500 border-gray-700/50',
  };
  return map[status] ?? map.void;
}

function fmtMonth(yyyymm: string): string {
  const [y, m] = yyyymm.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

function MyAccountModal({ coachName, coachInitials, coachEmail, chargingPaused, view, onClose }: MyAccountModalProps) {
  const [summary, setSummary] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  // Invoice history
  const [invoices, setInvoices] = React.useState<BillingInvoiceRow[]>([]);
  const [invoicesLoading, setInvoicesLoading] = React.useState(true);

  // Subscribe flow state
  const [showSubscribe, setShowSubscribe] = React.useState(false);
  const [selectedTier, setSelectedTier] = React.useState('starter');
  const [couponCode, setCouponCode] = React.useState('');
  const [subscribing, setSubscribing] = React.useState(false);
  const [subscribeError, setSubscribeError] = React.useState('');
  const [subscribeSuccess, setSubscribeSuccess] = React.useState('');

  const token = localStorage.getItem('authToken') || '';
  const apiBase = config.apiBaseUrl;

  const fetchSummary = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/billing/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load billing info');
      setSummary(await res.json());
    } catch {
      setError('Could not load account info.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, token]);

  const fetchInvoices = React.useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const res = await fetch(`${apiBase}/billing/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.invoices ?? []);
      }
    } catch {
      // non-fatal — invoice list just stays empty
    } finally {
      setInvoicesLoading(false);
    }
  }, [apiBase, token]);

  React.useEffect(() => {
    fetchSummary();
    fetchInvoices();
  }, [fetchSummary, fetchInvoices]);

  const handleSubscribe = async () => {
    setSubscribing(true);
    setSubscribeError('');
    setSubscribeSuccess('');
    try {
      const body: any = { tier: selectedTier };
      if (couponCode.trim()) body.coupon_code = couponCode.trim();
      const res = await fetch(`${apiBase}/billing/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not subscribe');
      setSubscribeSuccess(`Subscribed to ${TIER_LABELS[selectedTier]}${data.coupon_applied ? ' with coupon applied' : ''}.`);
      setShowSubscribe(false);
      setCouponCode('');
      fetchSummary();
      fetchInvoices();
    } catch (err) {
      setSubscribeError(err instanceof Error ? err.message : 'Could not subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const isSubscribed = summary && summary.current_tier;

  const netCents = React.useMemo(() => {
    if (!summary) return 0;
    const base = TIER_PRICES[summary.current_tier] ?? 0;
    const disc = summary.coupon ? Math.round(base * summary.coupon.discount_pct / 100) : 0;
    return base - disc;
  }, [summary]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-dark-card border border-dark-border rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-dark-border">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-fuchsia-400 to-violet-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
            {coachInitials}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white">{view === 'subscription' ? 'My Subscription' : 'Payment History'}</p>
            {coachEmail && <p className="text-xs text-gray-500 truncate">{coachEmail}</p>}
          </div>
          <button onClick={onClose} className="ml-auto text-gray-500 hover:text-gray-300 flex-shrink-0">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {loading && <p className="text-sm text-gray-400">Loading...</p>}
          {error && <p className="text-sm text-red-400">{error}</p>}
          {subscribeSuccess && <p className="text-sm text-green-400">{subscribeSuccess}</p>}

          {summary && !loading && view === 'subscription' && (
            <>
              {/* Current plan */}
              <div className="bg-dark-canvas border border-dark-border rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-1">Current Plan</p>
                <div className="flex items-baseline gap-2">
                  <span className={`text-xl font-bold capitalize ${tierColorMA(summary.current_tier)}`}>
                    {TIER_LABELS[summary.current_tier] ?? summary.current_tier}
                  </span>
                  <span className="text-sm text-gray-400">{formatCentsMA(TIER_PRICES[summary.current_tier] ?? 0)}/mo</span>
                  {summary.coupon && (
                    <span className="text-xs text-green-400 ml-1">{summary.coupon.discount_pct}% off</span>
                  )}
                </div>
                {summary.coupon && (
                  <p className="text-xs text-gray-500 mt-1">
                    After discount: <span className="text-white font-medium">{formatCentsMA(netCents)}/mo</span>
                    {' '}&mdash; coupon <span className="font-mono text-gray-400">{summary.coupon.code}</span>
                  </p>
                )}
              </div>

              {/* Sessions this month */}
              <div className="bg-dark-canvas border border-dark-border rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-1">Sessions this month</p>
                <p className="text-2xl font-bold text-white">{summary.session_count}</p>
                <p className="text-xs text-gray-500 mt-0.5">Billing month: {summary.billing_month}</p>
              </div>

              {/* Tiers overview */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Available Plans</p>
                <div className="grid grid-cols-3 gap-2">
                  {(summary.tiers ?? []).map((t: any) => (
                    <div
                      key={t.name}
                      className={`rounded-lg border p-3 text-center ${
                        t.name === summary.current_tier
                          ? 'border-primary-500 bg-primary-900/20'
                          : 'border-dark-border'
                      }`}
                    >
                      <p className={`text-xs font-semibold capitalize ${tierColorMA(t.name)}`}>{TIER_LABELS[t.name] ?? t.name}</p>
                      <p className="text-sm font-bold text-white mt-0.5">{formatCentsMA(t.price_cents)}</p>
                      <p className="text-xs text-gray-500">/mo</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Subscribe / change plan */}
              {chargingPaused ? (
                <div className="bg-dark-canvas border border-dark-border rounded-xl p-4 text-center">
                  {!showSubscribe ? (
                    <>
                      <p className="text-xs text-gray-500 mb-3">
                        Billing is not yet active. Select your plan now and we will notify you when billing goes live.
                      </p>
                      <button
                        onClick={() => { setShowSubscribe(true); setSelectedTier(summary.current_tier || 'starter'); }}
                        className="text-sm bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        {isSubscribed ? 'Change Plan' : 'Choose a Plan'}
                      </button>
                    </>
                  ) : (
                    <div className="space-y-3 text-left">
                      <p className="text-xs font-medium text-gray-400">Select Plan</p>
                      <div className="grid grid-cols-3 gap-2">
                        {['starter', 'growth', 'elite'].map(t => (
                          <button
                            key={t}
                            onClick={() => setSelectedTier(t)}
                            className={`rounded-lg border p-2.5 text-center transition-colors ${
                              selectedTier === t
                                ? 'border-primary-500 bg-primary-900/30 text-white'
                                : 'border-dark-border text-gray-400 hover:border-gray-500'
                            }`}
                          >
                            <p className="text-xs font-semibold capitalize">{TIER_LABELS[t]}</p>
                            <p className="text-xs mt-0.5">{formatCentsMA(TIER_PRICES[t])}</p>
                          </button>
                        ))}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Coupon Code (optional)</label>
                        <input
                          type="text"
                          value={couponCode}
                          onChange={e => setCouponCode(e.target.value.toUpperCase())}
                          placeholder="e.g. BETA25"
                          className="w-full text-sm bg-dark-canvas border border-dark-border rounded-lg px-3 py-2 text-gray-200 placeholder-gray-600 focus:ring-2 focus:ring-primary-500 focus:outline-none font-mono"
                        />
                      </div>
                      {subscribeError && <p className="text-xs text-red-400">{subscribeError}</p>}
                      <div className="flex gap-2">
                        <button
                          onClick={handleSubscribe}
                          disabled={subscribing}
                          className="flex-1 text-sm bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {subscribing ? 'Saving...' : 'Confirm Plan'}
                        </button>
                        <button
                          onClick={() => { setShowSubscribe(false); setSubscribeError(''); }}
                          className="text-sm text-gray-400 hover:text-gray-200 px-3 py-2 rounded-lg border border-dark-border transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-500 text-center">
                  To change your plan, contact support.
                </p>
              )}

            </>
          )}

          {view === 'payment-history' && (
            invoicesLoading ? (
              <p className="text-xs text-gray-500">Loading...</p>
            ) : invoices.length === 0 ? (
              <p className="text-xs text-gray-500">No invoices yet.</p>
            ) : (
              <div className="rounded-xl border border-dark-border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-dark-canvas border-b border-dark-border">
                      <th className="text-left px-3 py-2 text-gray-500 font-medium">Month</th>
                      <th className="text-left px-3 py-2 text-gray-500 font-medium">Plan</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium">Sessions</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium">Amount</th>
                      <th className="text-right px-3 py-2 text-gray-500 font-medium">Status</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-dark-border">
                    {invoices.map(inv => (
                      <tr key={inv.id} className="hover:bg-dark-canvas/50 transition-colors">
                        <td className="px-3 py-2.5 text-gray-300 whitespace-nowrap">{fmtMonth(inv.billing_month)}</td>
                        <td className="px-3 py-2.5 capitalize text-gray-400">{TIER_LABELS[inv.tier] ?? inv.tier}</td>
                        <td className="px-3 py-2.5 text-right text-gray-400">{inv.session_count}</td>
                        <td className="px-3 py-2.5 text-right text-gray-300 whitespace-nowrap">
                          {formatCentsMA(inv.net_amount_cents)}
                          {inv.discount_pct > 0 && (
                            <span className="ml-1 text-green-500">-{inv.discount_pct}%</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium capitalize ${invoiceStatusBadge(inv.status)}`}>
                            {inv.status}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <button
                            onClick={async () => {
                              const res = await fetch(`${apiBase}/billing/invoices/${inv.id}/pdf`, {
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              if (!res.ok) return;
                              const blob = await res.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `CoachSight-Invoice-${inv.billing_month}.pdf`;
                              a.click();
                              URL.revokeObjectURL(url);
                            }}
                            className="text-[10px] text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
                            title="Download invoice PDF"
                          >
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default CoachDashboard;
